import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Select from "@mui/material/Select";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import { useRefundProgress } from "context/RefundProgressContext";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { API_BASE_URL } from "config/api";
import { useAuth } from "context/AuthContext";

// Determine reservation ID from URL query parameter (e.g., ?reservationId=<id>)
const urlParams = new URLSearchParams(window.location.search);
const initialReservationId = urlParams.get("reservationId") || "";

// Official Upsell Fields with Categories
const OFFICIAL_UPSELL_FIELDS = [
  { id: "field-1", category: "Early check-in fee", name: "Early Checkin Charges per hour" },
  { id: "field-2", category: "Additional cleaning fee", name: "Mid Stay Cleaning" },
  { id: "field-3", category: "Bed Linen fee", name: "Fresh Bedsheets Fee" },
  { id: "field-4", category: "Bed Linen fee", name: "Comforter Fee" },
  { id: "field-5", category: "Extra Beds fee", name: "Mattress Fee" },
  { id: "field-6", category: "Late check-out fee", name: "Late checkout fee per hour" },
  { id: "field-7", category: "Other fees", name: "Damage Fee" },
  { id: "field-8", category: "Other fees", name: "Security Deposit" },
  { id: "field-9", category: "Parking fee", name: "Extra vehicle parking fee" },
  { id: "field-10", category: "Service fee", name: "water bottle fee" },
  { id: "field-11", category: "Service fee", name: "Tissue box Fee" },
  { id: "field-12", category: "Towel change fee", name: "Towel Change Fee" },
  { id: "field-13", category: "Room request fee", name: "Driver Accommodation" },
  { id: "field-14", category: "Room request fee", name: "Re Check in Charges" },
];

const isNoQtyItem = (item) => {
  if (!item) return false;
  const name = String(item.upsellName || item.title || item.name || "").toLowerCase().trim();
  return (
    name === "security deposit" ||
    name === "early checkin charges per hour" ||
    name === "late checkout fee per hour" ||
    name.includes("security deposit") ||
    name.includes("early checkin") ||
    name.includes("late checkout")
  );
};

const CHARGE_NAMES = [
  { id: "charge-1", name: "CIH-BNR" },
  { id: "charge-2", name: "MZN-BNR01" },
  { id: "charge-3", name: "KEENU-BNR" },
];

// --- Component Approach: Hostaway Reservation Card ---
const HostawayReservationCard = ({
  rec,
  col,
  onOpenUpsell,
  onOpenCharge,
  onOpenRefund,
  getActualCheckInTime,
  getActualCheckOutTime,
  formatDisplayDate,
  isViewOnly,
}) => {
  const fields = rec.fields || {};
  const resId = fields["Reservation ID"] || fields["Reservation ID "] || fields["Hostaway ID"] || fields["Reservation"] || fields["Res ID"] || fields["reservationId"] || fields["Reservation Id"] || rec.id;
  const formattedResId = String(resId).replace(/[^0-9]/g, "");
  const guest = fields["Guest Name"] || fields["Guest Name "] || fields.guestName || fields.GuestName || "Guest Name";
  const room =
    fields["Listing Name"] ||
    fields["Listing Name "] ||
    fields["Apartment Name"] ||
    fields["Apartment Name "] ||
    fields["Apartment"] ||
    fields["Listing"] ||
    fields["Unit"] ||
    fields.listingName ||
    fields.apartmentName ||
    fields.room ||
    "Apt Number";
  const arr = fields["Arrival Date"] || "";
  const dep = fields["Departure Date"] || "";
  const actualCheckin = getActualCheckInTime(fields);
  const actualCheckout = getActualCheckOutTime(fields);

  const displayInDate = actualCheckin ? formatDisplayDate(actualCheckin) : (arr ? formatDisplayDate(arr) : "N/A");
  const displayOutDate = actualCheckout ? formatDisplayDate(actualCheckout) : (dep ? formatDisplayDate(dep) : "N/A");

  const stayDates = (() => {
    if (!arr && !dep) return "N/A";
    const cleanArr = arr ? String(arr).split("T")[0].split(" ")[0] : "";
    const cleanDep = dep ? String(dep).split("T")[0].split(" ")[0] : "";
    if (cleanArr && cleanDep) {
      return `${dayjs(cleanArr).format("D MMM")} – ${dayjs(cleanDep).format("D MMM, YYYY")}`;
    }
    if (cleanArr) return dayjs(cleanArr).format("D MMM, YYYY");
    if (cleanDep) return dayjs(cleanDep).format("D MMM, YYYY");
    return "N/A";
  })();


  return (
    <Card
      key={rec.id}
      sx={{
        backgroundColor: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        p: 2,
        mb: 1.75,
        boxShadow: "0 2px 4px rgba(15, 23, 42, 0.03), 0 1px 2px rgba(15, 23, 42, 0.02)",
        transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          backgroundColor: col.accentColor,
          transform: "scaleX(0.85)",
          transition: "transform 0.25s ease",
        },
        "&:hover::before": {
          transform: "scaleX(1)",
        },
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 12px 20px -8px rgba(15, 23, 42, 0.08), 0 4px 6px -2px rgba(15, 23, 42, 0.03)",
          borderColor: "#cbd5e1",
        },
      }}
    >
      {/* Card Header */}
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pb={1} sx={{ borderBottom: "1px dashed #e2e8f0", mb: 1.5 }}>
        <MDTypography
          variant="caption"
          sx={{
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            fontSize: "0.725rem",
            color: "#0f172a",
            fontWeight: 500,
            letterSpacing: "0.05em",
            backgroundColor: "#e0e7ff",
            border: "1px solid #c7d2fe",
            px: 0.75,
            py: 0.25,
            borderRadius: "4px",
          }}
        >
          {formattedResId}
        </MDTypography>
        <MDTypography
          variant="caption"
          sx={{
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            fontSize: "0.725rem",
            backgroundColor: "#f3e8ff",
            color: "#0f172a",
            fontWeight: 500,
            px: 1,
            py: 0.35,
            borderRadius: "6px",
            border: "1px solid #e9d5ff",
          }}
        >
          {room}
        </MDTypography>
      </MDBox>

      {/* Guest Name */}
      <MDTypography
        variant="h6"
        fontWeight="bold"
        mb={1.5}
        sx={{
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          fontSize: "0.95rem",
          color: "#0f172a",
          letterSpacing: "-0.01em",
          lineHeight: 1.25,
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}
      >
        {guest}
      </MDTypography>

      {/* Column Details: Show ONLY relevant timestamp based on stack */}
      {(col.catIdx === 7 || col.catIdx === 8) && (
        <MDBox
          px={1.25}
          py={0.8}
          mb={1.5}
          sx={{
            backgroundColor: col.catIdx === 8 ? "#fff7ed" : "#eff6ff",
            border: `1px solid ${col.catIdx === 8 ? "#fed7aa" : "#dbeafe"}`,
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          <MDTypography
            variant="caption"
            fontWeight="bold"
            sx={{
              fontSize: "0.72rem",
              color: col.catIdx === 8 ? "#c2410c" : "#1d4ed8",
              fontWeight: 800,
              whiteSpace: "nowrap",
            }}
          >
            Dates:
          </MDTypography>
          <MDTypography
            variant="caption"
            sx={{
              fontSize: "0.72rem",
              color: "#0f172a",
              fontWeight: 500,
              wordBreak: "break-word",
            }}
          >
            {stayDates}
          </MDTypography>
        </MDBox>
      )}

      {(col.catIdx === 0 || col.catIdx === 1 || col.catIdx === 2 || col.catIdx === 3) && (
        <MDBox
          px={1.25}
          py={0.8}
          mb={1.5}
          sx={{
            backgroundColor: col.catIdx === 0 ? "#f0fdf4" : col.catIdx === 1 ? "#eff6ff" : col.catIdx === 3 ? "#fdf2f8" : "#fffbeb",
            border: `1px solid ${col.catIdx === 0 ? "#dcfce7" : col.catIdx === 1 ? "#dbeafe" : col.catIdx === 3 ? "#fbcfe8" : "#fef3c7"}`,
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          <MDTypography
            variant="caption"
            fontWeight="bold"
            sx={{
              fontSize: "0.72rem",
              color: col.catIdx === 0 ? "#15803d" : col.catIdx === 1 ? "#1d4ed8" : col.catIdx === 3 ? "#db2777" : "#b45309",
              fontWeight: 800,
              whiteSpace: "nowrap",
            }}
          >
            Checked In:
          </MDTypography>
          <MDTypography
            variant="caption"
            sx={{
              fontSize: "0.72rem",
              color: "#0f172a",
              fontWeight: 500,
              wordBreak: "break-word",
            }}
          >
            {displayInDate}
          </MDTypography>
        </MDBox>
      )}

      {(col.catIdx === 4 || col.catIdx === 5 || col.catIdx === 6) && (
        <MDBox
          mb={1.5}
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(115px, 1fr))",
            gap: 0.75,
          }}
        >
          {/* Checked In Box */}
          <MDBox
            p={0.75}
            sx={{
              backgroundColor: "#f0fdf4",
              border: "1px solid #dcfce7",
              borderRadius: "6px",
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              minWidth: 0,
            }}
          >
            <MDTypography
              variant="caption"
              fontWeight="bold"
              sx={{
                fontSize: "0.62rem",
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                color: "#15803d",
                fontWeight: 800,
                whiteSpace: "nowrap",
              }}
            >
              Checked In
            </MDTypography>
            <MDTypography
              variant="caption"
              sx={{
                fontSize: "0.68rem",
                color: "#0f172a",
                fontWeight: 500,
                wordBreak: "break-word",
                lineHeight: 1.25,
                letterSpacing: "-0.2px",
              }}
            >
              {displayInDate}
            </MDTypography>
          </MDBox>

          {/* Checked Out Box */}
          <MDBox
            p={0.75}
            sx={{
              backgroundColor: col.catIdx === 6 ? "#fef2f2" : col.catIdx === 5 ? "#f8fafc" : "#fffbeb",
              border: `1px solid ${col.catIdx === 6 ? "#fecaca" : col.catIdx === 5 ? "#cbd5e1" : "#fef3c7"}`,
              borderRadius: "6px",
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              minWidth: 0,
            }}
          >
            <MDTypography
              variant="caption"
              fontWeight="bold"
              sx={{
                fontSize: "0.62rem",
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                color: col.catIdx === 6 ? "#dc2626" : col.catIdx === 5 ? "#475569" : "#b45309",
                fontWeight: 800,
                whiteSpace: "nowrap",
              }}
            >
              Checked Out
            </MDTypography>
            <MDTypography
              variant="caption"
              sx={{
                fontSize: "0.68rem",
                color: "#0f172a",
                fontWeight: 500,
                wordBreak: "break-word",
                lineHeight: 1.25,
                letterSpacing: "-0.2px",
              }}
            >
              {displayOutDate}
            </MDTypography>
          </MDBox>
        </MDBox>
      )}

      {/* Action Buttons - Enabled for regular/admin, disabled format for view-only */}
      <MDBox display="flex" gap={0.75} width="100%">
        <MDButton
          variant="outlined"
          size="small"
          disabled={isViewOnly}
          onClick={isViewOnly ? undefined : () => onOpenUpsell(fields, String(resId), guest, arr, dep)}
          sx={{
            flex: 1,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            py: 0.65,
            px: 1,
            fontSize: "0.72rem",
            fontWeight: "600",
            borderRadius: "6px",
            textTransform: "none",
            whiteSpace: "nowrap",
            backgroundColor: isViewOnly ? "#f8fafc" : "#f0fdf4",
            color: isViewOnly ? "#94a3b8 !important" : "#15803d !important",
            borderColor: isViewOnly ? "#e2e8f0 !important" : "#bbf7d0 !important",
            boxShadow: isViewOnly ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
            cursor: isViewOnly ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: isViewOnly ? "#f8fafc" : "#dcfce7",
              borderColor: isViewOnly ? "#e2e8f0 !important" : "#86efac !important",
              color: isViewOnly ? "#94a3b8 !important" : "#166534 !important",
            },
            "&.Mui-disabled": {
              color: "#94a3b8 !important",
              borderColor: "#e2e8f0 !important",
              backgroundColor: "#f8fafc !important",
              boxShadow: "none",
            },
          }}
        >
          Add Upsell
        </MDButton>

        <MDButton
          variant="outlined"
          size="small"
          disabled={isViewOnly}
          onClick={isViewOnly ? undefined : () => onOpenCharge(fields, String(resId), guest, arr, dep, room)}
          sx={{
            flex: 1,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            py: 0.65,
            px: 1,
            fontSize: "0.72rem",
            fontWeight: "600",
            borderRadius: "6px",
            textTransform: "none",
            whiteSpace: "nowrap",
            backgroundColor: isViewOnly ? "#f8fafc" : "#eff6ff",
            color: isViewOnly ? "#94a3b8 !important" : "#1d4ed8 !important",
            borderColor: isViewOnly ? "#e2e8f0 !important" : "#bfdbfe !important",
            boxShadow: isViewOnly ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
            cursor: isViewOnly ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: isViewOnly ? "#f8fafc" : "#dbeafe",
              borderColor: isViewOnly ? "#e2e8f0 !important" : "#93c5fd !important",
              color: isViewOnly ? "#94a3b8 !important" : "#1e40af !important",
            },
            "&.Mui-disabled": {
              color: "#94a3b8 !important",
              borderColor: "#e2e8f0 !important",
              backgroundColor: "#f8fafc !important",
              boxShadow: "none",
            },
          }}
        >
          Add Charge
        </MDButton>
      </MDBox>
    </Card>
  );
};

// --- Component Approach: Hostaway Kanban Column ---
const HostawayKanbanColumn = ({
  col,
  colRecords,
  onOpenUpsell,
  onOpenCharge,
  onOpenRefund,
  getActualCheckInTime,
  getActualCheckOutTime,
  formatDisplayDate,
  isViewOnly,
}) => {
  return (
    <MDBox
      key={col.title}
      id={`cat-sec-${col.catIdx}`}
      sx={{
        minWidth: { xs: "280px", sm: "310px", md: "330px" },
        maxWidth: { xs: "100%", md: "380px" },
        flex: "1 1 320px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Card
        sx={{
          backgroundColor: "rgba(241, 245, 249, 0.6)",
          border: "1px solid #e2e8f0",
          borderRadius: "14px",
          p: 0,
          height: "100%",
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Sticky Column Header */}
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2.25}
          py={2}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #e2e8f0",
            borderTopLeftRadius: "14px",
            borderTopRightRadius: "14px",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1.25}>
            <MDBox
              width="10px"
              height="10px"
              borderRadius="50%"
              sx={{ backgroundColor: col.accentColor }}
            />
            <MDTypography
              variant="h6"
              fontWeight="bold"
              sx={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                fontSize: "0.85rem",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#1e293b",
              }}
            >
              {col.title}
            </MDTypography>
          </MDBox>
          <Chip
            label={colRecords.length}
            size="small"
            sx={{
              backgroundColor: col.badgeBg,
              color: col.badgeText,
              fontWeight: 700,
              fontSize: "0.78rem",
              height: "24px",
              minWidth: "24px",
              borderRadius: "6px",
              fontFamily: "'Inter', sans-serif",
            }}
          />
        </MDBox>

        {/* Column Cards Scrollable Container */}
        <MDBox sx={{ maxHeight: "calc(100vh - 260px)", minHeight: "400px", overflowY: "auto", p: 1.75, flex: 1 }}>
          {colRecords.length > 0 ? (
            colRecords.map((rec) => (
              <HostawayReservationCard
                key={rec.id}
                rec={rec}
                col={col}
                onOpenUpsell={onOpenUpsell}
                onOpenCharge={onOpenCharge}
                onOpenRefund={onOpenRefund}
                getActualCheckInTime={getActualCheckInTime}
                getActualCheckOutTime={getActualCheckOutTime}
                formatDisplayDate={formatDisplayDate}
                isViewOnly={isViewOnly}
              />
            ))
          ) : (
            <MDBox py={4} textAlign="center">
              <MDTypography variant="caption" color="text" sx={{ fontFamily: "'Inter', sans-serif" }}>
                No reservations found.
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </Card>
    </MDBox>
  );
};

const getFieldVal = (f) => {
  if (!f) return 0;
  const v = f.total !== undefined && f.total !== null && f.total !== "" ? f.total :
    (f.amount !== undefined && f.amount !== null && f.amount !== "" ? f.amount :
      (f.value !== undefined && f.value !== null && f.value !== "" ? f.value :
        (f.price !== undefined && f.price !== null && f.price !== "" ? f.price :
          (f.raw?.total || f.raw?.amount || f.raw?.value || 0))));
  const num = Number(String(v).replace(/[^0-9.-]/g, ""));
  return isNaN(num) ? 0 : num;
};

function Upsells({ defaultTab = 0, isEmbedded = false }) {
  const navigate = useNavigate();
  const { user, isViewOnly: checkViewOnly, hasPermission, isUser } = useAuth();
  const isViewOnly =
    (typeof checkViewOnly === "function" ? checkViewOnly() : user?.role === "view_only") ||
    (user?.role === "custom" && typeof hasPermission === "function" && !hasPermission("upsells", "complete"));
  const isStandardUser = (typeof isUser === "function" ? isUser() : user?.role === "user");
  const isCustomViewOnly = user?.role === "custom" && typeof hasPermission === "function" && !hasPermission("upsells", "complete");
  const showDateBeforeSearchBar = isStandardUser || isCustomViewOnly;
  const showDateAtTop = !showDateBeforeSearchBar;
  const canDownload = !isStandardUser && !isViewOnly;

  // Reservation Selection & Search State
  const [selectedResId, setSelectedResId] = useState(initialReservationId);
  const [searchInput, setSearchInput] = useState(initialReservationId);
  const [reservationDetails, setReservationDetails] = useState(null);
  const [loadingReservation, setLoadingReservation] = useState(false);

  // Modal State for Upsells
  const [openModal, setOpenModal] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [qtyInput, setQtyInput] = useState("1");
  const [upsellCurrency, setUpsellCurrency] = useState("");

  // Modal State for Add Charge
  const [openChargeModal, setOpenChargeModal] = useState(false);
  const [chargeModalStep, setChargeModalStep] = useState(1); // 1: Financial Overview, 2: Add Charge Section
  const [chargeType, setChargeType] = useState("Charge");
  const [selectedChargeName, setSelectedChargeName] = useState("");
  const [chargeDescription, setChargeDescription] = useState("");
  const [chargeAmountInput, setChargeAmountInput] = useState("");
  const [currency, setCurrency] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [hostawayFinanceFields, setHostawayFinanceFields] = useState([]);
  const [hostawayPayments, setHostawayPayments] = useState([]);
  const [hostawayTotalPrice, setHostawayTotalPrice] = useState(0);
  const [hostawayPaidAmount, setHostawayPaidAmount] = useState(0);
  const [hostawayPendingAmount, setHostawayPendingAmount] = useState(0);
  const [hostawayPaymentStatus, setHostawayPaymentStatus] = useState("paid");
  const [hostawayRefundIdMap, setHostawayRefundIdMap] = useState({}); // internalRefundId → providerRefundId
  const [hostawayRefundToChargeMap, setHostawayRefundToChargeMap] = useState({}); // refundId / providerRefundId → parent chargeId
  const [loadingFinanceFields, setLoadingFinanceFields] = useState(false);

  // Toast Error Messages
  const [chargeErrorToast, setChargeErrorToast] = useState("");
  const [upsellErrorToast, setUpsellErrorToast] = useState("");

  // Modal State for Security Deposit Refund
  const [openRefundModal, setOpenRefundModal] = useState(false);
  const [refundTitle, setRefundTitle] = useState("");
  const [refundChargeId, setRefundChargeId] = useState("");
  const [refundDetectedChargeId, setRefundDetectedChargeId] = useState("");
  const [refundAmountInput, setRefundAmountInput] = useState("");
  const [refundReason, setRefundReason] = useState("Other");
  const [refundDescription, setRefundDescription] = useState("");
  const [refundDetectedDeposit, setRefundDetectedDeposit] = useState(0);
  const [refundExistingRefunds, setRefundExistingRefunds] = useState(0);
  const [refundErrorToast, setRefundErrorToast] = useState("");
  const [openRefundConfirmModal, setOpenRefundConfirmModal] = useState(false);
  const [openUpsellConfirmModal, setOpenUpsellConfirmModal] = useState(false);
  const [openChargeConfirmModal, setOpenChargeConfirmModal] = useState(false);
  const [includeInPriceBreakdown, setIncludeInPriceBreakdown] = useState(true);
  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [selectedRowRecord, setSelectedRowRecord] = useState(null);

  // Saving / Loading States for Modals
  const [isSavingUpsell, setIsSavingUpsell] = useState(false);
  const [isSavingCharge, setIsSavingCharge] = useState(false);
  const [isSavingRefund, setIsSavingRefund] = useState(false);
  const [refundElapsedSeconds, setRefundElapsedSeconds] = useState(0);

  const isMobile = useMediaQuery("(max-width:600px)");
  const { triggerRefund, isProcessing: isGlobalRefundProcessing } = useRefundProgress();

  const [appliedUpsells, setAppliedUpsells] = useState([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    color: "success",
    title: "",
    content: "",
  });

  // PostgreSQL Backend Reservations State (previously Teable)
  const [fdoRecords, setFdoRecords] = useState(() => {
    // Clear stale Teable cache to ensure fresh backend data loads
    try { sessionStorage.removeItem("cached_fdo_records"); } catch (e) { }
    return [];
  });
  const [loadingFdo, setLoadingFdo] = useState(true);
  const [resSubTab, setResSubTab] = useState(0); // 0: Checked in, 1: Staying guest, 2: Upcoming Checkout, 3: Checked out
  const [fdoSearchQuery, setFdoSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("kanban"); // "kanban" or "table"
  const [livePkrRate, setLivePkrRate] = useState(0);
  // Teable Upsell List Database State (tblVlCzoQlP35ui0MZH)
  const [dbUpsellRecords, setDbUpsellRecords] = useState(() => {
    try {
      const cached = sessionStorage.getItem("cached_db_upsell_records");
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [loadingDbUpsells, setLoadingDbUpsells] = useState(() => {
    try {
      const cached = sessionStorage.getItem("cached_db_upsell_records");
      return !cached;
    } catch (e) {
      return true;
    }
  });
  const [upsellSearchQuery, setUpsellSearchQuery] = useState("");

  const getTodayISO = () => {
    const today = new Date();
    const tYear = today.getFullYear();
    const tMonth = String(today.getMonth() + 1).padStart(2, "0");
    const tDay = String(today.getDate()).padStart(2, "0");
    return `${tYear}-${tMonth}-${tDay}`;
  };

  const getStartOfMonthISO = () => {
    const today = new Date();
    const tYear = today.getFullYear();
    const tMonth = String(today.getMonth() + 1).padStart(2, "0");
    return `${tYear}-${tMonth}-01`;
  };

  const [upsellStartDate, setUpsellStartDate] = useState(getTodayISO);
  const [upsellEndDate, setUpsellEndDate] = useState(getTodayISO);
  const [chargeStartDate, setChargeStartDate] = useState(getTodayISO);
  const [chargeEndDate, setChargeEndDate] = useState(getTodayISO);
  const [viewUpsellModalOpen, setViewUpsellModalOpen] = useState(false);
  const [selectedReservationForView, setSelectedReservationForView] = useState(null);
  const [viewChargeModalOpen, setViewChargeModalOpen] = useState(false);
  const [selectedChargeForView, setSelectedChargeForView] = useState(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [targetDownloadReservation, setTargetDownloadReservation] = useState(null);
  const [targetDownloadCharge, setTargetDownloadCharge] = useState(null);

  const formatHeaderDate = (isoStr) => {
    if (!isoStr) return "";
    const [y, m, d] = isoStr.split("-");
    if (!y || !m || !d) return isoStr;
    return `${d}/${m}/${y}`;
  };

  const formatFooterDate = (isoStr) => {
    if (!isoStr) return "";
    const parts = isoStr.split("-");
    if (parts.length !== 3) return isoStr;
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const getSelectedDateUpsellEntries = () => {
    return dbUpsellRecords.filter((rec) => {
      const rawDate = rec.dateTime || rec.date_time || rec.fields?.["Date and Time"] || rec.fields?.["date_time"] || rec.createdAt || rec.created_at;
      if (!rawDate) return true;
      const d = new Date(rawDate);
      if (isNaN(d.getTime())) return true;
      const rYear = d.getFullYear();
      const rMonth = String(d.getMonth() + 1).padStart(2, "0");
      const rDay = String(d.getDate()).padStart(2, "0");
      const recDateISO = `${rYear}-${rMonth}-${rDay}`;

      if (upsellStartDate && recDateISO < upsellStartDate) return false;
      if (upsellEndDate && recDateISO > upsellEndDate) return false;
      return true;
    });
  };

  const formatReservationTotal = (res, defaultRate) => {
    const items = res?.items || [];
    const hasUsd = items.some((item) => (item.currency || "").toUpperCase() === "USD" || (item.currency || "").includes("$"));
    if (hasUsd) {
      const totalUSD = items.reduce((sum, item) => {
        const isUsd = (item.currency || "").toUpperCase() === "USD" || (item.currency || "").includes("$");
        const price = Number(item.price || item.totalPrice || 0);
        return sum + (isUsd ? price : 0);
      }, 0);
      const totalPKR = items.reduce((sum, item) => {
        const isUsd = (item.currency || "").toUpperCase() === "USD" || (item.currency || "").includes("$");
        const price = Number(item.price || item.totalPrice || 0);
        const rate = Number(item.pkrRate || res?.pkrRate || defaultRate || livePkrRate || 0);
        return sum + (isUsd && rate > 0 ? price * rate : price);
      }, 0);
      const dynRate = Number(res?.pkrRate || defaultRate || livePkrRate || 0);
      return dynRate > 0
        ? `$${totalUSD.toFixed(2)} USD = PKR ${Math.round(totalPKR).toLocaleString()}`
        : `$${totalUSD.toFixed(2)}`;
    }
    const totalPKR = Number(res?.totalPrice || items.reduce((sum, item) => sum + Number(item.price || item.totalPrice || 0), 0) || 0);
    return `Rs. ${Math.round(totalPKR).toLocaleString()}`;
  };

  const formatEntriesTotal = (entriesList, defaultRate) => {
    const items = entriesList || [];
    const totalPKR = items.reduce((sum, item) => {
      const isUsd = (item.currency || "").toUpperCase() === "USD" || (item.currency || "").includes("$");
      const price = Number(item.price || item.totalPrice || 0);
      const rate = Number(item.pkrRate || defaultRate || livePkrRate || 0);
      return sum + (isUsd && rate > 0 ? price * rate : price);
    }, 0);
    return `Rs. ${Math.round(totalPKR).toLocaleString()}`;
  };

  const formatChargeAmountWithConversion = (amount, currency, itemRate) => {
    const num = Number(amount || 0);
    if (isNaN(num)) return "-";
    const isUsd = (currency || "").toUpperCase() === "USD" || (currency || "").includes("$");
    const isNegative = num < 0;
    const absNum = Math.abs(num);

    if (isUsd) {
      const rate = Number(itemRate || livePkrRate || 0);
      if (rate > 0) {
        const pkrVal = Math.round(absNum * rate);
        return isNegative
          ? `-$${absNum.toFixed(2)} USD = -PKR ${pkrVal.toLocaleString()}`
          : `$${absNum.toFixed(2)} USD = PKR ${pkrVal.toLocaleString()}`;
      }
      return isNegative ? `-$${absNum.toFixed(2)}` : `$${absNum.toFixed(2)}`;
    }
    return isNegative ? `-Rs. ${Math.round(absNum).toLocaleString()}` : `Rs. ${Math.round(absNum).toLocaleString()}`;
  };

  const formatPakistanDateTime = (raw, includeSeconds = false) => {
    if (!raw) return "-";
    const str = String(raw).trim();
    if (!str || str.toLowerCase() === "n/a" || str === "-") return "-";

    // 1. Explicit UTC / Timezone string (ends with Z, or has +/- offset, or UTC)
    // e.g. "2026-09-03T06:36:57Z" or "2026-09-03T06:37:22.040Z"
    if (str.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(str) || str.toLowerCase().endsWith("utc")) {
      const d = new Date(str);
      if (!isNaN(d.getTime())) {
        const opts = {
          timeZone: "Asia/Karachi",
          month: "short",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
          ...(includeSeconds ? { second: "2-digit" } : {}),
        };
        return d.toLocaleString("en-US", opts);
      }
    }

    // 2. Plain date-time string without timezone (e.g. from Teable "2026-09-03 11:36:57" or "2026-09-03 11:38:35")
    // In Teable, "Charge Date" is already recorded in Pakistan local time.
    // Parse date and time components directly without applying secondary timezone shift.
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (match) {
      const [, yr, mo, dy, hr, min, sec] = match;
      let hourNum = parseInt(hr, 10);
      const ampm = hourNum >= 12 ? "PM" : "AM";
      hourNum = hourNum % 12 || 12;
      const hourStr = String(hourNum).padStart(2, "0");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const monthName = months[parseInt(mo, 10) - 1] || mo;

      if (includeSeconds && sec !== undefined) {
        return `${monthName} ${dy}, ${yr}, ${hourStr}:${min}:${sec} ${ampm}`;
      }
      return `${monthName} ${dy}, ${yr}, ${hourStr}:${min} ${ampm}`;
    }

    // 3. Fallback for other formats
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      return d.toLocaleString("en-US", {
        timeZone: "Asia/Karachi",
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        ...(includeSeconds ? { second: "2-digit" } : {}),
      });
    }
    return str;
  };

  const getOriginalChargeId = (record, allPayments = [], dbCharges = []) => {
    if (!record) return "-";
    const fields = record.fields || record.raw || record || {};
    const recId = String(fields.id || fields.ID || fields.Id || record.id || "").trim();
    const rawAmt = Number(String(fields.Amount || fields.amount || "0").replace(/[^0-9.-]/g, ""));
    const typeStr = String(fields.Type || fields.type || "").toLowerCase().trim();
    const statusStr = String(fields.Status || fields.status || "").toLowerCase().trim();
    const descStr = String(fields.Description || fields.description || fields.Title || fields.title || "").toLowerCase();

    const isRefund =
      typeStr === "refund" ||
      statusStr === "refunded" ||
      descStr.includes("refund") ||
      (!isNaN(rawAmt) && rawAmt < 0);

    // If it's a positive charge, its own ID is the original charge ID
    if (!isRefund && recId && recId !== "-" && recId !== "0") {
      return recId;
    }

    // 1. Direct refundedChargeId / originalChargeId on this record
    const directRefundId = String(
      fields.refundInfo?.refundedChargeId ||
      fields.raw?.refundInfo?.refundedChargeId ||
      record.refundInfo?.refundedChargeId ||
      record.raw?.refundInfo?.refundedChargeId ||
      fields.originalChargeId ||
      fields.original_charge_id ||
      record.originalChargeId ||
      record.original_charge_id ||
      fields["Refunded Charge ID"] ||
      fields["RefundedChargeId"] ||
      fields.refundedChargeId ||
      record.refundedChargeId ||
      ""
    ).trim();

    if (directRefundId && directRefundId !== "-" && directRefundId !== "0" && directRefundId !== recId) {
      return directRefundId;
    }

    // 0b. Backend refundToChargeMap lookup
    if (hostawayRefundToChargeMap && typeof hostawayRefundToChargeMap === "object") {
      if (recId && hostawayRefundToChargeMap[recId]) {
        return String(hostawayRefundToChargeMap[recId]).trim();
      }
      const recIdNum = Number(recId);
      if (recIdNum && hostawayRefundToChargeMap[recIdNum]) {
        return String(hostawayRefundToChargeMap[recIdNum]).trim();
      }
      const pRef = fields.paymentProviderRefundId || fields.paymentProviderChargeId || fields.refundInfo?.id || fields["Refund Id"] || fields["Refund ID"];
      if (pRef && hostawayRefundToChargeMap[String(pRef).trim()]) {
        return String(hostawayRefundToChargeMap[String(pRef).trim()]).trim();
      }
    }

    // 1. Gather all candidate identifiers for this refund (internal ID and provider ID)
    const refundCandidates = new Set();
    if (recId && recId !== "-" && recId !== "0") refundCandidates.add(recId);
    const pRef1 = fields.refundInfo?.id || fields.raw?.refundInfo?.id || record.refundInfo?.id;
    if (pRef1) refundCandidates.add(String(pRef1).trim());
    const pRef2 = fields.paymentProviderRefundId || fields.paymentProviderChargeId;
    if (pRef2) refundCandidates.add(String(pRef2).trim());
    const pRef3 = fields["Refund Id"] || fields["Refund ID"] || fields.refundId;
    if (pRef3) refundCandidates.add(String(pRef3).trim());
    const recIdNum = Number(recId);
    if (recIdNum && hostawayRefundIdMap && hostawayRefundIdMap[recIdNum]) {
      refundCandidates.add(String(hostawayRefundIdMap[recIdNum]).trim());
    }
    if (recId && hostawayRefundIdMap && hostawayRefundIdMap[recId]) {
      refundCandidates.add(String(hostawayRefundIdMap[recId]).trim());
    }
    const msg = String(fields.paymentProviderMessage || record.paymentProviderMessage || "");
    const msgMatch = msg.match(/Payment Provider charge ID:\s*([0-9]+)/i);
    if (msgMatch && msgMatch[1]) refundCandidates.add(String(msgMatch[1]).trim());

    // 2. Check if this refund is listed in refundIds or paymentProviderRefundIds of any positive charge
    const matchingParentCharge = (allPayments || []).find((p) => {
      const pf = p.fields || p.raw || p || {};
      const pAmt = Number(String(pf.Amount || pf.amount || "0").replace(/[^0-9.-]/g, ""));
      if (isNaN(pAmt) || pAmt <= 0) return false;

      const rIds = (pf.refundIds || p.refundIds || (p.raw && p.raw.refundIds) || []).map(String);
      const ppRIds = (pf.paymentProviderRefundIds || p.paymentProviderRefundIds || (p.raw && p.raw.paymentProviderRefundIds) || []).map(String);

      for (const cand of refundCandidates) {
        if (rIds.includes(cand) || ppRIds.includes(cand)) {
          return true;
        }
      }
      return false;
    });
    if (matchingParentCharge) {
      const mpf = matchingParentCharge.fields || matchingParentCharge.raw || matchingParentCharge || {};
      const parentId = String(mpf.id || mpf.ID || mpf.Id || matchingParentCharge.id || "").trim();
      if (parentId) return parentId;
    }

    // 3. Check if record itself is a DB refund record with chargeId / charge_id targeting a known positive charge
    const dbLinkedChargeId = String(
      record.chargeId ||
      record.charge_id ||
      fields.chargeId ||
      fields.charge_id ||
      fields["Charge ID"] ||
      ""
    ).trim();
    if (dbLinkedChargeId && dbLinkedChargeId !== "-" && dbLinkedChargeId !== "0" && dbLinkedChargeId !== recId) {
      const isKnownPositive = (allPayments || []).some((p) => {
        const pf = p.fields || p.raw || p || {};
        const pid = String(pf.id || pf.ID || pf.Id || p.id || "").trim();
        return pid === dbLinkedChargeId;
      });
      if (isKnownPositive) {
        return dbLinkedChargeId;
      }
    }

    // 4. Check local dbCharges for matching originalChargeId
    if (Array.isArray(dbCharges) && dbCharges.length > 0) {
      const matchedDb = dbCharges.find((c) => {
        const cId = String(c.chargeId || c.id || "").trim();
        const origId = String(c.originalChargeId || "").trim();
        if (origId && (cId === recId || String(c.id) === recId)) return true;
        return false;
      });
      if (matchedDb && matchedDb.originalChargeId) {
        return String(matchedDb.originalChargeId).trim();
      }
    }

    // 5. Find positive charges in the payment list
    const positiveCharges = (allPayments || []).filter((p) => {
      const pf = p.fields || p.raw || p || {};
      const pAmt = Number(String(pf.Amount || pf.amount || "0").replace(/[^0-9.-]/g, ""));
      const pType = String(pf.Type || pf.type || "").toLowerCase().trim();
      const pStatus = String(pf.Status || pf.status || "").toLowerCase().trim();
      const pDesc = String(pf.Description || pf.description || pf.Title || pf.title || "").toLowerCase();
      const isPRefund = pType === "refund" || pStatus === "refunded" || pDesc.includes("refund") || (!isNaN(pAmt) && pAmt < 0);
      return !isPRefund && pAmt > 0;
    });

    if (positiveCharges.length === 1) {
      const pf = positiveCharges[0].fields || positiveCharges[0].raw || positiveCharges[0] || {};
      const pId = String(pf.id || pf.ID || pf.Id || positiveCharges[0].id || "").trim();
      if (pId) return pId;
    } else if (positiveCharges.length > 1) {
      const recTitle = String(fields.Title || fields.title || fields.name || "").trim().toLowerCase();
      const sameTitleCharges = positiveCharges.filter((p) => {
        const pf = p.fields || p.raw || p || {};
        const pTitle = String(pf.Title || pf.title || pf.name || "").trim().toLowerCase();
        return pTitle && recTitle && pTitle === recTitle;
      });

      const candidateList = sameTitleCharges.length > 0 ? sameTitleCharges : positiveCharges;

      const parseDateMs = (val) => {
        if (!val) return NaN;
        if (typeof val === "number") return val;
        const s = String(val).trim();
        if (!s || s === "-" || s.toLowerCase() === "n/a") return NaN;
        if (s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s)) {
          const t = new Date(s).getTime();
          if (!isNaN(t)) return t;
        }
        const iso = s.replace(" ", "T");
        const tIso = new Date(iso).getTime();
        if (!isNaN(tIso)) return tIso;
        const tDirect = new Date(s).getTime();
        return isNaN(tDirect) ? NaN : tDirect;
      };

      const refDate = parseDateMs(
        fields["Charge Date and Time"] ||
        fields["Charge Date"] ||
        fields.chargeDate ||
        fields.date ||
        fields.dateTime ||
        fields.created_at ||
        record.dateTime ||
        record.date_time ||
        record.created_at
      );

      let bestCharge = null;
      let minDiff = Infinity;

      for (const pc of candidateList) {
        const pf = pc.fields || pc.raw || pc || {};
        const cDate = parseDateMs(
          pf["Charge Date and Time"] ||
          pf["Charge Date"] ||
          pf.chargeDate ||
          pf.date ||
          pf.dateTime ||
          pf.created_at ||
          pc.dateTime ||
          pc.date_time ||
          pc.created_at
        );
        // CRITICAL: A refund can NEVER belong to a charge that was created in the future!
        if (!isNaN(refDate) && !isNaN(cDate) && refDate >= cDate) {
          const diff = refDate - cDate;
          if (diff < minDiff) {
            minDiff = diff;
            bestCharge = pc;
          }
        }
      }

      // If no past charge was matched by exact timestamp, pick the earliest created charge
      // (NEVER attribute past refunds to a newly created future charge!)
      if (!bestCharge && candidateList.length > 0) {
        const sortedOldestFirst = [...candidateList].sort((a, b) => {
          const af = a.fields || a.raw || a || {};
          const bf = b.fields || b.raw || b || {};
          const ad = parseDateMs(af.date || af.chargeDate || af["Charge Date"] || af.dateTime) || 0;
          const bd = parseDateMs(bf.date || bf.chargeDate || bf["Charge Date"] || bf.dateTime) || 0;
          return ad - bd;
        });
        bestCharge = sortedOldestFirst[0];
      }

      if (bestCharge) {
        const pf = bestCharge.fields || bestCharge.raw || bestCharge || {};
        const pId = String(pf.id || pf.ID || pf.Id || bestCharge.id || "").trim();
        if (pId) return pId;
      }
    }

    return recId || "-";
  };

  const getGroupedReservations = () => {
    const entries = getSelectedDateUpsellEntries();
    const groupsMap = {};

    entries.forEach((rec) => {
      const resId = String(rec.reservationId || "Unknown");
      const cleanResId = String(resId).replace(/[^0-9]/g, "");
      if (!groupsMap[resId]) {
        const matchedFdo = (fdoRecords || []).find((f) => {
          const rId = String(f.fields?.["Reservation ID"] || f.fields?.["Reservation ID "] || f.fields?.["Hostaway Reservation ID"] || f.fields?.ReservationId || f.fields?.reservationId || f.id || "").replace(/[^0-9]/g, "");
          return rId === cleanResId;
        });
        const listingName =
          rec.apartmentName && rec.apartmentName !== "-"
            ? rec.apartmentName
            : matchedFdo?.fields?.["Listing Name"] ||
            matchedFdo?.fields?.["Listing Name "] ||
            matchedFdo?.fields?.["Apartment Name"] ||
            matchedFdo?.fields?.["Apartment Name "] ||
            matchedFdo?.fields?.["Apartment"] ||
            matchedFdo?.fields?.["Listing"] ||
            matchedFdo?.fields?.["Unit"] ||
            matchedFdo?.fields?.["Room"] ||
            "-";

        groupsMap[resId] = {
          reservationId: resId,
          guestName: rec.guestName || matchedFdo?.fields?.["Guest Name"] || "Guest",
          listingName: listingName,
          items: [],
          totalQuantity: 0,
          totalPrice: 0,
          currency: rec.currency || "PKR",
          lastAddedTime: rec.dateTime,
          addedBy: rec.addedBy || "-",
        };
      }

      groupsMap[resId].items.push(rec);
      groupsMap[resId].totalQuantity += isNoQtyItem(rec) ? 0 : Number(rec.quantity || 1);
      groupsMap[resId].totalPrice += Number(rec.totalPrice || rec.price || 0);

      if (new Date(rec.dateTime) > new Date(groupsMap[resId].lastAddedTime)) {
        groupsMap[resId].lastAddedTime = rec.dateTime;
        if (rec.addedBy && rec.addedBy !== "-") {
          groupsMap[resId].addedBy = rec.addedBy;
        }
      }
    });

    let list = Object.values(groupsMap);

    if (upsellSearchQuery.trim()) {
      const q = upsellSearchQuery.toLowerCase().trim();
      list = list.filter((r) => {
        const rId = String(r.reservationId).toLowerCase();
        const gName = String(r.guestName).toLowerCase();
        const lName = String(r.listingName).toLowerCase();
        const aBy = String(r.addedBy).toLowerCase();
        return rId.includes(q) || gName.includes(q) || lName.includes(q) || aBy.includes(q);
      });
    }

    return list;
  };

  const handleExportAllPDF = () => {
    const groups = getGroupedReservations();
    if (groups.length === 0) {
      setSnackbar({ open: true, color: "warning", title: "Export", content: "No reservations found for selected date range." });
      return;
    }

    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const dateRangeStr = upsellStartDate === upsellEndDate ? formatFooterDate(upsellStartDate) : `${formatFooterDate(upsellStartDate)} - ${formatFooterDate(upsellEndDate)}`;
    const entries = getSelectedDateUpsellEntries();
    const totalRevPKR = entries.reduce((sum, r) => sum + Number(r.totalPrice || r.price || 0), 0);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text("Upsell Overview Report", 40, 45);

    const overviewLabel = `Date Range: ${dateRangeStr}  |  Reservations: ${groups.length}  |  Total: `;
    const overviewAmount = formatEntriesTotal(entries, livePkrRate);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(overviewLabel, 40, 65);
    const overviewLabelWidth = doc.getTextWidth(overviewLabel);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(5, 150, 105);
    doc.text(overviewAmount, 40 + overviewLabelWidth, 65);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139);

    const tableHeaders = [["SR", "Reservation ID", "Guest Name", "Apartment Name", "Items", "Qty", "Date & Time", "Total Price", "Added By"]];
    const tableBody = groups.map((g, idx) => [
      idx + 1,
      `#${g.reservationId}`,
      g.guestName,
      g.listingName || "-",
      g.items.length ? `${g.items.length} items` : "0 items",
      g.totalQuantity,
      formatPakistanDateTime(g.lastAddedTime),
      formatReservationTotal(g, livePkrRate),
      g.addedBy || "-",
    ]);

    autoTable(doc, {
      head: tableHeaders,
      body: tableBody,
      startY: 85,
      theme: "grid",
      styles: { fontSize: 8.5, cellPadding: 6, font: "helvetica", textColor: [15, 23, 42] },
      headStyles: { fillColor: [2, 132, 199], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 40, right: 40 },
    });

    doc.save(`Upsell_Overview_${upsellStartDate}_to_${upsellEndDate}.pdf`);
    setDownloadModalOpen(false);
  };

  const handleExportAllExcel = async () => {
    const groups = getGroupedReservations();
    if (groups.length === 0) {
      setSnackbar({ open: true, color: "warning", title: "Export", content: "No reservations found for selected date range." });
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Upsell Overview");

    worksheet.columns = [
      { header: "SR", key: "sr", width: 8 },
      { header: "Reservation ID", key: "resId", width: 18 },
      { header: "Guest Name", key: "guestName", width: 24 },
      { header: "Apartment Name", key: "listingName", width: 26 },
      { header: "Upsell Items Count", key: "itemCount", width: 18 },
      { header: "Total Quantity", key: "quantity", width: 14 },
      { header: "Date & Time", key: "dateTime", width: 22 },
      { header: "Total Price", key: "totalPrice", width: 16 },
      { header: "Currency", key: "currency", width: 12 },
      { header: "Added By", key: "addedBy", width: 18 },
    ];

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0284C7" } };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };

    groups.forEach((g, idx) => {
      worksheet.addRow({
        sr: idx + 1,
        resId: `#${g.reservationId}`,
        guestName: g.guestName,
        listingName: g.listingName || "-",
        itemCount: `${g.items.length} items`,
        quantity: g.totalQuantity,
        dateTime: formatPakistanDateTime(g.lastAddedTime),
        totalPrice: Math.round(g.totalPrice),
        currency: g.currency,
        addedBy: g.addedBy || "-",
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `Upsell_Overview_${upsellStartDate}_to_${upsellEndDate}.xlsx`);
    setDownloadModalOpen(false);
  };

  const handleExportSinglePDF = (resGroup) => {
    if (!resGroup || !resGroup.items || resGroup.items.length === 0) return;

    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text(`Upsells for Reservation #${resGroup.reservationId}`, 40, 45);

    const singleLabel = `Guest Name: ${resGroup.guestName}  |  Total Amount: `;
    const singleAmount = `Rs. ${Math.round(resGroup.totalPrice).toLocaleString()}`;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139);
    doc.text(singleLabel, 40, 65);
    const singleLabelWidth = doc.getTextWidth(singleLabel);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(5, 150, 105);
    doc.text(singleAmount, 40 + singleLabelWidth, 65);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139);

    const tableHeaders = [["SR", "Upsell Item", "Category", "Qty", "Price Per Unit", "Total Price", "Date & Time", "Added By"]];
    const tableBody = resGroup.items.map((item, idx) => [
      idx + 1,
      item.upsellName || item.title || "-",
      item.category || "Service fee",
      item.quantity ?? 1,
      item.currency === "PKR" ? `${Math.round(item.pricePerUnit || 0).toLocaleString()}` : `$${Number(item.pricePerUnit || 0).toFixed(2)}`,
      item.currency === "PKR" ? `Rs. ${Number(item.price || item.totalPrice || 0).toLocaleString()}` : `$${Number(item.price || item.totalPrice || 0).toFixed(2)}`,

      formatPakistanDateTime(item.dateTime),
      item.addedBy || "-",
    ]);

    autoTable(doc, {
      head: tableHeaders,
      body: tableBody,
      startY: 85,
      theme: "grid",
      styles: { fontSize: 8.5, cellPadding: 6, font: "helvetica", textColor: [15, 23, 42] },
      headStyles: { fillColor: [2, 132, 199], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 40, right: 40 },
    });

    doc.save(`Reservation_${resGroup.reservationId}_Upsells.pdf`);
    setDownloadModalOpen(false);
  };

  const handleExportSingleExcel = async (resGroup) => {
    if (!resGroup || !resGroup.items || resGroup.items.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`Res #${resGroup.reservationId}`);

    worksheet.columns = [
      { header: "SR", key: "sr", width: 8 },
      { header: "Upsell Item", key: "name", width: 26 },
      { header: "Category", key: "category", width: 22 },
      { header: "Quantity", key: "quantity", width: 12 },
      { header: "Price Per Unit", key: "unitPrice", width: 16 },
      { header: "Total Price", key: "totalPrice", width: 16 },
      { header: "Currency", key: "currency", width: 12 },
      { header: "Date & Time", key: "dateTime", width: 24 },
      { header: "Added By", key: "addedBy", width: 18 },
    ];

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0284C7" } };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };

    resGroup.items.forEach((item, idx) => {
      worksheet.addRow({
        sr: idx + 1,
        name: item.upsellName || item.title || "-",
        category: item.category || "Service fee",
        quantity: item.quantity ?? 1,
        unitPrice: Math.round(item.pricePerUnit || 0),
        totalPrice: Math.round(item.price || item.totalPrice || 0),
        currency: item.currency || "PKR",
        dateTime: formatPakistanDateTime(item.dateTime),
        addedBy: item.addedBy || "-",
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `Reservation_${resGroup.reservationId}_Upsells.xlsx`);
    setDownloadModalOpen(false);
  };

  const handleExportChargePDF = (chargeRec) => {
    if (!chargeRec) return;

    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text(`Charge Report #${chargeRec.chargeId || chargeRec.id}`, 40, 45);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Reservation ID: #${chargeRec.reservationId}  |  Guest: ${chargeRec.guestName || "Guest"}  |  Apt: ${chargeRec.apartmentName || "-"}`, 40, 65);

    const tableHeaders = [["Field", "Details"]];
    const tableBody = [
      ["Reservation ID", `#${chargeRec.reservationId}`],
      ["Guest Name", chargeRec.guestName || "Guest"],
      ["Apartment Name", chargeRec.apartmentName || "-"],
      ["Charge ID", chargeRec.chargeId || "-"],
      ["Charge Title", chargeRec.title || "-"],
      ["Description", chargeRec.description || "-"],
      ["Amount", formatChargeAmountWithConversion(chargeRec.amount, chargeRec.currency, chargeRec.pkrRate)],
      ["Currency", chargeRec.currency || "PKR"],
      ["Date and Time", formatPakistanDateTime(chargeRec.dateTime)],
      ["Added By", chargeRec.addedBy || "-"],
    ];

    autoTable(doc, {
      head: tableHeaders,
      body: tableBody,
      startY: 85,
      theme: "grid",
      styles: { fontSize: 9.5, cellPadding: 8, font: "helvetica", textColor: [15, 23, 42] },
      headStyles: { fillColor: [2, 132, 199], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 40, right: 40 },
    });

    doc.save(`Charge_${chargeRec.chargeId || chargeRec.id}.pdf`);
    setDownloadModalOpen(false);
  };

  const handleExportChargeExcel = async (chargeRec) => {
    if (!chargeRec) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`Charge #${chargeRec.chargeId || chargeRec.id}`);

    worksheet.columns = [
      { header: "Reservation ID", key: "resId", width: 18 },
      { header: "Guest Name", key: "guestName", width: 22 },
      { header: "Apartment Name", key: "apartmentName", width: 22 },
      { header: "Charge ID", key: "chargeId", width: 18 },
      { header: "Transaction Type", key: "title", width: 26 },
      { header: "Description", key: "description", width: 30 },
      { header: "Amount", key: "amount", width: 16 },
      { header: "Currency", key: "currency", width: 12 },
      { header: "Charge Date & Time", key: "dateTime", width: 24 },
      { header: "Added By", key: "addedBy", width: 18 },
    ];

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0284C7" } };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };

    worksheet.addRow({
      resId: `#${chargeRec.reservationId}`,
      guestName: chargeRec.guestName || "Guest",
      apartmentName: chargeRec.apartmentName || "-",
      chargeId: chargeRec.chargeId || "-",
      title: chargeRec.title || "-",
      description: chargeRec.description || "-",
      amount: Number(chargeRec.amount || 0),
      currency: chargeRec.currency || "PKR",
      dateTime: formatPakistanDateTime(chargeRec.dateTime),
      addedBy: chargeRec.addedBy || "-",
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `Charge_${chargeRec.chargeId || chargeRec.id}.xlsx`);
    setDownloadModalOpen(false);
  };

  const handleExportAllChargesPDF = () => {
    const records = getFilteredCharges();
    if (records.length === 0) {
      setSnackbar({ open: true, color: "warning", title: "Export", content: "No charge records found to export." });
      return;
    }

    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const totalAmount = records.reduce((sum, r) => sum + Number(r.amount || 0), 0);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text("Charges Overview Report", 40, 45);

    const chargesLabel = `Total Records: ${records.length}  |  Total Amount: `;
    const chargesAmount = `${Math.round(totalAmount).toLocaleString()}`;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(chargesLabel, 40, 65);
    const chargesLabelWidth = doc.getTextWidth(chargesLabel);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(5, 150, 105);
    doc.text(chargesAmount, 40 + chargesLabelWidth, 65);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139);

    const tableHeaders = [["SR", "Reservation ID", "Guest Name", "Apartment Name", "Charge ID", "Title", "Description", "Date & Time", "Amount", "Added By"]];
    const tableBody = records.map((rec, idx) => [
      idx + 1,
      `#${rec.reservationId}`,
      rec.guestName || "Guest",
      rec.apartmentName || "-",
      rec.chargeId || "-",
      rec.title || "-",
      rec.description || "-",
      formatPakistanDateTime(rec.dateTime),
      formatChargeAmountWithConversion(rec.amount, rec.currency, rec.pkrRate),
      rec.addedBy || "-",
    ]);

    autoTable(doc, {
      head: tableHeaders,
      body: tableBody,
      startY: 85,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 5, font: "helvetica", textColor: [15, 23, 42] },
      headStyles: { fillColor: [2, 132, 199], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 40, right: 40 },
    });

    const todayISO = getTodayISO();
    doc.save(`Charges_Overview_${todayISO}.pdf`);
    setDownloadModalOpen(false);
  };

  const handleExportAllChargesExcel = async () => {
    const records = getFilteredCharges();
    if (records.length === 0) {
      setSnackbar({ open: true, color: "warning", title: "Export", content: "No charge records found to export." });
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Charges Overview");

    worksheet.columns = [
      { header: "SR", key: "sr", width: 8 },
      { header: "Reservation ID", key: "resId", width: 18 },
      { header: "Guest Name", key: "guestName", width: 22 },
      { header: "Apartment Name", key: "apartmentName", width: 22 },
      { header: "Charge ID", key: "chargeId", width: 18 },
      { header: "Transaction Type", key: "title", width: 24 },
      { header: "Description", key: "description", width: 30 },
      { header: "Date & Time", key: "dateTime", width: 22 },
      { header: "Amount", key: "amount", width: 16 },
      { header: "Currency", key: "currency", width: 12 },
      { header: "Added By", key: "addedBy", width: 18 },
    ];

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0284C7" } };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };

    records.forEach((rec, idx) => {
      worksheet.addRow({
        sr: idx + 1,
        resId: `#${rec.reservationId}`,
        guestName: rec.guestName || "Guest",
        apartmentName: rec.apartmentName || "-",
        chargeId: rec.chargeId || "-",
        title: rec.title || "-",
        description: rec.description || "-",
        dateTime: formatPakistanDateTime(rec.dateTime),
        amount: Number(rec.amount || 0),
        currency: rec.currency || "PKR",
        addedBy: rec.addedBy || "-",
      });
    });

    const todayISO = getTodayISO();
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `Charges_Overview_${todayISO}.xlsx`);
    setDownloadModalOpen(false);
  };

  const isTodayDate = (dateTimeRaw) => {
    if (!dateTimeRaw) return false;
    const d = new Date(dateTimeRaw);
    if (isNaN(d.getTime())) return false;

    const rYear = d.getFullYear();
    const rMonth = String(d.getMonth() + 1).padStart(2, "0");
    const rDay = String(d.getDate()).padStart(2, "0");
    const recDateISO = `${rYear}-${rMonth}-${rDay}`;

    const today = new Date();
    const tYear = today.getFullYear();
    const tMonth = String(today.getMonth() + 1).padStart(2, "0");
    const tDay = String(today.getDate()).padStart(2, "0");
    const todayISO = `${tYear}-${tMonth}-${tDay}`;

    return recDateISO === todayISO;
  };

  const getFilteredUpsells = () => {
    return dbUpsellRecords.filter((rec) => {
      // Show ONLY today's date records
      if (!isTodayDate(rec.dateTime)) {
        return false;
      }
      if (!upsellSearchQuery.trim()) return true;
      const q = upsellSearchQuery.toLowerCase().trim();
      const rId = String(rec.reservationId || "").toLowerCase();
      const gName = String(rec.guestName || "").toLowerCase();
      const aName = String(rec.apartmentName || "").toLowerCase();
      const uCategory = String(rec.category || "").toLowerCase();
      const uName = String(rec.upsellName || "").toLowerCase();
      const uAddedBy = String(rec.addedBy || "").toLowerCase();
      return rId.includes(q) || gName.includes(q) || aName.includes(q) || uCategory.includes(q) || uName.includes(q) || uAddedBy.includes(q);
    });
  };

  const isReservationCancelled = (resId) => {
    if (!resId) return false;
    const cleanId = String(resId).replace(/[^0-9]/g, "");
    if (!cleanId || !fdoRecords || fdoRecords.length === 0) return false;
    const found = fdoRecords.find((r) => {
      const f = r.fields || r || {};
      const fResId = String(
        f["Reservation ID"] ||
        f["Reservation ID "] ||
        f["Hostaway ID"] ||
        f["Reservation"] ||
        f["Res ID"] ||
        f["reservationId"] ||
        f["Reservation Id"] ||
        r.id ||
        ""
      ).replace(/[^0-9]/g, "");
      return fResId === cleanId;
    });
    if (found) {
      const f = found.fields || found || {};
      const status = String(f["Status"] || f["Apt Status"] || found.status || "").toLowerCase();
      return status.includes("cancel");
    }
    return false;
  };

  const isChargeCancelled = (rec) => {
    if (!rec) return false;
    const title = String(rec.title || "").toLowerCase();
    const desc = String(rec.description || "").toLowerCase();
    const status = String(rec.status || "").toLowerCase();
    const isRefund = status === "refunded" || rec.type === "refund" || rec.isRefund || Number(rec.amount) < 0;

    if (status === "cancelled" || title.includes("cancel charge") || desc.includes("cancel charge")) {
      return true;
    }
    // Only zero amount is ignored, negative amounts are genuine refunds and must be shown
    if (Number(rec.amount) === 0 && !isRefund) {
      return true;
    }
    return isReservationCancelled(rec.reservationId);
  };

  const getEnrichedCharge = (rec) => {
    if (!rec) return rec;
    let guestName = rec.guestName;
    let apartmentName = rec.apartmentName;

    const hasValidGuest = guestName && guestName !== "Guest" && guestName !== "-" && String(guestName).trim() !== "";
    const hasValidApt = apartmentName && apartmentName !== "-" && String(apartmentName).trim() !== "";

    if (!hasValidGuest || !hasValidApt) {
      const cleanResId = String(rec.reservationId || "").replace(/[^0-9]/g, "");
      if (cleanResId && Array.isArray(fdoRecords) && fdoRecords.length > 0) {
        const matchedFdo = fdoRecords.find((f) => {
          const fFields = f.fields || f || {};
          const fResId = String(
            fFields["Reservation ID"] ||
            fFields["Reservation ID "] ||
            fFields["Hostaway ID"] ||
            fFields["Reservation"] ||
            fFields["Res ID"] ||
            fFields["reservationId"] ||
            fFields["Reservation Id"] ||
            f.id ||
            ""
          ).replace(/[^0-9]/g, "");
          return fResId && fResId === cleanResId;
        });

        if (matchedFdo) {
          const fFields = matchedFdo.fields || matchedFdo || {};
          if (!hasValidGuest) {
            guestName =
              fFields["Guest Name"] ||
              fFields["Guest Name "] ||
              fFields["Guest"] ||
              fFields["guestName"] ||
              fFields["Name"] ||
              fFields["Guest First Name"] ||
              "";
          }
          if (!hasValidApt) {
            apartmentName =
              fFields["Listing Name"] ||
              fFields["Listing"] ||
              fFields["Apartment Name"] ||
              fFields["Apartment"] ||
              fFields["Room"] ||
              fFields["Unit"] ||
              "";
          }
        }
      }
    }

    return {
      ...rec,
      guestName: guestName || "Guest",
      apartmentName: apartmentName || "-",
    };
  };

  const getSelectedDateChargeEntries = () => {
    return dbChargeRecords
      .filter((rec) => {
        const rawDate = rec.dateTime || rec.date_time || rec.fields?.["Charge Date and Time"] || rec.fields?.["Date and Time"] || rec.fields?.["date_time"] || rec.createdAt || rec.created_at;
        if (!rawDate) return !isChargeCancelled(rec);
        const d = new Date(rawDate);
        if (isNaN(d.getTime())) return !isChargeCancelled(rec);
        const rYear = d.getFullYear();
        const rMonth = String(d.getMonth() + 1).padStart(2, "0");
        const rDay = String(d.getDate()).padStart(2, "0");
        const recDateISO = `${rYear}-${rMonth}-${rDay}`;

        if (chargeStartDate && recDateISO < chargeStartDate) return false;
        if (chargeEndDate && recDateISO > chargeEndDate) return false;
        return !isChargeCancelled(rec);
      })
      .map(getEnrichedCharge);
  };

  const getFilteredCharges = () => {
    return getSelectedDateChargeEntries().filter((rec) => {
      if (!chargeSearchQuery.trim()) return true;
      const q = chargeSearchQuery.toLowerCase().trim();
      const rId = String(rec.reservationId || "").toLowerCase();
      const cId = String(rec.chargeId || "").toLowerCase();
      const gName = String(rec.guestName || "").toLowerCase();
      const aName = String(rec.apartmentName || "").toLowerCase();
      const cTitle = String(rec.title || "").toLowerCase();
      const cDesc = String(rec.description || "").toLowerCase();
      const cAddedBy = String(rec.addedBy || "").toLowerCase();
      return (
        rId.includes(q) ||
        cId.includes(q) ||
        gName.includes(q) ||
        aName.includes(q) ||
        cTitle.includes(q) ||
        cDesc.includes(q) ||
        cAddedBy.includes(q)
      );
    });
  };

  // Teable Charges List Database State (tbl4qyGheBahXMVGxLI)
  const [dbChargeRecords, setDbChargeRecords] = useState(() => {
    try {
      const cached = sessionStorage.getItem("cached_db_charge_records");
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [loadingDbCharges, setLoadingDbCharges] = useState(() => {
    try {
      const cached = sessionStorage.getItem("cached_db_charge_records");
      return !cached;
    } catch (e) {
      return true;
    }
  });
  const [chargeSearchQuery, setChargeSearchQuery] = useState("");

  const fetchDbChargeRecords = async () => {
    try {
      const cached = sessionStorage.getItem("cached_db_charge_records");
      if (!cached) {
        setLoadingDbCharges(true);
      }
      try {
        const backendUrl = `${API_BASE_URL || ""}/api/charges/list`;
        const res = await fetch(backendUrl);
        if (res.ok) {
          const data = await res.json();
          if (data?.success && Array.isArray(data.records)) {
            setDbChargeRecords(data.records);
            try { sessionStorage.setItem("cached_db_charge_records", JSON.stringify(data.records)); } catch (e) { }
          }
        }
      } catch (e) {
        console.warn("Backend charge fetch notice:", e);
      }
    } finally {
      setLoadingDbCharges(false);
    }
  };

  const fetchDbUpsellRecords = async () => {
    try {
      const cached = sessionStorage.getItem("cached_db_upsell_records");
      if (!cached) {
        setLoadingDbUpsells(true);
      }
      try {
        const backendUrl = `${API_BASE_URL || ""}/api/upsells/list`;
        const res = await fetch(backendUrl);
        if (res.ok) {
          const data = await res.json();
          if (data?.success && Array.isArray(data.records)) {
            setDbUpsellRecords(data.records);
            try { sessionStorage.setItem("cached_db_upsell_records", JSON.stringify(data.records)); } catch (e) { }
          }
        }
      } catch (e) {
        console.warn("Backend upsells fetch notice:", e);
      }
    } finally {
      setLoadingDbUpsells(false);
    }
  };

  const fetchFdoRecords = async () => {
    try {
      const cached = sessionStorage.getItem("cached_fdo_records");
      if (!cached) {
        setLoadingFdo(true);
      }

      // Get today's date in PKT (same as FDO panel)
      const todayPKT = new Date(Date.now() + (5 * 60 * 60 * 1000)).toISOString().split("T")[0];
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      // PRIMARY: Fetch from /api/fdo/reservations with today's date (same as FDO panel)
      try {
        const queryParams = new URLSearchParams({
          _: Date.now(),
          startDate: todayPKT,
          endDate: todayPKT,
        });
        const backendUrl = `${API_BASE_URL || ""}/api/fdo/reservations?${queryParams.toString()}`;
        const res = await fetch(backendUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          cache: "no-store",
        });
        if (res.ok) {
          const data = await res.json();
          const apiRecords = data.data || data.records || [];
          if (apiRecords.length > 0) {
            // Normalize records to have .fields (same format FDO panel uses)
            const records = apiRecords.map((r) => {
              if (r.fields) return r;
              return {
                id: String(r.reservation_id || r.id),
                ...r,
                fields: {
                  "Guest Name": r.guest_name,
                  "Reservation ID": r.reservation_id,
                  "Listing Name": r.listing_name,
                  "Arrival Date": r.arrival_date,
                  "Departure Date": r.departure_date,
                  "Status": r.status,
                  "Actual Checkin": r.actual_checkin,
                  "Actual Checkout": r.actual_checkout,
                  "Payment Status": r.payment_status,
                  "Total Price": r.total_price,
                  "Total Paid": r.total_paid,
                  "Currency": r.currency,
                  "ADR": r.adr,
                  "Phone": r.phone,
                  "Number of Guests": r.number_of_guests,
                  "Channel Name": r.channel_name,
                  "Booking Date": r.booking_date,
                },
              };
            });
            setFdoRecords(records);
            try { sessionStorage.setItem("cached_fdo_records", JSON.stringify(records)); } catch (e) { }
            return;
          }
        }
      } catch (backendErr) {
        console.warn("Backend /api/fdo/reservations notice, trying combined-data:", backendErr.message);
      }

      // FALLBACK 1: Fetch from /api/fdo/combined-data (no date filter)
      try {
        const combinedUrl = `${API_BASE_URL || ""}/api/fdo/combined-data`;
        const res = await fetch(combinedUrl);
        if (res.ok) {
          const data = await res.json();
          if (data?.success && Array.isArray(data.records) && data.records.length > 0) {
            setFdoRecords(data.records);
            try { sessionStorage.setItem("cached_fdo_records", JSON.stringify(data.records)); } catch (e) { }
            return;
          }
        }
      } catch (combinedErr) {
        console.warn("Backend combined-data notice, falling back to Teable:", combinedErr.message);
      }

      // FALLBACK 2: Fetch from Teable if backend is unavailable
      const fdoUrl = process.env.REACT_APP_TEABLE_TABLE_URL_FDO_MAIN || "https://teable.namuve.com/api/table/tbliOdo8ldmMO8rrYyN/record";
      const fdoToken = process.env.REACT_APP_TEABLE_API_TOKEN_FDO || process.env.REACT_APP_TEABLE_BEARER_TOKEN || process.env.REACT_APP_TEABLE_API_TOKEN || "";
      const teableAuth = fdoToken.startsWith("Bearer ") ? fdoToken : `Bearer ${fdoToken}`;

      const res = await fetch(`${fdoUrl}?take=1000`, {
        headers: {
          "Authorization": teableAuth,
          "Content-Type": "application/json",
        },
      });
      if (res.ok) {
        const data = await res.json();
        const records = data.records || data.data || [];
        setFdoRecords(records);
        try { sessionStorage.setItem("cached_fdo_records", JSON.stringify(records)); } catch (e) { }
      }
    } catch (err) {
      console.warn("Could not fetch reservations:", err);
    } finally {
      setLoadingFdo(false);
    }
  };

  // Auto-clear ALL notifications and error toasts after exactly 8 seconds
  useEffect(() => {
    if (!snackbar.open) return;
    const timer = setTimeout(() => {
      setSnackbar((prev) => ({ ...prev, open: false }));
    }, 8000);
    return () => clearTimeout(timer);
  }, [snackbar.open]);

  useEffect(() => {
    if (!refundErrorToast) return;
    const timer = setTimeout(() => setRefundErrorToast(""), 8000);
    return () => clearTimeout(timer);
  }, [refundErrorToast]);

  useEffect(() => {
    if (!chargeErrorToast) return;
    const timer = setTimeout(() => setChargeErrorToast(""), 8000);
    return () => clearTimeout(timer);
  }, [chargeErrorToast]);

  useEffect(() => {
    if (!upsellErrorToast) return;
    const timer = setTimeout(() => setUpsellErrorToast(""), 8000);
    return () => clearTimeout(timer);
  }, [upsellErrorToast]);

  // Live 30-second countdown timer for refund processing (30, 29, 28 ... 1s)
  useEffect(() => {
    let interval = null;
    if (isSavingRefund) {
      setRefundElapsedSeconds(30);
      interval = setInterval(() => {
        setRefundElapsedSeconds((prev) => Math.max(1, prev - 1));
      }, 1000);
    } else {
      setRefundElapsedSeconds(30);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isSavingRefund]);

  useEffect(() => {
    // Run initial fetches in parallel
    Promise.all([
      fetchFdoRecords(),
      fetchDbUpsellRecords(),
      fetchDbChargeRecords(),
    ]);
  }, []);

  const getActualCheckInTime = (fields, rec) => {
    if (rec && Array.isArray(rec.customFieldValues)) {
      const customObj = rec.customFieldValues.find(
        (f) => f.customFieldId === 76281 || f.customField?.name === "Actual Check-in Time"
      );
      if (customObj && customObj.value) return String(customObj.value).trim();
    }
    if (!fields) return "";
    const rawVal =
      fields["Actual Checkin"] ||
      fields["Actual Check-in"] ||
      fields["Actual Check-In"] ||
      fields["Actual Check-in Time"] ||
      fields["Actual Check-In Time"] ||
      "";
    return String(rawVal || "").trim();
  };

  const getActualCheckOutTime = (fields, rec) => {
    if (rec && Array.isArray(rec.customFieldValues)) {
      const customObj = rec.customFieldValues.find(
        (f) => f.customFieldId === 76282 || f.customField?.name === "Actual Check-out Time"
      );
      if (customObj && customObj.value) return String(customObj.value).trim();
    }
    if (!fields) return "";
    const rawVal =
      fields["Actual Checkout"] ||
      fields["Actual Check-out"] ||
      fields["Actual Check-Out"] ||
      fields["Actual Check-out Time"] ||
      fields["Actual Check-Out Time"] ||
      "";
    return String(rawVal || "").trim();
  };

  const getFdoCategoryIndex = (status) => {
    const s = String(status || "").toLowerCase().trim();
    if (s.includes("staying") || s.includes("current stay")) return 1; // Staying guest
    if (s.includes("upcoming checkout") || s.includes("upcoming check out")) return 2; // Upcoming Checkout
    if (s.includes("check out") || s.includes("checked out")) return 3; // Checked out
    return 0; // Checked in / Upcoming stay / Default
  };

  const parseDateToYYYYMMDD = (val) => {
    if (!val) return "";
    let str = val;
    if (typeof val === "object" && val instanceof Date && !isNaN(val)) {
      str = val.toISOString();
    }
    str = String(str).trim();
    if (str.toLowerCase() === "n/a" || str === "") return "";

    if (str.includes("/")) {
      const parts = str.split(",")[0].trim().split("/");
      if (parts.length === 3) {
        const month = parts[0].padStart(2, "0");
        const day = parts[1].padStart(2, "0");
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
    } else if (str.includes("-")) {
      return str.split("T")[0].split(" ")[0];
    }
    return "";
  };

  const formatDateRange = (arrRaw, depRaw) => {
    if (!arrRaw && !depRaw) return "N/A";
    const d1 = new Date(String(arrRaw).trim());
    const d2 = new Date(String(depRaw).trim());
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    if (!isNaN(d1.getTime()) && !isNaN(d2.getTime())) {
      const day1 = String(d1.getDate()).padStart(2, "0");
      const day2 = String(d2.getDate()).padStart(2, "0");
      const m1 = months[d1.getMonth()];
      const m2 = months[d2.getMonth()];
      const y1 = d1.getFullYear();
      const y2 = d2.getFullYear();

      if (m1 === m2 && y1 === y2) {
        return `${day1}ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Å“${day2} ${m1} ${y1}`;
      } else if (y1 === y2) {
        return `${day1} ${m1} ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Å“ ${day2} ${m2} ${y1}`;
      } else {
        return `${day1} ${m1} ${y1} ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Å“ ${day2} ${m2} ${y2}`;
      }
    }
    return `${arrRaw || "N/A"} ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Å“ ${depRaw || "N/A"}`;
  };

  const formatDisplayDate = (raw) => {
    if (!raw) return "N/A";
    const str = String(raw).trim();
    if (str.toLowerCase() === "n/a" || str === "") return "N/A";

    let parseStr = str;
    if (!parseStr.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(parseStr) && !parseStr.toLowerCase().endsWith("utc")) {
      if (parseStr.includes("T")) {
        parseStr = `${parseStr}Z`;
      } else if (parseStr.includes(" ")) {
        parseStr = `${parseStr.replace(" ", "T")}Z`;
      }
    }

    let d = new Date(parseStr);
    if (isNaN(d.getTime())) d = new Date(str);
    if (!isNaN(d.getTime())) {
      if (str.includes("T") || str.includes(":")) {
        return d.toLocaleString("en-US", {
          timeZone: "Asia/Karachi",
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }).replace(",", "");
      }
      return d.toLocaleDateString("en-US", {
        timeZone: "Asia/Karachi",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
    return str;
  };

  const formatDateTimeString = (raw) => {
    return formatPakistanDateTime(raw, true);
  };

  const getTodayDateStr = () => {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Karachi",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(new Date());
    } catch (e) {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
  };

  const getRecordStack = (rec) => {
    const fields = rec.fields || {};

    // Read status â€” same field priority as FDO panel's getValue()
    const rawStatus = String(
      rec.status || fields["Status"] || fields["Apt Status"] || ""
    ).trim();
    let stackVal = rawStatus || "Unknown";

    // Step 1: Normalize status string â€” exact same conditions as FDO panel (lines 7186-7204)
    const lowerStatus = rawStatus.toLowerCase().replace(/[-_]/g, " ");
    if (lowerStatus === "early checked in" || lowerStatus === "early check in" || lowerStatus === "early checkin") {
      stackVal = "Early Checked In";
    } else if (lowerStatus === "same day checked out" || lowerStatus === "same day check out" || lowerStatus === "same day checkout") {
      stackVal = "Same Day Checked Out";
    } else if (lowerStatus === "early checked out" || lowerStatus === "early check out" || lowerStatus === "early checkout") {
      stackVal = "Early Checked Out";
    } else if (lowerStatus === "checked in" || lowerStatus === "checkin") {
      stackVal = "Checked In";
    } else if (lowerStatus === "checked out" || lowerStatus === "checkout") {
      stackVal = "Checked Out";
    } else if (lowerStatus === "upcoming stay") {
      stackVal = "Upcoming Stay";
    } else if (lowerStatus === "upcoming checkout" || lowerStatus === "upcoming check out") {
      stackVal = "Upcoming Checkout";
    } else if (lowerStatus === "staying guest" || lowerStatus === "staying" || lowerStatus === "in house" || lowerStatus === "inhouse") {
      stackVal = "Staying Guest";
    } else if (lowerStatus === "no show" || lowerStatus === "noshow") {
      stackVal = "No Show";
    }

    // Step 2: Read timestamps â€” same field priority as FDO panel (lines 7206-7207)
    const actualIn = fields["Actual Checkin"] || fields["Actual Check-in"] || rec.actual_checkin || null;
    const actualOut = fields["Actual Checkout"] || fields["Actual Check-out"] || rec.actual_checkout || null;
    const arrDate = fields["Arrival Date"] || rec.arrival_date || "";
    const depDate = fields["Departure Date"] || rec.departure_date || "";

    // Step 3: Helper functions â€” exact same as FDO panel (lines 7211-7230)
    const getPktDateStr = (val) => {
      if (!val || val === "N/A") return null;
      const d = new Date(val);
      if (isNaN(d.getTime())) return null;
      const isRaw = typeof val === "string" && !(/Z$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(val.trim()));
      if (isRaw) {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      }
      const pkt = new Date(d.getTime() + (5 * 60 * 60 * 1000));
      return pkt.toISOString().split("T")[0];
    };

    const getPktHourNum = (val) => {
      if (!val || val === "N/A") return null;
      const d = new Date(val);
      if (isNaN(d.getTime())) return null;
      const isRaw = typeof val === "string" && !(/Z$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(val.trim()));
      if (isRaw) return d.getHours();
      return new Date(d.getTime() + (5 * 60 * 60 * 1000)).getUTCHours();
    };

    // Step 4: PKT "today" â€” same as FDO panel (line 7232)
    const nowPktStr = new Date(Date.now() + (5 * 60 * 60 * 1000)).toISOString().split("T")[0];
    const arrDateClean = arrDate ? String(arrDate).split("T")[0].split(" ")[0] : "";
    const depDateClean = depDate ? String(depDate).split("T")[0].split(" ")[0] : "";

    // Step 5: Unconditional timestamp override â€” exact same as FDO panel (lines 7237-7258)
    if (actualOut && actualOut !== "N/A") {
      const inDatePkt = getPktDateStr(actualIn);
      const outDatePkt = getPktDateStr(actualOut);
      const isSameDayStay = (arrDateClean && depDateClean && arrDateClean === depDateClean) || (arrDateClean && outDatePkt === arrDateClean);
      const isTodayCheckout = outDatePkt === nowPktStr;

      if (isTodayCheckout && isSameDayStay) {
        stackVal = "Same Day Checked Out";
      } else if (isTodayCheckout && depDateClean && outDatePkt < depDateClean) {
        stackVal = "Early Checked Out";
      } else {
        stackVal = "Checked Out";
      }
    } else if (actualIn && actualIn !== "N/A") {
      const inHour = getPktHourNum(actualIn);
      const inDatePkt = getPktDateStr(actualIn);
      if (nowPktStr <= arrDateClean && inDatePkt && inDatePkt < arrDateClean) {
        stackVal = "Early Checked In";
      } else if (arrDateClean === nowPktStr && inDatePkt === arrDateClean && inHour !== null && inHour < 15) {
        stackVal = "Early Checked In";
      } else if (arrDateClean === nowPktStr) {
        stackVal = "Checked In";
      } else if (depDateClean === nowPktStr) {
        stackVal = "Upcoming Checkout";
      } else if (arrDateClean < nowPktStr && depDateClean > nowPktStr) {
        stackVal = "Staying Guest";
      }
    } else {
      const isStatusModified =
        lowerStatus === "modified" ||
        String(rec.status || "").toLowerCase() === "modified" ||
        String(rec.reservation_status || "").toLowerCase() === "modified" ||
        String(fields.Status || "").toLowerCase() === "modified" ||
        String(fields["Reservation Status"] || "").toLowerCase() === "modified";

      // Date-based fallback when no actual check-in time is recorded.
      if (lowerStatus === "no show" || lowerStatus === "noshow" || stackVal === "No Show") {
        stackVal = "No Show";
      } else if (
        arrDateClean &&
        arrDateClean !== nowPktStr &&
        (isStatusModified || arrDateClean < nowPktStr)
      ) {
        // If arrival date is not equal to today's date and reservation status is modified (or arrival in past without checkin) -> No Show
        stackVal = "No Show";
      } else if (arrDateClean < nowPktStr && depDateClean > nowPktStr) {
        // Clearly mid-stay: arrived before today, departs after today → Staying Guest
        stackVal = "Staying Guest";
      } else if (depDateClean && depDateClean < nowPktStr) {
        // Departure has already passed, never checked in → No Show
        stackVal = "No Show";
      } else {
        // Arriving today (not yet logged), future arrivals, same-day, departing today with no data
        // → keep as Upcoming Stay (matches production behaviour)
        stackVal = "Upcoming Stay";
      }
    }

    const VALID_STACKS = [
      "Early Checked In", "Checked In", "Staying Guest", "Upcoming Checkout",
      "Early Checked Out", "Checked Out", "Same Day Checked Out", "Upcoming Stay", "No Show"
    ];
    return VALID_STACKS.includes(stackVal) ? stackVal : "Unknown";
  };

  const isRecordInCategory = (rec, targetCatIdx) => {
    const stack = getRecordStack(rec);
    if (targetCatIdx === 0) return stack === "Early Checked In";
    if (targetCatIdx === 1) return stack === "Checked In";
    if (targetCatIdx === 2) return stack === "Staying Guest";
    if (targetCatIdx === 3) return stack === "Upcoming Checkout";
    if (targetCatIdx === 4) return stack === "Early Checked Out";
    if (targetCatIdx === 5) return stack === "Checked Out";
    if (targetCatIdx === 6) return stack === "Same Day Checked Out";
    if (targetCatIdx === 7) return stack === "Upcoming Stay";
    if (targetCatIdx === 8) return stack === "No Show";
    return false;
  };

  const allCombinedFdoRecords = fdoRecords;

  const countEarlyCheckIn = allCombinedFdoRecords.filter(r => isRecordInCategory(r, 0)).length;
  const countCheckIn = allCombinedFdoRecords.filter(r => isRecordInCategory(r, 1)).length;
  const countStaying = allCombinedFdoRecords.filter(r => isRecordInCategory(r, 2)).length;
  const countUpcomingCheckout = allCombinedFdoRecords.filter(r => isRecordInCategory(r, 3)).length;
  const countEarlyCheckOut = allCombinedFdoRecords.filter(r => isRecordInCategory(r, 4)).length;
  const countCheckOut = allCombinedFdoRecords.filter(r => isRecordInCategory(r, 5)).length;
  const countSameDayCheckOut = allCombinedFdoRecords.filter(r => isRecordInCategory(r, 6)).length;
  const countUpcomingStay = allCombinedFdoRecords.filter(r => isRecordInCategory(r, 7)).length;
  const countNoShow = allCombinedFdoRecords.filter(r => isRecordInCategory(r, 8)).length;

  const filteredFdoRecords = allCombinedFdoRecords.filter((rec) => {
    const fields = rec.fields || {};
    if (fdoSearchQuery.trim()) {
      const q = fdoSearchQuery.toLowerCase().trim();
      const resId = String(fields["Reservation ID"] || rec.id || "").toLowerCase();
      const guest = String(fields["Guest Name"] || "").toLowerCase();
      const room = String(fields["Listing Name"] || "").toLowerCase();
      const status = String(fields["Status"] || fields["Apt Status"] || rec.status || "").toLowerCase();
      return resId.includes(q) || guest.includes(q) || room.includes(q) || status.includes(q);
    }
    return isRecordInCategory(rec, resSubTab);
  });

  useEffect(() => {
    async function fetchExchangeRate() {
      // 1. Try fetching from Backend route
      try {
        const backendUrl = `${API_BASE_URL || ""}/api/upsells/exchange-rate`;
        const res = await fetch(backendUrl);
        if (res.ok) {
          const data = await res.json();
          if (data?.success && data?.rate && !isNaN(data.rate) && data.rate > 0) {
            setLivePkrRate(parseFloat(Number(data.rate).toFixed(2)));
            return;
          }
        }
      } catch (e) {
        // Continue to direct Teable fetch
      }

      // 2. Direct Teable Table tblwOgPKznCj4t7x9xI (TEABLE_REVENUE_EXPENSES_TABLE_URL)
      try {
        const tableUrl =
          process.env.REACT_APP_TEABLE_REVENUE_EXPENSES_TABLE_URL ||
          process.env.TEABLE_REVENUE_EXPENSES_TABLE_URL ||
          process.env.REACT_APP_QB_API_USD_RATE ||
          "https://teable.namuve.com/api/table/tblwOgPKznCj4t7x9xI/record";
        const token =
          process.env.REACT_APP_TEABLE_REVENUE_EXPENSES_TOKEN ||
          process.env.TEABLE_REVENUE_EXPENSES_TOKEN ||
          process.env.REACT_APP_QB_PAYMENT_TOKEN ||
          process.env.REACT_APP_TEABLE_API_TOKEN_PAYMENTS ||
          "";

        const authHeader = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
        const cleanUrl = tableUrl.split("?")[0];
        const res = await fetch(`${cleanUrl}?take=1000`, {
          headers: {
            Authorization: authHeader,
            "Content-Type": "application/json",
          },
        });

        if (res.ok) {
          const data = await res.json();
          const records = data.records || [];
          if (records.length > 0) {
            // Get the last record
            const lastRecord = records[records.length - 1];
            const fields = lastRecord.fields || {};
            const usdBuyingVal =
              fields["USD Buying"] ||
              fields["USD Buying "] ||
              fields["usd buying"] ||
              fields["USD Buying Rate"] ||
              fields["USD Selling"] ||
              fields["Rate"] ||
              fields["rate"];

            const parsedRate = parseFloat(String(usdBuyingVal || "0").replace(/[^0-9.-]/g, ""));
            if (!isNaN(parsedRate) && parsedRate > 0) {
              setLivePkrRate(parseFloat(parsedRate.toFixed(2)));
              return;
            }
          }
        }
      } catch (err) {
        console.warn("Teable Revenue Expenses exchange rate fetch notice:", err);
      }

      // 3. Fallback to Open Exchange Rates API
      try {
        const res = await fetch("https://open.er-api.com/v6/latest/USD");
        if (res.ok) {
          const data = await res.json();
          if (data?.rates?.PKR) {
            setLivePkrRate(parseFloat(data.rates.PKR.toFixed(2)));
          }
        }
      } catch (e) {
        console.warn("Live exchange rate fetch notice:", e);
      }
    }
    fetchExchangeRate();
  }, []);

  const detectCurrencyFromReservation = (resId = selectedResId) => {
    const cleanId = String(resId || "").replace(/[^0-9]/g, "");

    // 1. Check loaded reservationDetails first if matching ID
    if (reservationDetails && String(reservationDetails.id || "").replace(/[^0-9]/g, "") === cleanId) {
      if (reservationDetails.currency) {
        const resCurr = String(reservationDetails.currency).toUpperCase();
        if (resCurr.includes("PKR") || resCurr.includes("RS")) return "PKR";
        if (resCurr.includes("USD") || resCurr.includes("$")) return "USD";
      }
      if (reservationDetails.channelName || reservationDetails.channelId) {
        const ch = String(reservationDetails.channelName || reservationDetails.channelId || "").toLowerCase();
        if (ch.includes("airbnb") || ch.includes("booking") || ch.includes("vrbo") || ch.includes("expedia")) {
          return "USD";
        }
      }
    }

    // 2. Check Teable fdoRecords record for explicit Currency or monetary fields
    if (cleanId && fdoRecords && fdoRecords.length > 0) {
      const rec = fdoRecords.find((r) => {
        const rResId = String(r.id || "").replace(/[^0-9]/g, "");
        const rFieldResId = String(
          r.fields?.["Reservation ID"] ||
          r.fields?.["Reservation ID "] ||
          r.fields?.["Hostaway ID"] ||
          r.fields?.["Reservation"] ||
          r.fields?.["Res ID"] ||
          ""
        ).replace(/[^0-9]/g, "");
        return (rResId && rResId === cleanId) || (rFieldResId && rFieldResId === cleanId);
      });

      if (rec && rec.fields) {
        const explicitCurr = String(
          rec.fields["Currency"] ||
          rec.fields["Currency "] ||
          rec.fields["currency"] ||
          ""
        ).toUpperCase();
        if (explicitCurr.includes("PKR") || explicitCurr.includes("RS")) return "PKR";
        if (explicitCurr.includes("USD") || explicitCurr.includes("$")) return "USD";

        const monetaryStr = String(
          rec.fields["Total Amount"] ||
          rec.fields["Total Amount "] ||
          rec.fields["Price"] ||
          rec.fields["Price "] ||
          rec.fields["Amount (ADR)"] ||
          rec.fields["ADR"] ||
          ""
        ).toUpperCase();

        if (monetaryStr.includes("PKR") || monetaryStr.includes("RS")) return "PKR";
        if (monetaryStr.includes("USD") || monetaryStr.includes("$")) return "USD";

        const channelStr = String(rec.fields["Channel ID"] || rec.fields["Channel"] || rec.fields["Source"] || "").toLowerCase();
        if (channelStr.includes("airbnb") || channelStr.includes("booking") || channelStr.includes("vrbo") || channelStr.includes("expedia")) {
          return "USD";
        }
        if (channelStr.includes("direct") || channelStr.includes("walk-in") || channelStr.includes("manual") || channelStr.includes("fdo")) {
          return "PKR";
        }
      }
    }

    return "PKR";
  };

  const detectCurrencyFromFields = (fields, resId) => {
    if (fields) {
      const explicitCurr = String(
        fields["Currency"] ||
        fields["Currency "] ||
        fields["currency"] ||
        ""
      ).toUpperCase();
      if (explicitCurr.includes("USD") || explicitCurr.includes("$")) return "USD";
      if (explicitCurr.includes("PKR") || explicitCurr.includes("RS")) return "PKR";

      const monetaryStr = String(
        fields["Total Amount"] ||
        fields["Total Amount "] ||
        fields["Price"] ||
        fields["Price "] ||
        fields["Amount (ADR)"] ||
        fields["ADR"] ||
        ""
      ).toUpperCase();

      if (monetaryStr.includes("USD") || monetaryStr.includes("$")) return "USD";
      if (monetaryStr.includes("PKR") || monetaryStr.includes("RS")) return "PKR";

      const channelStr = String(
        fields["Channel ID"] ||
        fields["Channel"] ||
        fields["Source"] ||
        ""
      ).toLowerCase();
      if (channelStr.includes("airbnb") || channelStr.includes("booking") || channelStr.includes("vrbo") || channelStr.includes("expedia")) {
        return "USD";
      }
      if (channelStr.includes("direct") || channelStr.includes("walk-in") || channelStr.includes("manual") || channelStr.includes("fdo")) {
        return "PKR";
      }
    }
    return detectCurrencyFromReservation(resId);
  };

  const getEffectiveRate = () => reservationDetails?.pkrRate || livePkrRate || 0;

  // Load reservation when selectedResId changes
  // Normalizes any upsell item from Hostaway or localStorage to official Category & Field Name
  const normalizeUpsellItem = (item, pkrRate = getEffectiveRate()) => {
    const norm = (str) => String(str || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const rawTitle = item.fieldName || item.title || item.name || item.customName || "";
    const rawCategory = item.category || "";

    const titleNorm = norm(rawTitle);
    const catNorm = norm(rawCategory);

    let officialField = null;

    if (titleNorm.includes("latecheckout") || titleNorm.includes("checkout")) {
      officialField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === "field-6");
    } else if (titleNorm.includes("earlycheckin") || titleNorm.includes("checkin")) {
      officialField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === "field-1");
    } else if (titleNorm.includes("midstay") || titleNorm.includes("cleaning")) {
      officialField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === "field-2");
    } else if (titleNorm.includes("extrabed") || titleNorm.includes("mattress")) {
      officialField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === "field-5");
    } else if (titleNorm.includes("parking")) {
      officialField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === "field-8");
    } else if (titleNorm.includes("towel")) {
      officialField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === "field-11");
    } else if (titleNorm.includes("comforter")) {
      officialField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === "field-4");
    } else if (titleNorm.includes("sheet") || titleNorm.includes("linen") || titleNorm.includes("bedsheet")) {
      officialField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === "field-3");
    } else if (titleNorm.includes("bottle") || titleNorm.includes("water")) {
      officialField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === "field-9");
    } else if (titleNorm.includes("tissue")) {
      officialField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === "field-10");
    } else if (titleNorm.includes("damage")) {
      officialField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === "field-7");
    } else if (titleNorm.includes("driver") || titleNorm.includes("accommodation")) {
      officialField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === "field-12");
    } else if (titleNorm.includes("recheckin") || titleNorm.includes("recheck")) {
      officialField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === "field-13");
    }

    if (!officialField) {
      officialField = OFFICIAL_UPSELL_FIELDS.find(
        (f) => norm(f.name) === titleNorm
      );
    }

    const finalCategory = officialField ? officialField.category : (rawCategory || "Service fee");
    const finalName = officialField ? officialField.name : (rawTitle || "Upsell Fee");

    const totalUSD = parseFloat(item.totalUSD ?? item.total ?? item.value ?? item.unitPrice ?? 0);
    const totalPKR = item.totalPKR ? item.totalPKR : (pkrRate > 0 ? Math.round(totalUSD * pkrRate) : 0);

    return {
      ...item,
      category: finalCategory,
      fieldName: finalName,
      totalUSD,
      totalPKR,
    };
  };

  useEffect(() => {
    if (!selectedResId) {
      setReservationDetails(null);
      setAppliedUpsells([]);
      return;
    }

    async function fetchLiveReservation() {
      setLoadingReservation(true);
      try {
        const res = await fetch(`${API_BASE_URL || ""}/api/upsells/reservations/${selectedResId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.reservation) {
            setReservationDetails(data.reservation);
            setPaymentLink(`https://guest-portal.hostaway.com/${data.reservation.id}/2b3y0A-f`);

            // Determine live currency cleanly without flipping
            let resolvedCurr = null;
            if (data.reservation.currency) {
              const cStr = String(data.reservation.currency).toUpperCase();
              if (cStr.includes("USD") || cStr.includes("$")) resolvedCurr = "USD";
              else if (cStr.includes("PKR") || cStr.includes("RS")) resolvedCurr = "PKR";
            }
            if (!resolvedCurr && (data.reservation.channelName || data.reservation.channelId)) {
              const ch = String(data.reservation.channelName || data.reservation.channelId).toLowerCase();
              if (ch.includes("airbnb") || ch.includes("booking") || ch.includes("vrbo") || ch.includes("expedia")) {
                resolvedCurr = "USD";
              }
            }
            if (resolvedCurr) {
              setCurrency(resolvedCurr);
              setUpsellCurrency(resolvedCurr);
            }

            try {
              const currentPkrRate = data.reservation.pkrRate || livePkrRate;

              const EXCLUDED_FINANCIAL_KEYS = [
                "baserate",
                "totalpaid",
                "airbnbpayoutsum",
                "hostchannelfee",
                "totalpricefromchannel",
                "cancellationhostfee",
                "tax",
                "cleaningfee",
                "totalprice",
                "netamount",
                "hostawayfee",
                "channelcommission",
                "commission",
                "payoutsum",
                "payout",
              ];

              const hostawayFees = (data.reservation.financeField || [])
                .filter((f) => {
                  const normName = String(f.name || f.title || f.alias || f.customName || "").toLowerCase().replace(/[^a-z0-9]/g, "");
                  if (EXCLUDED_FINANCIAL_KEYS.some((key) => normName.includes(key))) {
                    return false;
                  }
                  return f.type === "fee" || f.type === "upsell" || f.isUpsell;
                })
                .map((f, i) =>
                  normalizeUpsellItem(
                    {
                      id: `hostaway-fee-${f.id || i}`,
                      fieldId: f.name || `field-${i}`,
                      fieldName: f.feeTitle || f.customName || f.title || f.alias || f.name || "Fee",
                      category: f.title || f.feeType || f.name || f.type || "Fee",
                      unitPrice: f.value || f.total || 0,
                      qty: f.units || f.quantity || 1,
                      totalUSD: f.total || f.value || 0,
                      addedDate: new Date().toLocaleDateString("en-CA"),
                      hostawaySynced: true,
                    },
                    currentPkrRate
                  )
                );

              const saved = localStorage.getItem(`applied_upsells_${selectedResId}`);
              const localSaved = (saved ? JSON.parse(saved) : [])
                .filter((item) => {
                  const normName = String(item.fieldName || item.title || item.name || item.category || "").toLowerCase().replace(/[^a-z0-9]/g, "");
                  return !EXCLUDED_FINANCIAL_KEYS.some((key) => normName.includes(key));
                })
                .map((item) => normalizeUpsellItem(item, currentPkrRate));

              // Prioritize live Hostaway fees and eliminate stale local duplicates by category/fieldName
              const finalFees = [...hostawayFees];
              localSaved.forEach((lf) => {
                const isDuplicate = finalFees.some(
                  (hf) => hf.category === lf.category || hf.fieldName === lf.fieldName
                );
                if (!isDuplicate) {
                  finalFees.push(lf);
                }
              });

              setAppliedUpsells(finalFees);
              localStorage.setItem(`applied_upsells_${selectedResId}`, JSON.stringify(finalFees));
            } catch (e) {
              setAppliedUpsells([]);
            }
          } else {
            setReservationDetails(null);
            setSnackbar({
              open: true,
              color: "warning",
              title: "Reservation Not Found",
              content: `No active Hostaway reservation found for Res ID-${selectedResId}`,
            });
          }
        } else {
          setReservationDetails(null);
        }
      } catch (err) {
        console.warn("Could not fetch live reservation details:", err);
        setReservationDetails(null);
      } finally {
        setLoadingReservation(false);
      }
    }
    fetchLiveReservation();
  }, [selectedResId]);

  // Save applied upsells to localStorage
  useEffect(() => {
    if (selectedResId) {
      localStorage.setItem(`applied_upsells_${selectedResId}`, JSON.stringify(appliedUpsells));
    }
  }, [appliedUpsells, selectedResId]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    const trimmed = searchInput.trim();
    if (!trimmed) {
      setSnackbar({
        open: true,
        color: "warning",
        title: "Input Required",
        content: "Please enter a Hostaway Reservation ID.",
      });
      return;
    }
    setSelectedResId(trimmed);
    const newUrl = `${window.location.pathname}?reservationId=${trimmed}`;
    window.history.pushState({ path: newUrl }, "", newUrl);
  };

  const handleClearReservation = () => {
    setSelectedResId("");
    setSearchInput("");
    setReservationDetails(null);
    setAppliedUpsells([]);
    window.history.pushState({}, "", window.location.pathname);
  };

  const handleOpenAddModal = (resId) => {
    const targetResId = typeof resId === "string" ? resId : selectedResId;
    setSelectedFieldId("");
    setPriceInput("");
    setQtyInput("1");
    const autoCurrency = detectCurrencyFromReservation(targetResId);
    setUpsellCurrency(autoCurrency);
    setOpenModal(true);
  };

  const handleOpenChargeModal = (resId, initialDetails) => {
    setChargeModalStep(1); // Start on Page 1: Financial & Payment Overview
    setChargeType("Charge");
    setSelectedChargeName("");
    setChargeDescription("");
    setChargeAmountInput("");
    setChargeErrorToast("");
    setIncludeInPriceBreakdown(true);
    const targetResId = typeof resId === "string" && resId ? resId : selectedResId;
    const autoCurr = detectCurrencyFromReservation(targetResId);
    setCurrency(autoCurr);
    setPaymentLink(`https://guest-portal.hostaway.com/${reservationDetails?.id || targetResId || ""}/2b3y0A-f`);

    const cleanResId = String(targetResId).replace(/[^0-9]/g, "");
    if (cleanResId) {
      if (initialDetails?.listingName || initialDetails?.apartmentName || initialDetails?.guestName) {
        const initTotal = Number(initialDetails.totalPrice || 0);
        const initPaid = Number(initialDetails.paidAmount || 0);
        const initPending = Number(initialDetails.pendingAmount || 0);
        setReservationDetails((prev) => ({
          ...(prev || {}),
          ...initialDetails,
          id: cleanResId,
          listingName: initialDetails.listingName || initialDetails.apartmentName || prev?.listingName || "",
          apartmentName: initialDetails.apartmentName || initialDetails.listingName || prev?.apartmentName || "",
          totalPrice: initTotal || prev?.totalPrice || 0,
          paidAmount: initPaid || prev?.paidAmount || 0,
          pendingAmount: initPending || prev?.pendingAmount || 0,
        }));
        if (initTotal > 0) setHostawayTotalPrice(initTotal);
        if (initPaid > 0) setHostawayPaidAmount(initPaid);
        if (initPending > 0) setHostawayPendingAmount(initPending);
      } else {
        // Check if we can find reservation info immediately from fdoRecords, dbUpsellRecords, or charges
        const matchedFdo =
          (fdoRecords || []).find((r) => {
            const f = r.fields || r;
            const id = String(f["Reservation ID"] || f["Reservation ID "] || f.ReservationId || f.reservationId || f.id || "").replace(/[^0-9]/g, "");
            return id === cleanResId;
          }) ||
          (dbUpsellRecords || []).find((r) => {
            const f = r.fields || r;
            const id = String(f["Reservation ID"] || f["Reservation ID "] || f.ReservationId || f.reservationId || f.id || "").replace(/[^0-9]/g, "");
            return id === cleanResId;
          }) ||
          (dbChargeRecords || []).find((c) => {
            const id = String(c.reservationId || c.reservation_id || c.resId || "").replace(/[^0-9]/g, "");
            return id === cleanResId;
          });

        const f = matchedFdo?.fields || matchedFdo || {};
        const aptFound =
          f["Listing Name"] ||
          f["Apartment Name"] ||
          f["Listing"] ||
          f["Unit"] ||
          f.ListingName ||
          f.listingName ||
          f.apartmentName ||
          f.room ||
          f.apartment_name ||
          "";
        const guestFound = f["Guest Name"] || f.GuestName || f.guestName || "";
        const fTotal = Number(f["Total Price"] || f["Total Amount"] || f["Amount"] || f.totalPrice || f.total_price || f.total || 0);
        const fPaid = Number(f["Paid Amount"] || f["Paid"] || f.paidAmount || f.paid_amount || 0);
        const fPending = Number(f["Pending Amount"] || f["Pending"] || f.pendingAmount || f.pending_amount || 0);

        setReservationDetails((prev) => ({
          ...(prev || {}),
          id: cleanResId,
          guestName: guestFound || prev?.guestName || "Guest",
          listingName: aptFound || prev?.listingName || prev?.apartmentName || "",
          apartmentName: aptFound || prev?.apartmentName || prev?.listingName || "",
          totalPrice: fTotal || prev?.totalPrice || 0,
          paidAmount: fPaid || prev?.paidAmount || 0,
          pendingAmount: fPending || prev?.pendingAmount || 0,
        }));
        if (fTotal > 0) setHostawayTotalPrice(fTotal);
        if (fPaid > 0) setHostawayPaidAmount(fPaid);
        if (fPending > 0) setHostawayPendingAmount(fPending);
      }

      setLoadingFinanceFields(true);
      setHostawayFinanceFields([]);
      setHostawayPayments([]);
      setHostawayRefundIdMap({});
      setHostawayRefundToChargeMap({});
      setHostawayTotalPrice(0);
      setHostawayPaidAmount(0);
      setHostawayPendingAmount(0);
      setHostawayPaymentStatus("paid");

      // 1. Fetch finance fields (Fast Primary Request)
      fetch(`${API_BASE_URL || ""}/api/charges/finance-fields/${cleanResId}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.success) {
            const newListingName = data.listingName || data.apartmentName || "";
            const newGuestName = data.guestName || "";
            setReservationDetails((prev) => ({
              ...(prev || {}),
              listingName: newListingName || prev?.listingName || prev?.apartmentName || "",
              apartmentName: newListingName || prev?.apartmentName || prev?.listingName || "",
              guestName: newGuestName || prev?.guestName || "Guest",
            }));
            if (Array.isArray(data.financeFields)) {
              setHostawayFinanceFields(data.financeFields);
            }
            const tPrice = data.totalPrice || data.totalPriceFromChannel || 0;
            const pAmount = data.paidAmount || 0;
            const pendAmount = data.pendingAmount ?? Math.max(0, tPrice - pAmount);
            setHostawayTotalPrice(tPrice);
            setHostawayPaidAmount(pAmount);
            setHostawayPendingAmount(pendAmount);
            setHostawayPaymentStatus(data.paymentStatus || "paid");
            const isDollar = (autoCurr || currency || "").toUpperCase() === "USD";
            if (pendAmount > 0) {
              setChargeAmountInput((prev) => (!prev || prev === "0" ? (isDollar ? Number(pendAmount).toFixed(2) : String(Math.round(pendAmount))) : prev));
            }
            // If payment-details has not populated yet, use Hostaway payments as initial
            if (Array.isArray(data.payments) && data.payments.length > 0) {
              setHostawayPayments((prev) => deduplicatePayments(prev.length === 0 ? data.payments : [...prev, ...data.payments]));
            }
            if (data.refundIdMap && typeof data.refundIdMap === "object") {
              setHostawayRefundIdMap(data.refundIdMap);
            }
            if (data.refundToChargeIdMap && typeof data.refundToChargeIdMap === "object") {
              setHostawayRefundToChargeMap(data.refundToChargeIdMap);
            }
          }
        })
        .catch((err) => {
          console.warn("Failed to fetch Hostaway finance fields:", err);
        })
        .finally(() => {
          // Release spinner immediately as soon as primary finance data arrives
          setLoadingFinanceFields(false);
        });

      // 2. Fetch payment details from Teable (Non-blocking background enhancement)
      fetch(`${API_BASE_URL || ""}/api/payment-details/${cleanResId}`)
        .then((r) => r.json())
        .then((data) => {
          const records = data.data || data.records || [];
          if (Array.isArray(records) && records.length > 0) {
            const paidRecords = records.filter((record) => {
              const f = record.fields || record || {};
              let statusVal = f.Status || f.status || "";
              if (typeof statusVal === "object" && statusVal !== null) {
                statusVal = statusVal.name || statusVal.value || String(statusVal);
              }
              const status = String(statusVal).toLowerCase().trim();
              return status === "paid" || status === "successded" || status === "succeeded" || status === "refunded";
            });
            const actualPayments = paidRecords.length > 0 ? paidRecords : records;
            setHostawayPayments((prev) => deduplicatePayments([...prev, ...actualPayments]));
          }
        })
        .catch((err) => {
          console.warn("Failed to fetch payment details from Teable:", err);
        });

      // 3. Fetch upsells for this reservation from Teable
      fetch(`${API_BASE_URL || ""}/api/upsells/reservations/${cleanResId}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.success && data.reservation) {
            setReservationDetails((prev) => ({
              ...prev,
              ...data.reservation,
            }));
            if (Array.isArray(data.reservation.upsells) && data.reservation.upsells.length > 0) {
              setAppliedUpsells(data.reservation.upsells);
            }
          }
        })
        .catch((err) => {
          console.warn("Failed to fetch reservation upsells:", err);
        });
    }

    setOpenChargeModal(true);
  };

  const handleOpenViewUpsellModal = (res) => {
    setSelectedReservationForView(res);
    setViewUpsellModalOpen(true);
  };

  const handleSaveChargeModal = () => {
    if (!selectedChargeName) {
      setChargeErrorToast("Please select a Transaction Type.");
      setSnackbar({
        open: true,
        color: "error",
        title: "Validation Error",
        content: "Please select a Transaction Type.",
      });
      return;
    }

    const amountVal = parseFloat(chargeAmountInput);
    if (isNaN(amountVal) || amountVal <= 0) {
      setChargeErrorToast("Please enter a valid price amount.");
      setSnackbar({
        open: true,
        color: "error",
        title: "Validation Error",
        content: "Please enter a valid price amount.",
      });
      return;
    }

    setChargeErrorToast("");
    setOpenChargeConfirmModal(true);
  };

  const handleExecuteCharge = async () => {
    const activeCurrency = currency || detectCurrencyFromReservation(selectedResId);
    const amountVal = parseFloat(chargeAmountInput);
    const rate = reservationDetails?.pkrRate || livePkrRate || 0;
    let totalUSD = amountVal;
    let totalPKR = rate > 0 ? Math.round(amountVal * rate) : 0;

    if (activeCurrency === "PKR") {
      totalPKR = Math.round(amountVal);
      totalUSD = rate > 0 ? parseFloat((amountVal / rate).toFixed(2)) : 0;
    }

    const displayName = `${selectedChargeName}${chargeDescription ? ` (${chargeDescription})` : ""}`;

    const newCharge = {
      id: `charge-${Date.now()}`,
      fieldId: selectedChargeName,
      fieldName: displayName,
      category: `Transaction (${chargeType})`,
      unitPrice: totalUSD,
      qty: 1,
      totalUSD,
      totalPKR,
      addedDate: new Date().toLocaleDateString("en-CA"),
      hostawaySynced: true,
    };

    const targetResId = reservationDetails?.id || selectedResId || "";
    const matchedFdo = (fdoRecords || []).find(
      (f) => String(f.fields?.["Reservation ID "] || f.fields?.["Reservation ID"] || f.id || "") === String(targetResId)
    );
    const guestNameStr =
      reservationDetails?.guestName ||
      (reservationDetails?.guestFirstName
        ? `${reservationDetails.guestFirstName} ${reservationDetails.guestLastName || ""}`.trim()
        : "") ||
      matchedFdo?.fields?.["Guest Name"] ||
      matchedFdo?.fields?.["Guest Name "] ||
      "";
    const apartmentNameStr =
      reservationDetails?.listingName ||
      reservationDetails?.listing?.name ||
      reservationDetails?.room ||
      matchedFdo?.fields?.["Listing Name"] ||
      matchedFdo?.fields?.["Listing"] ||
      matchedFdo?.fields?.["Apartment Name"] ||
      matchedFdo?.fields?.["Apartment Name "] ||
      matchedFdo?.fields?.["Room"] ||
      "";

    const finalAmount = activeCurrency === "PKR" ? totalPKR : totalUSD;
    const nowIso = new Date().toISOString();

    const newDbRecord = {
      id: `charge-${Date.now()}`,
      reservationId: Number(targetResId) || targetResId || 0,
      chargeId: Date.now(),
      guestName: guestNameStr || "Guest",
      apartmentName: apartmentNameStr || "-",
      title: selectedChargeName,
      description: chargeDescription && chargeDescription.trim() ? chargeDescription.trim() : selectedChargeName,
      amount: Number(finalAmount),
      dateTime: nowIso,
      currency: activeCurrency,
      addedBy: user?.name || user?.username || "You",
      rawFields: {},
    };

    setIsSavingCharge(true);
    try {
      setAppliedUpsells([newCharge, ...appliedUpsells]);
      setDbChargeRecords((prev) => {
        const updated = [newDbRecord, ...(Array.isArray(prev) ? prev : [])];
        try { sessionStorage.setItem("cached_db_charge_records", JSON.stringify(updated)); } catch (e) { }
        return updated;
      });

      const endpoint = `${API_BASE_URL || ""}/api/charges/add`;
      const dateStr = nowIso.replace("T", " ").substring(0, 19);

      await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reservationId: targetResId,
          guestName: guestNameStr,
          apartmentName: apartmentNameStr,
          title: selectedChargeName,
          name: selectedChargeName,
          type: chargeType,
          description: chargeDescription && chargeDescription.trim() ? chargeDescription.trim() : selectedChargeName,
          amount: finalAmount,
          currency: activeCurrency,
          amountUsd: totalUSD,
          amountPkr: totalPKR,
          includeInPriceBreakdown: includeInPriceBreakdown ? 1 : 0,
          paymentMethod: "bank_transfer",
          status: "paid",
          scheduledDate: dateStr,
          chargeDate: dateStr,
          addedBy: user?.name || user?.username || "",
        }),
      });

      setOpenChargeConfirmModal(false);
      setOpenChargeModal(false);
      await fetchDbChargeRecords();
    } catch (err) {
      console.warn("Hostaway Sync Notice:", err.message);
      setSnackbar({
        open: true,
        color: "error",
        title: "Save Failed",
        content: "An error occurred while saving the charge.",
      });
      setOpenChargeConfirmModal(false);
    } finally {
      setIsSavingCharge(false);
    }
  };

  const normalizeTransactionAccountTitle = (rawTitle) => {
    const t = String(rawTitle || "").trim();
    if (!t) return "";
    const u = t.toUpperCase();
    if (u.includes("KEENU")) return "KEENU-BNR";
    if (u.includes("CIH")) return "CIH-BNR";
    if (u.includes("MZN") || u.includes("MEEZAN")) return "MZN-BNR01";
    return t;
  };

  const deduplicatePayments = (paymentsList) => {
    if (!Array.isArray(paymentsList)) return [];
    const seen = new Set();
    const result = [];

    for (const item of paymentsList) {
      if (!item) continue;
      const f = item.fields || item || {};
      const rawId = f.id || f.ID || f.Id || f.chargeId || f.charge_id || f["Charge ID"] || item.id;
      const cleanId = rawId !== undefined && rawId !== null && String(rawId).trim() !== "" ? String(rawId).trim() : "";

      let key = "";
      if (cleanId && cleanId !== "0" && cleanId !== "-") {
        key = `id_${cleanId}`;
      } else {
        const amt = String(f.Amount || f.amount || "");
        const title = String(f.Title || f.title || f.name || "");
        const date = String(f["Charge Date"] || f.chargeDate || f.date || "");
        key = `fallback_${amt}_${title}_${date}`;
      }

      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      result.push(item);
    }

    // Sort chronologically ascending so early timestamps (10 AM, 11 AM, 12 PM...) display in proper sequence
    return result.sort((a, b) => {
      const fa = a.fields || a || {};
      const fb = b.fields || b || {};
      const dateA = new Date(fa["Charge Date"] || fa.chargeDate || fa.date || fa.dateTime || fa.date_time || fa.created_at || fa.createdAt || 0).getTime();
      const dateB = new Date(fb["Charge Date"] || fb.chargeDate || fb.date || fb.dateTime || fb.date_time || fb.created_at || fb.createdAt || 0).getTime();
      if (!isNaN(dateA) && !isNaN(dateB) && dateA !== dateB) {
        return dateA - dateB;
      }
      const idA = Number(fa.id || fa.ID || a.id || 0);
      const idB = Number(fb.id || fb.ID || b.id || 0);
      return idA - idB;
    });
  };

  const getRefundAvailableCharges = () => {
    const currentResId = String(reservationDetails?.id || selectedResId || "").replace(/[^0-9]/g, "");
    const list = [];
    const seenIds = new Set();

    // 1. Calculate net available balance across deduplicated payments for this reservation
    const payments = deduplicatePayments(hostawayPayments);
    const totalPaid = payments.reduce((sum, record) => {
      const f = record.fields || record.raw || record || {};
      const rawAmt = Number(String(f.Amount || f.amount || "0").replace(/[^0-9.-]/g, ""));
      return sum + (isNaN(rawAmt) || rawAmt <= 0 ? 0 : rawAmt);
    }, 0);
    const totalRefunded = payments.reduce((sum, record) => {
      const f = record.fields || record.raw || record || {};
      const rawAmt = Number(String(f.Amount || f.amount || "0").replace(/[^0-9.-]/g, ""));
      return sum + (isNaN(rawAmt) || rawAmt >= 0 ? 0 : Math.abs(rawAmt));
    }, 0);
    const netAvailableBalance = Math.max(0, parseFloat((totalPaid - totalRefunded).toFixed(2)));

    // Track refunds per charge ID without double counting
    const refundedByChargeId = {};

    payments.forEach((p) => {
      const f = p.fields || p.raw || p || {};
      const statusVal = String(f.Status || f.status || "").toLowerCase().trim();
      const typeVal = String(f.Type || f.type || "").toLowerCase().trim();
      const descVal = String(f.Description || f.description || f.Title || f.title || "").toLowerCase();
      const amt = Number(String(f.Amount || f.amount || "0").replace(/[^0-9.-]/g, ""));
      const isRefund = statusVal === "refunded" || (statusVal === "succeeded" && amt < 0) || typeVal === "refund" || descVal.includes("refund") || (!isNaN(amt) && amt < 0);

      if (isRefund && amt < 0) {
        const targetRefId = getOriginalChargeId(p, payments, dbChargeRecords);
        if (targetRefId && targetRefId !== "-") {
          refundedByChargeId[targetRefId] = parseFloat(((refundedByChargeId[targetRefId] || 0) + Math.abs(amt)).toFixed(2));
        }
      }
    });

    // If hostawayPayments is empty, fall back to dbChargeRecords
    if (payments.length === 0) {
      (dbChargeRecords || []).forEach((c) => {
        const rId = String(c.reservationId || c.reservation_id || "").replace(/[^0-9]/g, "");
        if (rId && rId === currentResId) {
          const amt = Number(c.amount || 0);
          const isRefund = c.status === "refunded" || c.type === "refund" || amt < 0 || c.isRefund;
          if (isRefund && amt < 0) {
            const targetRefId = getOriginalChargeId(c, [], dbChargeRecords);
            if (targetRefId && targetRefId !== "-") {
              refundedByChargeId[targetRefId] = parseFloat(((refundedByChargeId[targetRefId] || 0) + Math.abs(amt)).toFixed(2));
            }
          }
        }
      });
    }

    // 1. From deduplicated hostaway payments
    payments.forEach((p) => {
      const f = p.fields || p.raw || p || {};
      const cId = String(f.id || f.ID || f.Id || f.chargeId || f.charge_id || f["Charge ID"] || p.id || "").trim();
      const statusVal = String(f.Status || f.status || "").toLowerCase().trim();
      const typeVal = String(f.Type || f.type || "").toLowerCase().trim();
      const descVal = String(f.Description || f.description || f.Title || f.title || "").toLowerCase();
      const amt = Number(String(f.Amount || f.amount || "0").replace(/[^0-9.-]/g, ""));

      const isRefund = statusVal === "refunded" || (statusVal === "succeeded" && amt < 0) || typeVal === "refund" || descVal.includes("refund") || (!isNaN(amt) && amt < 0);
      const isTimestampId = cId.length > 11 || Number(cId) > 100000000000;

      // Include valid non-timestamp charges
      if (cId && !isRefund && !isTimestampId && !seenIds.has(cId)) {
        seenIds.add(cId);
        const title = f.Title || f.title || f.name || f.Name || f["Transaction Account"] || f.concept || f.Description || f.description || "MZN-BNR01";
        const alreadyRefunded = refundedByChargeId[cId] || 0;
        const remainingRefundable = Math.min(netAvailableBalance, Math.max(0, parseFloat((amt - alreadyRefunded).toFixed(2))));
        list.push({
          id: cId,
          title: normalizeTransactionAccountTitle(title),
          amount: amt,
          refundableAmount: remainingRefundable,
          currency: f.Currency || f.currency || currency || "PKR",
          status: typeof statusVal === "object" ? statusVal?.name || "" : String(statusVal || "paid"),
        });
      }
    });

    // 2. From dbChargeRecords (PostgreSQL) - only for charges not already present in payments
    (dbChargeRecords || []).forEach((c) => {
      const rId = String(c.reservationId || c.reservation_id || "").replace(/[^0-9]/g, "");
      if (rId && rId === currentResId) {
        const cId = String(c.chargeId || c.id || "").trim();
        const statusVal = String(c.status || "").toLowerCase().trim();
        const typeVal = String(c.type || "").toLowerCase().trim();
        const descVal = String(c.description || c.title || "").toLowerCase();
        const amt = Number(c.amount || 0);

        const isRefund = statusVal === "refunded" || typeVal === "refund" || descVal.includes("refund") || amt < 0 || c.isRefund;
        const isTimestampId = cId.length > 11 || Number(cId) > 100000000000;

        if (cId && !isRefund && !isTimestampId && !seenIds.has(cId)) {
          seenIds.add(cId);
          const alreadyRefunded = refundedByChargeId[cId] || 0;
          const remainingRefundable = Math.min(netAvailableBalance, Math.max(0, parseFloat((amt - alreadyRefunded).toFixed(2))));
          list.push({
            id: cId,
            title: normalizeTransactionAccountTitle(c.title || c.name || ""),
            amount: amt,
            refundableAmount: remainingRefundable,
            currency: c.currency || currency || "PKR",
            status: c.status || "paid",
          });
        }
      }
    });

    return list;
  };

  const handleOpenRowMenu = (event, record) => {
    event.stopPropagation();
    setRowMenuAnchor(event.currentTarget);
    setSelectedRowRecord(record);
  };

  const handleCloseRowMenu = () => {
    setRowMenuAnchor(null);
    setSelectedRowRecord(null);
  };

  const handleRowRefundClick = (rowRecord) => {
    const targetRecord = rowRecord || selectedRowRecord;
    if (!targetRecord) return;
    const payments = deduplicatePayments(hostawayPayments);
    const fields = targetRecord.fields || targetRecord || {};
    const rawAmt = Number(String(fields.Amount || fields.amount || "0").replace(/[^0-9.-]/g, ""));
    const origCId = getOriginalChargeId(targetRecord, payments, dbChargeRecords);
    const rowTitle = fields.Title || fields.title || fields.name || fields.concept || "MZN-BNR01";
    const normTitle = normalizeTransactionAccountTitle(rowTitle);

    handleCloseRowMenu();

    // 1. Calculate how much has already been refunded on this specific charge
    let refundedOnThisCharge = 0;
    (payments || []).forEach((p) => {
      const pf = p.fields || p || {};
      const pAmt = Number(String(pf.Amount || pf.amount || "0").replace(/[^0-9.-]/g, ""));
      const pType = String(pf.Type || pf.type || "").toLowerCase();
      const pStatus = String(pf.Status || pf.status || "").toLowerCase();
      const pDesc = String(pf.Description || pf.description || pf.Title || pf.title || "").toLowerCase();
      const isPRefund = pType === "refund" || pStatus === "refunded" || pDesc.includes("refund") || (!isNaN(pAmt) && pAmt < 0);

      if (isPRefund && pAmt < 0) {
        const targetRefId = getOriginalChargeId(p, payments, dbChargeRecords);
        if (targetRefId && String(targetRefId) === String(origCId)) {
          refundedOnThisCharge += Math.abs(pAmt);
        }
      }
    });

    const isDollar = (currency || "").toUpperCase() === "USD";
    const baseAmt = Math.abs(rawAmt);
    const remainingAmt = Math.max(0, parseFloat((baseAmt - refundedOnThisCharge).toFixed(2)));

    // 2. Set all refund fields for this charge
    setRefundDetectedChargeId(origCId);
    setRefundTitle(normTitle);
    setRefundChargeId(normTitle);
    setRefundDetectedDeposit(baseAmt);
    setRefundExistingRefunds(refundedOnThisCharge);
    setRefundReason("Other");
    setRefundDescription("");
    setRefundErrorToast("");
    setIncludeInPriceBreakdown(true);

    if (remainingAmt > 0) {
      setRefundAmountInput(isDollar ? Number(remainingAmt).toFixed(2) : String(Math.round(remainingAmt)));
    } else {
      setRefundAmountInput("0");
      setRefundErrorToast(`Charge amount is 0 for Charge ID #${origCId}. Refund cannot be processed.`);
    }

    // 3. Switch directly to Step 3: Refund Security Deposit
    setChargeModalStep(3);
    setOpenRefundModal(true);
  };

  const handleOpenRefundModal = (resIdStr, initialResObj = null) => {
    const cleanResId = String(resIdStr || selectedResId || "").replace(/[^0-9]/g, "");
    if (!cleanResId) return;

    setOpenChargeModal(false);

    // 1. Resolve Matched Record from initialResObj or fdoRecords
    const matchedFdo = (fdoRecords || []).find((r) => {
      const f = r.fields || r;
      const rId = String(f["Reservation ID"] || f["Reservation ID "] || f["Hostaway Reservation ID"] || f.ReservationId || f.reservationId || f.id || "").replace(/[^0-9]/g, "");
      return rId === cleanResId;
    });
    const f = initialResObj?.rawFields || initialResObj?.fields || initialResObj || matchedFdo?.fields || matchedFdo || {};

    // 2. Resolve matched DB charges
    const matchedDbList = (dbChargeRecords || []).filter((c) => String(c.reservationId || c.reservation_id) === cleanResId && !isChargeCancelled(c));
    const secDbRecord = matchedDbList.find((c) => {
      const t = String(c.title || c.name || c.description || "").toLowerCase();
      return (t.includes("security") || t.includes("deposit") || t.includes("cih") || t.includes("mzn")) && Number(c.amount) > 0;
    }) || matchedDbList[0];

    // 3. Resolve Security Deposit Amount immediately
    let initialSecDep = Number(
      f["Security Deposit"] ||
      f["Security Deposit PKR"] ||
      f["Security Deposit USD"] ||
      f["Deposit"] ||
      f["Security Deposit Amount"] ||
      f.securityDeposit ||
      secDbRecord?.amount ||
      0
    );

    // 4. Resolve Charge ID immediately
    let initialCId = String(
      f["Charge ID"] ||
      f["ChargeId"] ||
      f["chargeId"] ||
      f["charge_id"] ||
      secDbRecord?.chargeId ||
      secDbRecord?.charge_id ||
      secDbRecord?.id ||
      ""
    ).trim();

    // 5. Resolve Transaction Account Title immediately (start empty so placeholder shows)
    let initialTitle = "";
    const autoCurr = detectCurrencyFromFields(f, cleanResId) || detectCurrencyFromReservation(cleanResId) || "PKR";
    const isDollar = (autoCurr || "").toUpperCase() === "USD";

    // 6. Set ALL fields simultaneously at the exact same time
    setCurrency(autoCurr);
    setRefundTitle("");
    setRefundChargeId("");
    setRefundDetectedChargeId(initialCId);
    setRefundReason("Other");
    setRefundDescription("");
    setRefundErrorToast("");
    setRefundDetectedDeposit(initialSecDep);
    setRefundExistingRefunds(0);
    setIncludeInPriceBreakdown(true);

    if (initialSecDep > 0) {
      setRefundAmountInput(isDollar ? Number(initialSecDep).toFixed(2) : String(Math.round(initialSecDep)));
    } else {
      setRefundAmountInput("");
    }

    setLoadingFinanceFields(true);
    setHostawayFinanceFields([]);
    setHostawayPayments([]);
    setHostawayRefundIdMap({});
    setHostawayRefundToChargeMap({});

    // 7. Load finance fields & payments simultaneously with strict timeout to prevent freeze
    const fetchWithTimeout = (url, ms = 3500) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), ms);
      return fetch(url, { signal: controller.signal })
        .then((r) => r.json())
        .catch(() => ({ success: false }))
        .finally(() => clearTimeout(timer));
    };

    Promise.all([
      fetchWithTimeout(`${API_BASE_URL || ""}/api/charges/finance-fields/${cleanResId}`, 4000),
      fetchWithTimeout(`${API_BASE_URL || ""}/api/payment-details/${cleanResId}`, 3000),
    ]).then(([finData, payData]) => {
      const fFields = finData?.success && Array.isArray(finData.financeFields) ? finData.financeFields : [];
      const hostPayments = finData?.success && Array.isArray(finData.payments) ? finData.payments : [];
      const teablePayments = Array.isArray(payData?.data) ? payData.data : (Array.isArray(payData?.records) ? payData.records : []);
      const combinedPayments = deduplicatePayments([...hostPayments, ...teablePayments]);

      if (finData?.success) {
        const newListingName = finData.listingName || finData.apartmentName || "";
        const newGuestName = finData.guestName || "";
        if (newListingName || newGuestName) {
          setReservationDetails((prev) => ({
            ...(prev || {}),
            listingName: newListingName || prev?.listingName || prev?.apartmentName || "",
            apartmentName: newListingName || prev?.apartmentName || prev?.listingName || "",
            guestName: newGuestName || prev?.guestName || "Guest",
          }));
        }
      }

      setHostawayFinanceFields(fFields);
      setHostawayPayments(combinedPayments);
      // Store the refundIdMap from backend so table can resolve IDs for any row (incl. Teable)
      if (finData?.refundIdMap && typeof finData.refundIdMap === "object") {
        setHostawayRefundIdMap(finData.refundIdMap);
      }
      if (finData?.refundToChargeIdMap && typeof finData.refundToChargeIdMap === "object") {
        setHostawayRefundToChargeMap(finData.refundToChargeIdMap);
      }

      // Detect security deposit, Charge ID & Title from combined payments & finance fields
      let secDepAmt = initialSecDep;
      let secDepChargeId = initialCId;
      let secDepTitle = "";

      const secField = fFields.find((ff) => {
        const name = String(ff.name || ff.alias || ff.feeName || ff.concept || "").toLowerCase();
        return name.includes("security") || name.includes("deposit");
      });
      if (secField) {
        const fVal = Number(secField.value || secField.total || secField.price || 0);
        if (fVal > 0) secDepAmt = fVal;
      }

      let prevRefundTotal = 0;
      combinedPayments.forEach((p) => {
        const pf = p.fields || p;
        const titleStr = String(pf.Title || pf.title || pf.name || pf.Name || pf["Transaction Account"] || pf.Description || pf.description || "").trim();
        const rawAmt = Number(String(pf.Amount || pf.amount || "0").replace(/[^0-9.-]/g, ""));
        const typeStr = String(pf.Type || pf.type || "").toLowerCase();
        const statusStr = String(pf.Status || pf.status || "").toLowerCase();
        const cid = pf.id || pf.ID || pf.Id || pf.chargeId || pf.charge_id || pf["Charge ID"] || p.id;

        if (titleStr.toLowerCase().includes("security") && !titleStr.toLowerCase().includes("refund") && rawAmt > 0) {
          if (secDepAmt === 0) secDepAmt = rawAmt;
          if (cid && !secDepChargeId) {
            secDepChargeId = String(cid);
            secDepTitle = titleStr;
          }
        }
        if (typeStr === "refund" || statusStr === "refunded" || titleStr.toLowerCase().includes("refund") || rawAmt < 0) {
          prevRefundTotal += Math.abs(rawAmt);
        }
      });

      // Find only genuine positive charges (skip refunds and timestamp IDs)
      const validPositivePayments = combinedPayments.filter((p) => {
        const pf = p.fields || p || {};
        const rawAmt = Number(String(pf.Amount || pf.amount || "0").replace(/[^0-9.-]/g, ""));
        const typeStr = String(pf.Type || pf.type || "").toLowerCase();
        const statusStr = String(pf.Status || pf.status || "").toLowerCase();
        const titleStr = String(pf.Title || pf.title || pf.name || pf.Name || pf["Transaction Account"] || pf.Description || pf.description || "").toLowerCase();
        const cid = String(pf.id || pf.ID || pf.Id || pf.chargeId || pf.charge_id || pf["Charge ID"] || p.id || "").trim();
        const isTimestamp = cid.length > 11 || Number(cid) > 100000000000;
        const isRefund = typeStr === "refund" || statusStr === "refunded" || titleStr.includes("refund") || rawAmt < 0;
        return !isRefund && !isTimestamp && rawAmt > 0;
      });

      if (!secDepChargeId && validPositivePayments.length > 0) {
        const p0 = validPositivePayments[0];
        const pf0 = p0.fields || p0;
        secDepChargeId = String(pf0.id || pf0.ID || pf0.Id || pf0.chargeId || pf0.charge_id || pf0["Charge ID"] || p0.id || "");
        if (!secDepTitle) {
          secDepTitle = String(pf0.Title || pf0.title || pf0.name || pf0.Name || pf0["Transaction Account"] || pf0.Description || pf0.description || "");
        }
      }

      if (secDepChargeId && (secDepChargeId.length > 11 || Number(secDepChargeId) > 100000000000)) {
        secDepChargeId = "";
      }

      if (secDepChargeId) {
        setRefundDetectedChargeId(secDepChargeId);
      }
      if (secDepTitle) {
        const norm = normalizeTransactionAccountTitle(secDepTitle);
        setRefundTitle(norm);
        setRefundChargeId(norm);
      }

      // Calculate total refunded specifically on this selected charge (not entire reservation)
      let refundedOnThisCharge = 0;
      combinedPayments.forEach((p) => {
        const pf = p.fields || p || {};
        const pAmt = Number(String(pf.Amount || pf.amount || "0").replace(/[^0-9.-]/g, ""));
        const pType = String(pf.Type || pf.type || "").toLowerCase();
        const pStatus = String(pf.Status || pf.status || "").toLowerCase();
        const pDesc = String(pf.Description || pf.description || pf.Title || pf.title || "").toLowerCase();
        const isPRefund = pType === "refund" || pStatus === "refunded" || pDesc.includes("refund") || (!isNaN(pAmt) && pAmt < 0);

        if (isPRefund && pAmt < 0) {
          const origCId = getOriginalChargeId(p, combinedPayments, dbChargeRecords);
          if (origCId && String(origCId) === String(secDepChargeId)) {
            refundedOnThisCharge += Math.abs(pAmt);
          }
        }
      });

      setRefundDetectedDeposit(secDepAmt);
      setRefundExistingRefunds(refundedOnThisCharge);

      const remainingSec = Math.max(0, parseFloat((secDepAmt - refundedOnThisCharge).toFixed(2)));
      if (remainingSec > 0) {
        setRefundAmountInput(isDollar ? Number(remainingSec).toFixed(2) : String(Math.round(remainingSec)));
        setRefundErrorToast("");
      } else if (secDepAmt > 0) {
        setRefundAmountInput("0");
        setRefundErrorToast(secDepChargeId ? `Charge amount is 0 for Charge ID #${secDepChargeId}. Refund cannot be processed.` : "Charge amount is 0. Refund cannot be processed.");
      } else {
        setRefundAmountInput("");
      }
    }).finally(() => {
      setLoadingFinanceFields(false);
    });

    setChargeModalStep(3);
    setOpenChargeModal(true);
    setOpenRefundModal(true);
  };

  const handleSaveRefundModal = () => {
    if (!refundDetectedChargeId) {
      setRefundErrorToast("Please select a Charge ID.");
      return;
    }

    const charges = getRefundAvailableCharges();
    const matched = charges.find((c) => String(c.id).trim() === String(refundDetectedChargeId).trim());
    let refundableAmt = matched ? (matched.refundableAmount !== undefined ? Number(matched.refundableAmount) : Number(matched.amount)) : 0;
    let initialAmt = matched ? Number(matched.amount || 0) : 0;

    // Fallback if matched wasn't found in getRefundAvailableCharges list
    if (!matched && refundDetectedChargeId) {
      const allP = deduplicatePayments(hostawayPayments);
      const directCharge = allP.find((p) => {
        const pf = p.fields || p.raw || p || {};
        const cid = String(pf.id || pf.ID || pf.Id || p.id || "").trim();
        return cid === String(refundDetectedChargeId).trim();
      });
      if (directCharge) {
        const df = directCharge.fields || directCharge.raw || directCharge || {};
        const damt = Number(String(df.Amount || df.amount || "0").replace(/[^0-9.-]/g, ""));
        initialAmt = damt;
        let alreadyRef = 0;
        allP.forEach((p) => {
          const pf = p.fields || p.raw || p || {};
          const amt = Number(String(pf.Amount || pf.amount || "0").replace(/[^0-9.-]/g, ""));
          const isRef = pf.type === "refund" || pf.status === "refunded" || amt < 0;
          if (isRef && amt < 0) {
            const orig = getOriginalChargeId(p, allP, dbChargeRecords);
            if (String(orig) === String(refundDetectedChargeId).trim()) {
              alreadyRef += Math.abs(amt);
            }
          }
        });
        refundableAmt = Math.max(0, parseFloat((damt - alreadyRef).toFixed(2)));
      }
    }

    const amountVal = parseFloat(refundAmountInput);

    if (!refundAmountInput || String(refundAmountInput).trim() === "" || isNaN(amountVal) || amountVal <= 0) {
      setRefundErrorToast("Please enter amount");
      return;
    }

    if (refundableAmt <= 0 || (matched && initialAmt <= 0)) {
      setRefundErrorToast(refundDetectedChargeId ? `Charge amount is 0 for Charge ID #${refundDetectedChargeId}. Refund cannot be processed.` : "Charge amount is 0. Refund cannot be processed.");
      return;
    }

    if (!refundChargeId || !refundChargeId.trim()) {
      setRefundErrorToast("Please select or enter a Transaction Type.");
      return;
    }

    // --- Net balance validation ---
    const netAvailableBalance = (() => {
      const payments = deduplicatePayments(hostawayPayments);
      const totalPaid = payments.reduce((sum, record) => {
        const f = record.fields || record.raw || record || {};
        const rawAmt = Number(String(f.Amount || f.amount || "0").replace(/[^0-9.-]/g, ""));
        return sum + (isNaN(rawAmt) || rawAmt <= 0 ? 0 : rawAmt);
      }, 0);
      const totalRefunded = payments.reduce((sum, record) => {
        const f = record.fields || record.raw || record || {};
        const rawAmt = Number(String(f.Amount || f.amount || "0").replace(/[^0-9.-]/g, ""));
        return sum + (isNaN(rawAmt) || rawAmt >= 0 ? 0 : Math.abs(rawAmt));
      }, 0);
      return Math.max(0, parseFloat((totalPaid - totalRefunded).toFixed(2)));
    })();

    if (amountVal > netAvailableBalance) {
      setRefundErrorToast("No do refund for this");
      return;
    }

    if (refundableAmt > 0 && amountVal > refundableAmt) {
      const isDollar = (currency || "").toUpperCase() === "USD";
      const formattedRefundable = isDollar ? `$${refundableAmt.toFixed(2)}` : `Rs. ${Math.round(refundableAmt).toLocaleString()}`;
      setRefundErrorToast(
        refundDetectedChargeId
          ? `Entered refund amount exceeds available balance for Charge #${refundDetectedChargeId} (${formattedRefundable}).`
          : `Entered refund amount exceeds available balance (${formattedRefundable}).`
      );
      return;
    }
    // --- End net balance validation ---

    if (!refundReason || !refundReason.trim()) {
      setRefundErrorToast("Please select a Reason.");
      return;
    }

    if (!refundDescription || !refundDescription.trim()) {
      setRefundErrorToast("Please enter Description Notes.");
      return;
    }

    setRefundErrorToast("");
    setOpenRefundConfirmModal(true);
  };

  const handleExecuteRefund = async () => {
    const amountVal = parseFloat(refundAmountInput) || 0;
    if (!refundAmountInput || String(refundAmountInput).trim() === "" || amountVal <= 0) {
      setRefundErrorToast("Please enter amount");
      setOpenRefundConfirmModal(false);
      return;
    }
    const finalTitle = refundChargeId || (refundTitle && refundTitle.trim() ? refundTitle.trim() : "MZN-BNR01");
    const activeCurrency = currency || detectCurrencyFromReservation(selectedResId) || "PKR";
    const rate = reservationDetails?.pkrRate || livePkrRate || 0;

    let totalUSD = amountVal;
    let totalPKR = rate > 0 ? Math.round(amountVal * rate) : 0;

    if (activeCurrency === "PKR") {
      totalPKR = Math.round(amountVal);
      totalUSD = rate > 0 ? parseFloat((amountVal / rate).toFixed(2)) : 0;
    }

    const combinedDesc = [
      refundReason ? `Reason: ${refundReason}` : "",
      refundDescription ? refundDescription.trim() : ""
    ].filter(Boolean).join(" | ") || finalTitle;

    const targetResId = reservationDetails?.id || selectedResId || "";
    const matchedFdo = (fdoRecords || []).find(
      (f) => String(f.fields?.["Reservation ID "] || f.fields?.["Reservation ID"] || f.id || "") === String(targetResId)
    );
    const guestNameStr =
      reservationDetails?.guestName ||
      (reservationDetails?.guestFirstName
        ? `${reservationDetails.guestFirstName} ${reservationDetails.guestLastName || ""}`.trim()
        : "") ||
      matchedFdo?.fields?.["Guest Name"] ||
      matchedFdo?.fields?.["Guest Name "] ||
      "Guest";
    const apartmentNameStr =
      reservationDetails?.listingName ||
      reservationDetails?.listing?.name ||
      reservationDetails?.room ||
      matchedFdo?.fields?.["Listing Name"] ||
      matchedFdo?.fields?.["Listing"] ||
      matchedFdo?.fields?.["Apartment Name"] ||
      matchedFdo?.fields?.["Apartment Name "] ||
      matchedFdo?.fields?.["Room"] ||
      "-";

    const finalAmount = activeCurrency === "PKR" ? totalPKR : totalUSD;
    const nowIso = new Date().toISOString();
    const dateStr = nowIso.replace("T", " ").substring(0, 19);

    const newDbRecord = {
      id: `charge-${Date.now()}`,
      reservationId: Number(targetResId) || targetResId || 0,
      chargeId: refundDetectedChargeId || Date.now(),
      guestName: guestNameStr || "Guest",
      apartmentName: apartmentNameStr || "-",
      title: finalTitle,
      description: combinedDesc,
      amount: -Math.abs(Number(finalAmount)),
      amountUsd: -Math.abs(Number(totalUSD)),
      amountPkr: -Math.abs(Number(totalPKR)),
      dateTime: nowIso,
      currency: activeCurrency,
      status: "refunded",
      type: "refund",
      isRefund: true,
      addedBy: user?.name || user?.username || "You",
      rawFields: {},
    };

    const payload = {
      reservationId: targetResId,
      guestName: guestNameStr,
      apartmentName: apartmentNameStr,
      title: finalTitle,
      name: refundChargeId,
      chargeId: refundDetectedChargeId || Date.now(),
      originalChargeId: refundDetectedChargeId || undefined,
      type: "refund",
      isRefund: true,
      reason: refundReason || "Other",
      description: refundDescription || combinedDesc,
      includeInPriceBreakdown: includeInPriceBreakdown ? 1 : 0,
      amount: -Math.abs(finalAmount),
      currency: activeCurrency,
      amountUsd: -Math.abs(totalUSD),
      amountPkr: -Math.abs(totalPKR),
      paymentMethod: "bank_transfer",
      status: "refunded",
      scheduledDate: dateStr,
      chargeDate: dateStr,
      addedBy: user?.name || user?.username || "",
    };

    // 1. Immediately CLOSE all dialogs so user is never blocked or stuck
    setOpenRefundConfirmModal(false);
    setOpenRefundModal(false);
    setOpenChargeModal(false);
    setRefundErrorToast("");

    // 2. Trigger asynchronous background refund via global context (persists across all pages)
    triggerRefund(payload, newDbRecord, {
      onSuccess: (record, responseData) => {
        setDbChargeRecords((prev) => {
          const updated = [record, ...(Array.isArray(prev) ? prev : [])];
          try { sessionStorage.setItem("cached_db_charge_records", JSON.stringify(updated)); } catch (e) { }
          return updated;
        });
        fetchDbChargeRecords();

        const rawRefId = responseData?.newRefundId || responseData?.refund?.sixDigitRefundId || responseData?.refund?.refundInfo?.id || responseData?.refund?.id;
        const refStr = rawRefId ? String(rawRefId).trim() : "";
        const isSixDigit = /^\d{6}$/.test(refStr);
        const isMoreThanSix = refStr.length > 6;

        if (isSixDigit) {
          setSnackbar({
            open: true,
            color: "success",
            icon: "check",
            title: "Refund Successful",
            content: `Refund for Reservation #${targetResId} | Refund ID: #${refStr} verified in Hostaway!`,
          });
        } else {
          // Hostaway 6-digit refund verification failed -> only show the exact error line
          setSnackbar({
            open: true,
            color: "error",
            icon: "warning",
            title: "Error",
            content: `Hostaway refund verification failed for Reservation #${targetResId}`,
          });
          if (isMoreThanSix) {
            handleReverifyHostawayRefund(targetResId);
          }
        }
      },
      onError: (err) => {
        let msg = err?.message || "Failed to process refund in Hostaway.";
        if (msg.toLowerCase().includes("no charge") || msg.toLowerCase().includes("no charges")) {
          msg = "Please try again refund security deposit";
        } else if (
          msg.toLowerCase().includes("verification failed") ||
          msg.toLowerCase().includes("hostaway refund verification") ||
          msg.toLowerCase().includes("provider refund id")
        ) {
          msg = `Hostaway refund verification failed for Reservation #${targetResId}`;
        }
        setSnackbar({
          open: true,
          color: "error",
          icon: "warning",
          title: "Error",
          content: msg,
        });
      },
    });
  };

  const handleReverifyHostawayRefund = async (resId) => {
    const targetId = String(resId || reservationDetails?.id || selectedResId || "").replace(/[^0-9]/g, "");
    if (!targetId) return;

    try {
      const resp = await fetch(`${API_BASE_URL || ""}/api/charges/verify-refund/${targetId}`);
      const data = await resp.json();
      if (data.success && data.refundIdMap) {
        setHostawayRefundIdMap((prev) => ({ ...prev, ...data.refundIdMap }));
        if (data.refundToChargeIdMap) {
          setHostawayRefundToChargeMap((prev) => ({ ...prev, ...data.refundToChargeIdMap }));
        }
        try {
          const finResp = await fetch(`${API_BASE_URL || ""}/api/charges/finance-fields/${targetId}`);
          const finData = await finResp.json();
          if (finData.success && Array.isArray(finData.financeFields)) {
            setHostawayFinanceFields(finData.financeFields);
          }
          if (finData.success && Array.isArray(finData.payments)) {
            setHostawayPayments((prev) => deduplicatePayments([...prev, ...finData.payments]));
          }
          if (finData.success && finData.refundToChargeIdMap) {
            setHostawayRefundToChargeMap((prev) => ({ ...prev, ...finData.refundToChargeIdMap }));
          }
        } catch (e) {
          // ignore
        }
        if (data.hasErrors) {
          setSnackbar({
            open: true,
            color: "warning",
            icon: "warning",
            title: "Hostaway Verification Notice",
            content: data.message || "Some refunds still pending 6-digit provider ID from Hostaway.",
          });
        } else {
          setSnackbar({
            open: true,
            color: "success",
            icon: "check",
            title: "Refunds Verified",
            content: "6-digit refund IDs verified successfully from Hostaway!",
          });
        }
      }
    } catch (err) {
      console.warn("Re-verify error:", err.message);
    }
  };

  const handleDropdownSelect = (e) => {
    const val = e.target.value;
    setSelectedFieldId(val);
    setPriceInput("");
    const item = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === val);
    if (
      item &&
      (item.name === "Security Deposit" ||
        item.name === "Early Checkin Charges per hour" ||
        item.name === "Late checkout fee per hour" ||
        item.name === "Damage Fee" ||
        item.name === "Mid Stay Cleaning" ||
        item.category === "Additional cleaning fee" ||
        item.category === "Other fees")
    ) {
      setQtyInput("1");
    }
  };

  const handleSaveUpsellModal = () => {
    if (!selectedFieldId) {
      setSnackbar({
        open: true,
        color: "error",
        title: "Validation Error",
        content: "Please select an upsell field.",
      });
      return;
    }

    const selectedField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === selectedFieldId);
    if (!selectedField) return;

    const unitPriceVal = parseFloat(priceInput);
    if (isNaN(unitPriceVal) || unitPriceVal <= 0) {
      setSnackbar({
        open: true,
        color: "error",
        title: "Validation Error",
        content: "Please enter a valid price per unit.",
      });
      return;
    }

    setOpenUpsellConfirmModal(true);
  };

  const handleExecuteUpsell = async () => {
    const activeCurrency = upsellCurrency || detectCurrencyFromReservation(selectedResId) || "PKR";
    const selectedField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === selectedFieldId);
    if (!selectedField) return;

    const unitPriceVal = parseFloat(priceInput);
    if (isNaN(unitPriceVal) || unitPriceVal <= 0) return;

    setIsSavingUpsell(true);
    try {
      const parsedQty = parseInt(qtyInput) || 1;
      const rate = reservationDetails?.pkrRate || livePkrRate || 0;
      const totalAmountVal = parseFloat((unitPriceVal * parsedQty).toFixed(2));
      let totalUSD = totalAmountVal;
      let totalPKR = rate > 0 ? Math.round(totalAmountVal * rate) : 0;

      if (activeCurrency === "PKR") {
        totalPKR = Math.round(totalAmountVal);
        totalUSD = rate > 0 ? parseFloat((totalAmountVal / rate).toFixed(2)) : 0;
      }

      const currentUserName = user?.name || user?.username || "";

      const targetResIdStr = String(reservationDetails?.id || selectedResId || "").replace(/[^0-9]/g, "");
      const matchedFdo = (fdoRecords || []).find((r) => {
        const f = r.fields || r || {};
        const fResId = String(
          f["Reservation ID"] ||
          f["Reservation ID "] ||
          f["Hostaway ID"] ||
          f["Reservation"] ||
          f["Res ID"] ||
          f["reservationId"] ||
          f["Reservation Id"] ||
          r.id ||
          ""
        ).replace(/[^0-9]/g, "");
        return fResId === targetResIdStr;
      });
      const apartmentNameStr = String(
        reservationDetails?.listingName ||
        reservationDetails?.room ||
        matchedFdo?.fields?.["Listing Name"] ||
        matchedFdo?.fields?.["Listing"] ||
        matchedFdo?.fields?.["Apartment Name"] ||
        matchedFdo?.fields?.["Room"] ||
        ""
      ).trim();

      // Post to Hostaway API & Teable Table tblVlCzoQlP35ui0MZH
      const endpoint = `${API_BASE_URL || ""}/api/upsells/post-hostaway`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reservationId: reservationDetails?.id || selectedResId || "",
          apartmentName: apartmentNameStr,
          fieldName: selectedField.name,
          category: selectedField.category,
          amount: activeCurrency === "PKR" ? totalPKR : totalUSD,
          unitPrice: unitPriceVal,
          qty: parsedQty,
          currency: activeCurrency,
          amountUsd: totalUSD,
          amountPkr: totalPKR,
          addedBy: currentUserName,
        }),
      });

      const responseData = await response.json();

      if (!response.ok || !responseData.success) {
        const errorMsg =
          responseData.error ||
          responseData.details?.message ||
          (typeof responseData.details === "string" ? responseData.details : null) ||
          responseData.message ||
          "Failed to save upsell to Hostaway.";
        setSnackbar({
          open: true,
          color: "error",
          title: "Hostaway Error",
          content: String(errorMsg),
        });
        setOpenUpsellConfirmModal(false);
        return;
      }

      // Success: Add to local state, refresh database records, and close modal (no snackbar on success)
      const nowIso = new Date().toISOString();
      const newCharge = {
        id: `charge-${Date.now()}`,
        fieldId: selectedField.id,
        fieldName: selectedField.name,
        category: selectedField.category,
        unitPrice: unitPriceVal,
        qty: parsedQty,
        totalUSD,
        totalPKR,
        addedDate: new Date().toLocaleDateString("en-CA"),
        hostawaySynced: true,
      };

      const newDbUpsellRecord = {
        id: `upsell-${Date.now()}`,
        reservationId: Number(targetResIdStr) || targetResIdStr || 0,
        guestName: reservationDetails?.guestName || matchedFdo?.fields?.["Guest Name"] || "Guest",
        apartmentName: apartmentNameStr || "-",
        category: selectedField.category,
        upsellName: selectedField.name,
        quantity: parsedQty,
        pricePerUnit: unitPriceVal,
        totalPrice: activeCurrency === "PKR" ? totalPKR : totalUSD,
        price: activeCurrency === "PKR" ? totalPKR : totalUSD,
        amount: activeCurrency === "PKR" ? totalPKR : totalUSD,
        dateTime: nowIso,
        currency: activeCurrency,
        addedBy: currentUserName || "You",
        fields: {
          'Reservation ID ': Number(targetResIdStr) || 0,
          'Guest Name': reservationDetails?.guestName || matchedFdo?.fields?.["Guest Name"] || "Guest",
          'Apartment Name': apartmentNameStr || "-",
          'Upsells Category': selectedField.category,
          'Upsells Name': selectedField.name,
          'Quantity': parsedQty,
          'Price Per Unit ': unitPriceVal,
          'Total Price': activeCurrency === "PKR" ? totalPKR : totalUSD,
          'Date and Time': nowIso,
          'Currency': activeCurrency,
          'Added By': currentUserName || "You"
        }
      };

      setAppliedUpsells((prev) => [newCharge, ...prev]);
      setDbUpsellRecords((prev) => {
        const updated = [newDbUpsellRecord, ...(Array.isArray(prev) ? prev : [])];
        try { sessionStorage.setItem("cached_db_upsell_records", JSON.stringify(updated)); } catch (e) { }
        return updated;
      });

      await fetchDbUpsellRecords();
      setOpenUpsellConfirmModal(false);
      setOpenModal(false);
    } catch (err) {
      console.warn("Hostaway API Sync Background Notice:", err.message);
      setSnackbar({
        open: true,
        color: "error",
        title: "Hostaway Error",
        content: String(err.message || "An error occurred while saving the upsell."),
      });
      setOpenUpsellConfirmModal(false);
    } finally {
      setIsSavingUpsell(false);
    }
  };

  const [activeTab, setActiveTab] = useState(defaultTab || 0);

  useEffect(() => {
    if (typeof defaultTab === "number") {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  const handleRemoveCharge = (id, fieldName) => {
    setAppliedUpsells(appliedUpsells.filter((c) => c.id !== id));
    setSnackbar({
      open: true,
      color: "info",
      title: "Charge Removed",
      content: `"${fieldName}" removed from Res ID-${reservationDetails?.id || ""}.`,
    });
  };

  const totalUpsellsUSD = appliedUpsells.reduce((sum, c) => sum + c.totalUSD, 0);
  const totalUpsellsPKR = Math.round(totalUpsellsUSD * (reservationDetails?.pkrRate || 0));
  const updatedReservationTotalUSD = (parseFloat(reservationDetails?.totalPrice || 0) + totalUpsellsUSD).toFixed(2);

  const mainContent = (
    <MDBox pt={user?.role === "user" || isEmbedded ? 1 : 3} pb={3} px={user?.role === "user" || isEmbedded ? 1 : 0}>
      {/* ─── 3-Page Sub-Navigation Bar (Hidden in User Layout) ─── */}
      {!(user?.role === "user" || isEmbedded) && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: { xs: 2.5, sm: 3.5 }, width: "100%" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              padding: "5px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 6px 0 rgba(0, 0, 0, 0.04)",
              overflow: "visible",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, val) => setActiveTab(val)}
              variant="standard"
              sx={{
                minHeight: "42px",
                "& .MuiTabs-indicator": { display: "none" },
                "& .MuiTabs-flexContainer": {
                  alignItems: "center",
                  gap: "6px",
                  display: "flex",
                  flexDirection: "row",
                },
                "& .MuiTab-root": {
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  minHeight: "42px",
                  height: "42px",
                  padding: { xs: "0 14px", sm: "0 22px" },
                  borderRadius: "10px",
                  transition: "all 0.2s ease",
                  color: "#64748b",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  display: "inline-flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  boxSizing: "border-box",
                  "&:hover": {
                    backgroundColor: "#f8fafc",
                    color: "#0f172a",
                    borderColor: "#cbd5e1",
                  },
                },
                "& .Mui-selected": {
                  color: "#ffffff !important",
                  backgroundColor: "#0284c7 !important",
                  borderColor: "#0284c7 !important",
                  boxShadow: "0 2px 8px rgba(2, 132, 199, 0.25)",
                },
              }}
            >
              <Tab icon={<Icon sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: "1.1rem !important" }}>person_search</Icon>} iconPosition="start" label="Add Upsells / Charges" />
              <Tab icon={<Icon sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: "1.1rem !important" }}>local_offer</Icon>} iconPosition="start" label="Upsell List" />
              <Tab icon={<Icon sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: "1.1rem !important" }}>account_balance_wallet</Icon>} iconPosition="start" label="Charges List" />
            </Tabs>
          </Box>
        </Box>
      )}

      {/* PAGE 1: Hostaway Reservation */}
      {activeTab === 0 && (
        <Box>
          {/* Hostaway Reservations Card */}
          <Card sx={{ mb: 3.5, overflow: "hidden", borderRadius: "16px", boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", backgroundColor: "#ffffff" }}>
            <MDBox pt={2.5} px={3} pb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ borderBottom: "1px solid #f1f5f9", background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)" }}>
              <MDBox display="flex" alignItems="center" gap={1.5}>
                <MDBox
                  width="2.5rem"
                  height="2.5rem"
                  borderRadius="10px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  color="white"
                  sx={{ background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)", boxShadow: "0 4px 10px rgba(2, 132, 199, 0.2)" }}
                >
                  <Icon sx={{ fontSize: "1.25rem !important" }}>view_kanban</Icon>
                </MDBox>
                <MDBox display="flex" flexDirection="column">
                  <MDTypography variant="h5" fontWeight="bold" color="dark" sx={{ fontFamily: "'Inter', sans-serif", fontSize: "1.15rem", letterSpacing: "-0.2px", color: "#0f172a" }}>
                    Hostaway Reservations
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox width={{ xs: "100%", sm: "300px" }}>
                <MDInput
                  placeholder="Search ID, Guest, or Room..."
                  value={fdoSearchQuery}
                  onChange={(e) => setFdoSearchQuery(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "20px",
                      backgroundColor: "#ffffff",
                      fontSize: "0.88rem",
                      fontFamily: "'Inter', sans-serif",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": { borderColor: "#0284c7" },
                      "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(2, 132, 199, 0.15)" },
                    },
                  }}
                />
              </MDBox>
            </MDBox>

            {/* KANBAN BOARD */}
            <MDBox
              p={{ xs: 1, sm: 2 }}
              sx={{
                overflowX: "auto",
                width: "100%",
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f5f9",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: "4px",
                  "&:hover": { backgroundColor: "#94a3b8" },
                },
              }}
            >
              {loadingFdo ? (
                <MDBox py={8} textAlign="center" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                  <CircularProgress color="info" size={38} sx={{ mb: 2 }} />
                  <MDTypography variant="h6" color="text" fontWeight="medium" sx={{ fontFamily: "'Inter', sans-serif" }}>
                    Loading reservations...
                  </MDTypography>
                </MDBox>
              ) : (
                <Box
                  display="flex"
                  flexDirection="row"
                  flexWrap="nowrap"
                  gap={2}
                  pb={1}
                  sx={{
                    minWidth: "100%",
                    width: "max-content",
                  }}
                >
                  {(() => {
                    const allColumns = [
                      { title: "Upcoming Stay", catIdx: 7, count: countUpcomingStay, accentColor: "#2563eb", badgeBg: "#eff6ff", badgeBorder: "#bfdbfe" },
                      { title: "Early Checked In", catIdx: 0, count: countEarlyCheckIn, accentColor: "#059669", badgeBg: "#ecfdf5", badgeBorder: "#a7f3d0" },
                      { title: "Checked In", catIdx: 1, count: countCheckIn, accentColor: "#0284c7", badgeBg: "#f0f9ff", badgeBorder: "#bae6fd" },
                      { title: "Staying Guest", catIdx: 2, count: countStaying, accentColor: "#d97706", badgeBg: "#fffbeb", badgeBorder: "#fde68a" },
                      { title: "Upcoming Checkout", catIdx: 3, count: countUpcomingCheckout, accentColor: "#db2777", badgeBg: "#fdf2f8", badgeBorder: "#fbcfe8" },
                      { title: "Early Checked Out", catIdx: 4, count: countEarlyCheckOut, accentColor: "#64748b", badgeBg: "#f1f5f9", badgeBorder: "#cbd5e1" },
                      { title: "Checked Out", catIdx: 5, count: countCheckOut, accentColor: "#475569", badgeBg: "#f8fafc", badgeBorder: "#cbd5e1" },
                      { title: "Same Day Checked Out", catIdx: 6, count: countSameDayCheckOut, accentColor: "#dc2626", badgeBg: "#fef2f2", badgeBorder: "#fecaca" },
                      { title: "No Show", catIdx: 8, count: countNoShow, accentColor: "#ea580c", badgeBg: "#fff7ed", badgeBorder: "#fed7aa" },
                    ];

                    // Hide any stack where reservation count is 0
                    const activeCols = allColumns.filter((col) => col.count > 0);

                    if (activeCols.length === 0) {
                      return (
                        <MDBox py={6} textAlign="center" width="100%">
                          <MDTypography variant="h6" color="text" fontWeight="medium" sx={{ fontFamily: "'Inter', sans-serif", color: "#64748b" }}>
                            No active reservations found in any stack.
                          </MDTypography>
                        </MDBox>
                      );
                    }

                    return activeCols.map((col) => {
                      const colRecords = allCombinedFdoRecords.filter((rec) => {
                        const fields = rec.fields || {};
                        if (!isRecordInCategory(rec, col.catIdx)) return false;

                        if (!fdoSearchQuery.trim()) return true;
                        const q = fdoSearchQuery.toLowerCase().trim();
                        const resId = String(fields["Reservation ID"] || rec.id || "").toLowerCase();
                        const guest = String(fields["Guest Name"] || "").toLowerCase();
                        const room = String(fields["Listing Name"] || "").toLowerCase();
                        return resId.includes(q) || guest.includes(q) || room.includes(q);
                      });

                      return (
                        <HostawayKanbanColumn
                          key={col.title}
                          col={col}
                          colRecords={colRecords}
                          isViewOnly={isViewOnly}
                          onOpenUpsell={(fields, resIdStr, guest, arr, dep) => {
                            if (isViewOnly) return;
                            const cardCurr = detectCurrencyFromFields(fields, resIdStr);
                            const resObj = {
                              id: resIdStr,
                              guestName: guest,
                              arrivalDate: arr,
                              departureDate: dep,
                              currency: cardCurr,
                              pkrRate: reservationDetails?.pkrRate || livePkrRate || 0,
                              totalPrice: 0,
                            };
                            setReservationDetails(resObj);
                            setSelectedResId(resIdStr);
                            setSearchInput(resIdStr);
                            setCurrency(cardCurr);
                            setUpsellCurrency(cardCurr);
                            handleOpenAddModal(resIdStr);
                          }}
                          onOpenCharge={(fields, resIdStr, guest, arr, dep, room) => {
                            if (isViewOnly) return;
                            const cardCurr = detectCurrencyFromFields(fields, resIdStr);
                            const aptName =
                              room ||
                              fields?.["Listing Name"] ||
                              fields?.["Apartment Name"] ||
                              fields?.["Listing"] ||
                              fields?.listingName ||
                              fields?.apartmentName ||
                              "";
                            const resTotal = Number(fields?.["Total Price"] || fields?.["Total Amount"] || fields?.["Amount"] || fields?.totalPrice || fields?.total_price || fields?.total || 0);
                            const resPaid = Number(fields?.["Paid Amount"] || fields?.["Paid"] || fields?.paidAmount || fields?.paid_amount || 0);
                            const resPending = Number(fields?.["Pending Amount"] || fields?.["Pending"] || fields?.pendingAmount || fields?.pending_amount || (resTotal > 0 ? Math.max(0, resTotal - resPaid) : 0));

                            const resObj = {
                              id: resIdStr,
                              guestName: guest,
                              listingName: aptName,
                              apartmentName: aptName,
                              arrivalDate: arr,
                              departureDate: dep,
                              currency: cardCurr,
                              pkrRate: reservationDetails?.pkrRate || livePkrRate || 0,
                              totalPrice: resTotal,
                              paidAmount: resPaid,
                              pendingAmount: resPending,
                            };
                            setReservationDetails(resObj);
                            setSelectedResId(resIdStr);
                            setSearchInput(resIdStr);
                            setCurrency(cardCurr);
                            setUpsellCurrency(cardCurr);
                            handleOpenChargeModal(resIdStr, resObj);
                          }}
                          onOpenRefund={(fields, resIdStr, guest, arr, dep, room) => {
                            if (isViewOnly) return;
                            const cardCurr = detectCurrencyFromFields(fields, resIdStr);
                            const aptName =
                              room ||
                              fields?.["Listing Name"] ||
                              fields?.["Apartment Name"] ||
                              fields?.["Listing"] ||
                              fields?.listingName ||
                              fields?.apartmentName ||
                              "";
                            const resTotal = Number(fields?.["Total Price"] || fields?.["Total Amount"] || fields?.["Amount"] || fields?.totalPrice || fields?.total_price || fields?.total || 0);
                            const resPaid = Number(fields?.["Paid Amount"] || fields?.["Paid"] || fields?.paidAmount || fields?.paid_amount || 0);
                            const resPending = Number(fields?.["Pending Amount"] || fields?.["Pending"] || fields?.pendingAmount || fields?.pending_amount || (resTotal > 0 ? Math.max(0, resTotal - resPaid) : 0));

                            const resObj = {
                              id: resIdStr,
                              guestName: guest,
                              listingName: aptName,
                              apartmentName: aptName,
                              arrivalDate: arr,
                              departureDate: dep,
                              currency: cardCurr,
                              pkrRate: reservationDetails?.pkrRate || livePkrRate || 0,
                              totalPrice: resTotal,
                              paidAmount: resPaid,
                              pendingAmount: resPending,
                            };
                            setReservationDetails(resObj);
                            setSelectedResId(resIdStr);
                            setSearchInput(resIdStr);
                            setCurrency(cardCurr);
                            setUpsellCurrency(cardCurr);
                            handleOpenRefundModal(resIdStr, resObj);
                          }}
                          getActualCheckInTime={getActualCheckInTime}
                          getActualCheckOutTime={getActualCheckOutTime}
                          formatDisplayDate={formatDisplayDate}
                        />
                      );
                    });
                  })()}
                </Box>
              )}
            </MDBox>

          </Card>
        </Box>
      )}

      {/* PAGE 2: Upsell Overview UI (Exact Mockup Design) */}
      {activeTab === 1 && (
        <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
          {/* Top Controls Bar for Admin, View Only, Custom, Full Access */}
          {showDateAtTop && (
            <MDBox display="flex" justifyContent={{ xs: "stretch", sm: "flex-end" }} alignItems="center" mb={3} flexWrap="wrap" gap={1.5} sx={{ width: "100%" }}>
              <MDBox display="flex" alignItems="center" gap={1.25} flexWrap="wrap" sx={{ width: { xs: "100%", sm: "auto" } }}>
                {/* Start Date Input Pill */}
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent={{ xs: "space-between", sm: "flex-start" }}
                  gap={1}
                  px={1.6}
                  py={0.65}
                  sx={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                    width: { xs: "100%", sm: "auto" },
                    flex: { xs: "1 1 100%", sm: "none" },
                    cursor: "default",
                  }}
                >
                  <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                    Start Date:
                  </MDTypography>
                  <input
                    type="date"
                    value={upsellStartDate}
                    onChange={(e) => {
                      setUpsellStartDate(e.target.value);
                      if (upsellEndDate && e.target.value > upsellEndDate) {
                        setUpsellEndDate(e.target.value);
                      }
                    }}
                    style={{
                      border: "none",
                      outline: "none",
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                      fontSize: "0.85rem",
                      color: "#0f172a",
                      fontWeight: 500,
                      backgroundColor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </MDBox>

                {/* End Date Input Pill */}
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent={{ xs: "space-between", sm: "flex-start" }}
                  gap={1}
                  px={1.6}
                  py={0.65}
                  sx={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                    width: { xs: "100%", sm: "auto" },
                    flex: { xs: "1 1 100%", sm: "none" },
                    cursor: "default",
                  }}
                >
                  <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                    End Date:
                  </MDTypography>
                  <input
                    type="date"
                    value={upsellEndDate}
                    onChange={(e) => {
                      setUpsellEndDate(e.target.value);
                      if (upsellStartDate && e.target.value < upsellStartDate) {
                        setUpsellStartDate(e.target.value);
                      }
                    }}
                    style={{
                      border: "none",
                      outline: "none",
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                      fontSize: "0.85rem",
                      color: "#0f172a",
                      fontWeight: 500,
                      backgroundColor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </MDBox>

                {/* Download Button (Opens Format Choice Modal) */}
                {canDownload && (
                  <MDButton
                    variant="outlined"
                    disabled={isViewOnly}
                    onClick={isViewOnly ? undefined : () => {
                      setTargetDownloadReservation(null);
                      setDownloadModalOpen(true);
                    }}
                    sx={{
                      border: isViewOnly ? "1px solid #e2e8f0 !important" : "1px solid #cbd5e1 !important",
                      color: isViewOnly ? "#94a3b8 !important" : "#334155 !important",
                      borderRadius: "8px",
                      px: 1.8,
                      py: 0.65,
                      fontSize: "0.82rem",
                      fontWeight: 500,
                      textTransform: "none",
                      backgroundColor: isViewOnly ? "#f8fafc" : "#ffffff",
                      cursor: isViewOnly ? "not-allowed" : "pointer",
                      flex: { xs: 1, sm: "none" },
                      "&:hover": {
                        backgroundColor: isViewOnly ? "#f8fafc" : "#f8fafc",
                        borderColor: isViewOnly ? "#e2e8f0 !important" : "#94a3b8 !important",
                      },
                      "&.Mui-disabled": {
                        color: "#94a3b8 !important",
                        borderColor: "#e2e8f0 !important",
                        backgroundColor: "#f8fafc !important",
                      },
                    }}
                  >
                    Download
                  </MDButton>
                )}
              </MDBox>
            </MDBox>
          )}

          {/* 1. Three KPI Summary Cards */}
          {(() => {
            const grouped = getGroupedReservations();
            const entries = getSelectedDateUpsellEntries();
            const totalRevPKR = entries.reduce((sum, r) => sum + Number(r.totalPrice || r.price || 0), 0);

            return (
              <Grid container spacing={2} mb={3.5}>
                {/* Card 1: Reservations with upsells */}
                <Grid item xs={12} sm={4} md={4}>
                  <Card sx={{ p: 2.5, borderRadius: "14px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem", fontWeight: 500 }}>
                      Reservations with upsells
                    </MDTypography>
                    <MDTypography variant="h3" sx={{ color: "#0f172a", fontWeight: 700, fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" }, my: 0.6 }}>
                      {grouped.length}
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.78rem" }}>
                      Unique reservations for selected date
                    </MDTypography>
                  </Card>
                </Grid>

                {/* Card 2: Upsell entries */}
                <Grid item xs={12} sm={4} md={4}>
                  <Card sx={{ p: 2.5, borderRadius: "14px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem", fontWeight: 500 }}>
                      Upsell entries
                    </MDTypography>
                    <MDTypography variant="h3" sx={{ color: "#0f172a", fontWeight: 700, fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" }, my: 0.6 }}>
                      {entries.length}
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.78rem" }}>
                      Upsell lines added
                    </MDTypography>
                  </Card>
                </Grid>

                {/* Card 3: Total upsell revenue */}
                <Grid item xs={12} sm={4} md={4}>
                  <Card sx={{ p: 2.5, borderRadius: "14px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem", fontWeight: 500 }}>
                      Total upsell revenue
                    </MDTypography>
                    <MDTypography variant="h3" sx={{ color: "#059669", fontWeight: 500, fontSize: { xs: "1.4rem", sm: "1.65rem", md: "2rem" }, my: 0.6, wordBreak: "break-word" }}>
                      {formatEntriesTotal(entries, livePkrRate)}
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.78rem" }}>
                      All selected-date upsells
                    </MDTypography>
                  </Card>
                </Grid>
              </Grid>
            );
          })()}

          {/* 2. Main Reservations Table Card */}
          <Card sx={{ mb: 3.5, overflow: "hidden", borderRadius: "16px", boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", backgroundColor: "#ffffff" }}>
            {/* Card Header with Title, Date Filters, Search Bar and Refresh Button */}
            <MDBox p={2.5} px={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ borderBottom: "1px solid #f1f5f9" }}>
              {/* Left Side: Title */}
              <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#0f172a", fontSize: "1.05rem", letterSpacing: "-0.2px" }}>
                Reservations
              </MDTypography>

              {/* Right Side: Start/End Date (Standard & Custom View Users), Search Bar, Refresh Button */}
              <MDBox display="flex" alignItems="center" gap={1.25} flexWrap="wrap" sx={{ width: { xs: "100%", sm: "auto" } }}>
                {/* Start Date & End Date Input Pills (Only for Standard Users & Custom View Users before Search Bar) */}
                {showDateBeforeSearchBar && (
                  <>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1}
                      px={1.4}
                      py={0.55}
                      sx={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #cbd5e1",
                        borderRadius: "8px",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                        cursor: "default",
                      }}
                    >
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                        Start Date:
                      </MDTypography>
                      <input
                        type="date"
                        value={upsellStartDate}
                        onChange={(e) => {
                          setUpsellStartDate(e.target.value);
                          if (upsellEndDate && e.target.value > upsellEndDate) {
                            setUpsellEndDate(e.target.value);
                          }
                        }}
                        style={{
                          border: "none",
                          outline: "none",
                          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                          fontSize: "0.84rem",
                          color: "#0f172a",
                          fontWeight: 500,
                          backgroundColor: "transparent",
                          cursor: "pointer",
                        }}
                      />
                    </MDBox>

                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1}
                      px={1.4}
                      py={0.55}
                      sx={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #cbd5e1",
                        borderRadius: "8px",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                        cursor: "default",
                      }}
                    >
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                        End Date:
                      </MDTypography>
                      <input
                        type="date"
                        value={upsellEndDate}
                        onChange={(e) => {
                          setUpsellEndDate(e.target.value);
                          if (upsellStartDate && e.target.value < upsellStartDate) {
                            setUpsellStartDate(e.target.value);
                          }
                        }}
                        style={{
                          border: "none",
                          outline: "none",
                          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                          fontSize: "0.84rem",
                          color: "#0f172a",
                          fontWeight: 500,
                          backgroundColor: "transparent",
                          cursor: "pointer",
                        }}
                      />
                    </MDBox>
                  </>
                )}

                {/* Search Bar */}
                <MDBox width={{ xs: "100%", sm: "240px", md: "260px" }}>
                  <MDInput
                    placeholder="Search reservation, guest or listing"
                    size="small"
                    fullWidth
                    value={upsellSearchQuery}
                    onChange={(e) => setUpsellSearchQuery(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#ffffff",
                        fontSize: "0.84rem",
                        borderColor: "#cbd5e1",
                      },
                    }}
                  />
                </MDBox>

                {/* Refresh Button */}
                <MDButton
                  variant="contained"
                  onClick={fetchDbUpsellRecords}
                  disabled={loadingDbUpsells}
                  sx={{
                    backgroundColor: "#0284c7 !important",
                    color: "#ffffff !important",
                    borderRadius: "8px",
                    px: 2.2,
                    py: 0.75,
                    fontSize: "0.84rem",
                    fontWeight: 500,
                    textTransform: "none",
                    boxShadow: "0 2px 6px rgba(2, 132, 199, 0.2)",
                    whiteSpace: "nowrap",
                    "&:hover": { backgroundColor: "#0369a1 !important" },
                  }}
                >
                  {loadingDbUpsells ? "Loading..." : "Refresh"}
                </MDButton>
              </MDBox>
            </MDBox>

            {/* Table Content */}
            {(() => {
              const grouped = getGroupedReservations();
              const entries = getSelectedDateUpsellEntries();
              const totalRevPKR = entries.reduce((sum, r) => sum + Number(r.totalPrice || r.price || 0), 0);

              if (loadingDbUpsells) {
                return (
                  <MDBox py={8} textAlign="center" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <CircularProgress color="info" size={38} sx={{ mb: 2 }} />
                    <MDTypography variant="button" color="text" fontWeight="medium">Fetching upsell records...</MDTypography>
                  </MDBox>
                );
              }

              if (grouped.length === 0) {
                return (
                  <MDBox py={7} textAlign="center">
                    <Icon color="disabled" sx={{ fontSize: "3.2rem", mb: 1 }}>inbox</Icon>
                    <MDTypography variant="h6" color="text" fontWeight="medium">No Reservations with Upsells Found</MDTypography>
                    <MDTypography variant="caption" color="secondary">Try selecting another date or clearing your search.</MDTypography>
                  </MDBox>
                );
              }

              return (
                <>
                  {/* ---- MOBILE CARD VIEW (xs/sm only) ---- */}
                  <MDBox sx={{ display: { xs: "block", md: "none" } }}>
                    {grouped.map((res) => {
                      const lastAddedTimeStr = formatPakistanDateTime(res.lastAddedTime);
                      return (
                        <MDBox
                          key={res.reservationId}
                          sx={{
                            borderBottom: "1px solid #f1f5f9",
                            p: 2,
                            "&:last-child": { borderBottom: "none" },
                          }}
                        >
                          {/* Top row: Res ID + Total */}
                          <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <MDTypography
                              component="a"
                              href={`https://dashboard.hostaway.com/reservations/${String(res.reservationId).replace(/[^0-9]/g, "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                backgroundColor: "#eff6ff", color: "#0284c7",
                                px: 1.4, py: 0.35, borderRadius: "6px",
                                fontSize: "0.84rem", fontWeight: 400,
                                border: "1px solid #e0f2fe", textDecoration: "none", display: "inline-block",
                              }}
                            >
                              #{res.reservationId}
                            </MDTypography>
                            <MDBox
                              px={1.4} py={0.4} borderRadius="6px" display="inline-block"
                              sx={{ backgroundColor: "#ecfdf5", color: "#059669", border: "1px solid #a7f3d0", fontWeight: 400, fontSize: "0.84rem" }}
                            >
                              {formatReservationTotal(res, livePkrRate)}
                            </MDBox>
                          </MDBox>
                          {/* Guest Name & Apartment Name */}
                          <MDTypography variant="body2" sx={{ color: "#0f172a", fontSize: "0.86rem", mb: 0.2 }}>
                            {res.guestName || "Guest"}
                          </MDTypography>
                          {res.listingName ? (
                            <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.78rem", mb: 0.5, display: "block" }}>
                              Apartment: <strong style={{ color: "#334155" }}>{res.listingName}</strong>
                            </MDTypography>
                          ) : null}
                          {/* Meta row */}
                          <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={1.2}>
                            <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.78rem" }}>
                              Qty: <strong style={{ color: "#0f172a" }}>{res.totalQuantity}</strong>
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.76rem" }}>
                              {lastAddedTimeStr}
                            </MDTypography>
                          </MDBox>
                          {/* Action Buttons */}
                          <MDBox display="flex" gap={1}>
                            <MDButton
                              variant="outlined" size="small"
                              onClick={() => handleOpenViewUpsellModal(res)}
                              sx={{ border: "1px solid #cbd5e1 !important", color: "#334155 !important", borderRadius: "8px", px: 1.8, fontSize: "0.76rem", fontWeight: 500, textTransform: "none", backgroundColor: "#ffffff", flex: 1 }}
                            >View</MDButton>
                            {canDownload && (
                              <MDButton
                                variant="outlined" size="small"
                                disabled={isViewOnly}
                                onClick={isViewOnly ? undefined : () => { setTargetDownloadReservation(res); setDownloadModalOpen(true); }}
                                sx={{
                                  border: isViewOnly ? "1px solid #e2e8f0 !important" : "1px solid #cbd5e1 !important",
                                  color: isViewOnly ? "#94a3b8 !important" : "#334155 !important",
                                  borderRadius: "8px", px: 1.8, fontSize: "0.76rem", fontWeight: 500,
                                  textTransform: "none",
                                  backgroundColor: isViewOnly ? "#f8fafc" : "#ffffff",
                                  cursor: isViewOnly ? "not-allowed" : "pointer",
                                  flex: 1,
                                  "&.Mui-disabled": {
                                    color: "#94a3b8 !important",
                                    borderColor: "#e2e8f0 !important",
                                    backgroundColor: "#f8fafc !important",
                                  },
                                }}
                              >Download</MDButton>
                            )}
                          </MDBox>
                        </MDBox>
                      );
                    })}
                    {/* Mobile Footer */}
                    <MDBox p={2} px={3} display="flex" justifyContent="space-between" alignItems="center" sx={{ borderTop: "1px solid #f1f5f9", backgroundColor: "#f8fafc" }}>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                        Reservations: <strong style={{ color: "#0f172a" }}>{grouped.length}</strong>
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                        Total: <strong style={{ color: "#0f172a" }}>{formatEntriesTotal(entries, livePkrRate)}</strong>
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  {/* ---- DESKTOP TABLE VIEW (md and above) ---- */}
                  <TableContainer sx={{ boxShadow: "none", overflowX: "auto", width: "100%", display: { xs: "none", md: "block" } }}>
                    <Table sx={{ width: "100%", minWidth: 950, borderCollapse: "separate", borderSpacing: "0" }}>
                      <TableHead sx={{ display: "table-header-group" }}>
                        <TableRow sx={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                          <TableCell align="center" style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>DATE &amp; TIME</TableCell>
                          <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>RESERVATION ID</TableCell>
                          <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>GUEST NAME</TableCell>
                          <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>APARTMENT NAME</TableCell>
                          <TableCell align="center" style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em" }}>QUANTITY</TableCell>
                          <TableCell align="center" style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em" }}>TOTAL</TableCell>
                          <TableCell align="center" style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em" }}>ACTIONS</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody sx={{ backgroundColor: "#ffffff" }}>
                        {grouped.map((res) => {
                          const lastAddedTimeStr = formatPakistanDateTime(res.lastAddedTime);
                          return (
                            <TableRow key={res.reservationId} sx={{ borderBottom: "1px solid #f1f5f9", transition: "background-color 0.15s ease", "&:hover": { backgroundColor: "#f8fafc" } }}>
                              <TableCell align="center" style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", color: "#0f172a", fontSize: "0.82rem", whiteSpace: "nowrap" }}>{lastAddedTimeStr}</TableCell>
                              <TableCell style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                                <MDTypography component="a" href={`https://dashboard.hostaway.com/reservations/${String(res.reservationId).replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" variant="button" sx={{ backgroundColor: "#eff6ff", color: "#0284c7", px: 1.4, py: 0.35, borderRadius: "6px", fontSize: "0.84rem", fontWeight: 400, border: "1px solid #e0f2fe", textDecoration: "none", display: "inline-block", "&:hover": { backgroundColor: "#dbeafe", color: "#0369a1" } }}>#{res.reservationId}</MDTypography>
                              </TableCell>
                              <TableCell style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                                <MDTypography variant="button" sx={{ color: "#0f172a", fontWeight: 400, fontSize: "0.86rem" }}>{res.guestName || "Guest"}</MDTypography>
                              </TableCell>
                              <TableCell style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                                <MDTypography variant="caption" sx={{ color: "#475569", fontWeight: 500, fontSize: "0.82rem" }}>
                                  {res.listingName || "-"}
                                </MDTypography>
                              </TableCell>
                              <TableCell align="center" style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", color: "#0f172a", fontSize: "0.85rem" }}>{res.totalQuantity}</TableCell>
                              <TableCell align="center" style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                                <MDBox px={1.4} py={0.4} borderRadius="6px" display="inline-block" sx={{ backgroundColor: "#ecfdf5", color: "#059669", border: "1px solid #a7f3d0", fontWeight: 400, fontSize: "0.84rem" }}>{formatReservationTotal(res, livePkrRate)}</MDBox>
                              </TableCell>
                              <TableCell align="center" style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                                <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
                                  <MDButton variant="outlined" size="small" onClick={() => handleOpenViewUpsellModal(res)} sx={{ border: "1px solid #cbd5e1 !important", color: "#334155 !important", borderRadius: "8px", px: 1.4, py: 0.35, fontSize: "0.76rem", fontWeight: 500, textTransform: "none", backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#f8fafc", borderColor: "#94a3b8 !important" } }}>View</MDButton>
                                  {canDownload && (
                                    <MDButton
                                      variant="outlined" size="small"
                                      disabled={isViewOnly}
                                      onClick={isViewOnly ? undefined : () => { setTargetDownloadReservation(res); setDownloadModalOpen(true); }}
                                      sx={{
                                        border: isViewOnly ? "1px solid #e2e8f0 !important" : "1px solid #cbd5e1 !important",
                                        color: isViewOnly ? "#94a3b8 !important" : "#334155 !important",
                                        borderRadius: "8px", px: 1.4, py: 0.35, fontSize: "0.76rem", fontWeight: 500,
                                        textTransform: "none",
                                        backgroundColor: isViewOnly ? "#f8fafc" : "#ffffff",
                                        cursor: isViewOnly ? "not-allowed" : "pointer",
                                        "&:hover": {
                                          backgroundColor: isViewOnly ? "#f8fafc" : "#f8fafc",
                                          borderColor: isViewOnly ? "#e2e8f0 !important" : "#94a3b8 !important",
                                        },
                                        "&.Mui-disabled": {
                                          color: "#94a3b8 !important",
                                          borderColor: "#e2e8f0 !important",
                                          backgroundColor: "#f8fafc !important",
                                        },
                                      }}
                                    >Download</MDButton>
                                  )}
                                </MDBox>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    {/* Table Footer Summary Bar */}
                    <MDBox p={2} px={3} display="flex" justifyContent="flex-end" alignItems="center" gap={3} sx={{ borderTop: "1px solid #f1f5f9", backgroundColor: "#f8fafc" }}>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem" }}>
                        Selected date: <strong style={{ color: "#0f172a", fontWeight: 500 }}>{upsellStartDate === upsellEndDate ? formatFooterDate(upsellStartDate) : `${formatFooterDate(upsellStartDate)} - ${formatFooterDate(upsellEndDate)}`}</strong>
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem" }}>
                        Reservations: <strong style={{ color: "#0f172a", fontWeight: 500 }}>{grouped.length}</strong>
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem" }}>
                        Total: <strong style={{ color: "#0f172a", fontWeight: 500 }}>{formatEntriesTotal(entries, livePkrRate)}</strong>
                      </MDTypography>
                    </MDBox>
                  </TableContainer>
                </>
              );
            })()}
          </Card>
          {/* 4. Single Reservation Upsells Modal Dialog (Opened by View Button) */}
          <Dialog
            open={viewUpsellModalOpen}
            onClose={() => setViewUpsellModalOpen(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "16px",
                m: { xs: 1, sm: 3 },
                boxShadow: "0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.05)",
                border: "1px solid #e2e8f0",
                overflow: "hidden",
              },
            }}
          >
            <DialogTitle component="div" sx={{ p: "18px 24px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDBox display="flex" alignItems="center" gap={1.2}>
                    <MDTypography variant="h6" sx={{ color: "#0f172a", fontWeight: 700, fontSize: "1.1rem" }}>
                      Reservation Details
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem", mt: 0.3, display: "flex", alignItems: "center", gap: 1, flexWrap: "nowrap", overflowX: "auto" }}>
                    <span>Reservation ID: <span style={{ color: "#0f172a", fontWeight: 400 }}>#{selectedReservationForView?.reservationId}</span></span>
                    <span style={{ color: "#cbd5e1" }}>|</span>
                    <span>Guest Name: <span style={{ color: "#0f172a", fontWeight: 400 }}>{selectedReservationForView?.guestName}</span></span>
                    {selectedReservationForView?.listingName ? (
                      <>
                        <span style={{ color: "#cbd5e1" }}>|</span>
                        <span>Apartment Name: <span style={{ color: "#0f172a", fontWeight: 400 }}>{selectedReservationForView?.listingName}</span></span>
                      </>
                    ) : null}
                  </MDTypography>
                </MDBox>
                <IconButton size="small" onClick={() => setViewUpsellModalOpen(false)} sx={{ color: "#64748b", "&:hover": { color: "#0f172a" } }}>
                  <Icon>close</Icon>
                </IconButton>
              </MDBox>
            </DialogTitle>

            <DialogContent sx={{ p: 2.5, overflowX: "auto" }}>
              {/* Upsell List Header */}
              <MDTypography variant="h6" fontWeight="bold" mb={1.5} sx={{ fontSize: "1rem", color: "#0f172a" }}>
                Upsell List
              </MDTypography>

              {(() => {
                const formatUpsellPriceWithConversion = (val, curr, itemRate) => {
                  const num = Number(val || 0);
                  if (isNaN(num)) return "-";
                  const isUsd = (curr || "").toUpperCase() === "USD" || (curr || "").includes("$");
                  if (isUsd) {
                    const rate = Number(itemRate || selectedReservationForView?.pkrRate || livePkrRate || 0);
                    if (rate > 0) {
                      const pkrVal = Math.round(num * rate);
                      return `$${num.toFixed(2)} USD = PKR ${pkrVal.toLocaleString()}`;
                    }
                    return `$${num.toFixed(2)}`;
                  }
                  return `Rs. ${Math.round(num).toLocaleString()}`;
                };

                return (
                  <>
                    {/* MOBILE CARD VIEW FOR DIALOG (xs/sm) */}
                    <MDBox sx={{ display: { xs: "block", md: "none" }, p: 2 }}>
                      {selectedReservationForView?.items?.map((item, idx) => {
                        const hideItemQty = isNoQtyItem(item);

                        return (
                          <MDBox key={item.id || idx} sx={{ borderBottom: "1px solid #f1f5f9", pb: 1.5, mb: 1.5, "&:last-child": { borderBottom: "none", mb: 0, pb: 0 } }}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                              <MDTypography variant="body2" sx={{ color: "#0f172a", fontWeight: 600, fontSize: "0.86rem" }}>
                                {idx + 1}. {item.upsellName || item.title || "-"}
                              </MDTypography>
                              <MDTypography variant="button" sx={{ color: "#0f172a", fontWeight: 500, fontSize: "0.84rem" }}>
                                {formatUpsellPriceWithConversion(item.price || item.totalPrice || 0, item.currency, item.pkrRate)}
                              </MDTypography>
                            </MDBox>
                            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                              <Chip label={item.category || "Service fee"} size="small" sx={{ fontWeight: 500, fontSize: "0.72rem", height: "20px", backgroundColor: "#f1f5f9", color: "#334155" }} />
                              {!hideItemQty && (
                                <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.76rem" }}>
                                  Qty: <strong style={{ color: "#0f172a" }}>{item.quantity ?? 1}</strong>
                                </MDTypography>
                              )}
                              {item.pricePerUnit && !hideItemQty ? (
                                <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.76rem" }}>
                                  Per Unit: {formatUpsellPriceWithConversion(item.pricePerUnit, item.currency, item.pkrRate)}
                                </MDTypography>
                              ) : null}
                            </MDBox>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                              <MDTypography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.74rem" }}>
                                {formatPakistanDateTime(item.dateTime)}
                              </MDTypography>
                              <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.74rem" }}>
                                By: {item.addedBy || "-"}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        );
                      })}
                    </MDBox>

                    {/* DESKTOP TABLE VIEW FOR DIALOG (md and above) */}
                    <TableContainer sx={{ boxShadow: "none", overflowX: "auto", display: { xs: "none", md: "block" } }}>
                      <Table sx={{ width: "100%", tableLayout: "auto", borderCollapse: "separate", borderSpacing: "0", "& .MuiTableCell-root": { display: "table-cell !important", verticalAlign: "middle" } }}>
                        <TableHead sx={{ display: "table-header-group" }}>
                          <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                            <TableCell style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 10px", borderBottom: "1px solid #e2e8f0" }}>Upsell Item</TableCell>
                            <TableCell style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 10px", borderBottom: "1px solid #e2e8f0" }}>Category</TableCell>
                            <TableCell align="center" style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 8px", borderBottom: "1px solid #e2e8f0", width: "65px" }}>Quantity</TableCell>
                            <TableCell align="center" style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 10px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>Total Price</TableCell>
                            <TableCell align="center" style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 10px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>Price Per Unit</TableCell>
                            <TableCell align="center" style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 10px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>Date &amp; Time</TableCell>
                            <TableCell align="center" style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 10px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>Added By</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody sx={{ backgroundColor: "#ffffff" }}>
                          {selectedReservationForView?.items?.map((item, idx) => {
                            const hideItemQty = isNoQtyItem(item);

                            return (
                              <TableRow key={item.id || idx} sx={{ "&:hover": { backgroundColor: "#f8fafc" }, borderBottom: "1px solid #f1f5f9" }}>
                                <TableCell style={{ padding: "10px 10px", color: "#0f172a", fontWeight: 600, fontSize: "0.84rem", borderBottom: "1px solid #f1f5f9" }}>{item.upsellName || item.title || "-"}</TableCell>
                                <TableCell style={{ padding: "10px 10px", borderBottom: "1px solid #f1f5f9" }}>
                                  <Chip label={item.category || "Service fee"} size="small" sx={{ fontWeight: 500, fontSize: "0.72rem", height: "22px", backgroundColor: "#f1f5f9", color: "#334155" }} />
                                </TableCell>
                                <TableCell align="center" style={{ padding: "10px 8px", color: "#0f172a", fontSize: "0.84rem", borderBottom: "1px solid #f1f5f9" }}>{hideItemQty ? "-" : (item.quantity ?? 1)}</TableCell>
                                <TableCell align="center" style={{ padding: "10px 10px", color: "#0f172a", fontWeight: 400, fontSize: "0.84rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>
                                  {formatUpsellPriceWithConversion(item.price || item.totalPrice || 0, item.currency, item.pkrRate)}
                                </TableCell>
                                <TableCell align="center" style={{ padding: "10px 10px", color: "#0f172a", fontSize: "0.82rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>
                                  {hideItemQty ? "-" : formatUpsellPriceWithConversion(item.pricePerUnit || 0, item.currency, item.pkrRate)}
                                </TableCell>
                                <TableCell align="center" style={{ padding: "10px 10px", color: "#64748b", fontSize: "0.8rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>
                                  {formatPakistanDateTime(item.dateTime)}
                                </TableCell>
                                <TableCell align="center" style={{ padding: "10px 10px", color: "#0f172a", fontSize: "0.82rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>{item.addedBy || "-"}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                        <TableFooter sx={{ backgroundColor: "#f8fafc" }}>
                          <TableRow sx={{ borderTop: "2px solid #e2e8f0" }}>
                            <TableCell colSpan={2} style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.82rem", padding: "12px 10px", borderBottom: "none" }}>
                              Total:
                            </TableCell>
                            <TableCell align="center" style={{ fontWeight: 700, color: "#0284c7", fontSize: "0.86rem", padding: "12px 8px", borderBottom: "none" }}>
                              {selectedReservationForView?.items?.reduce((sum, item) => sum + (isNoQtyItem(item) ? 0 : (Number(item.quantity) || 1)), 0) ?? 0}
                            </TableCell>
                            <TableCell align="center" style={{ fontWeight: 500, color: "#0f172a", fontSize: "0.86rem", padding: "12px 10px", whiteSpace: "nowrap", borderBottom: "none" }}>
                              {(() => {
                                const items = selectedReservationForView?.items || [];
                                const hasUsd = items.some((item) => (item.currency || "").toUpperCase() === "USD" || (item.currency || "").includes("$"));
                                if (hasUsd) {
                                  const totalUSD = items.reduce((sum, item) => {
                                    const isUsd = (item.currency || "").toUpperCase() === "USD" || (item.currency || "").includes("$");
                                    const price = Number(item.price || item.totalPrice || 0);
                                    return sum + (isUsd ? price : 0);
                                  }, 0);
                                  const totalPKR = items.reduce((sum, item) => {
                                    const isUsd = (item.currency || "").toUpperCase() === "USD" || (item.currency || "").includes("$");
                                    const price = Number(item.price || item.totalPrice || 0);
                                    const rate = Number(item.pkrRate || selectedReservationForView?.pkrRate || livePkrRate || 0);
                                    return sum + (isUsd && rate > 0 ? price * rate : price);
                                  }, 0);
                                  const dynRate = Number(selectedReservationForView?.pkrRate || livePkrRate || 0);
                                  return dynRate > 0
                                    ? `$${totalUSD.toFixed(2)} USD = PKR ${Math.round(totalPKR).toLocaleString()}`
                                    : `$${totalUSD.toFixed(2)}`;
                                }
                                const totalPKR = selectedReservationForView?.totalPrice || items.reduce((sum, item) => sum + Number(item.price || item.totalPrice || 0), 0) || 0;
                                return `Rs. ${Math.round(totalPKR).toLocaleString()}`;
                              })()}
                            </TableCell>
                            <TableCell align="center" style={{ padding: "12px 10px", borderBottom: "none" }} />
                            <TableCell colSpan={2} style={{ padding: "12px 10px", borderBottom: "none" }} />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </>
                );
              })()}
            </DialogContent>

            <DialogActions sx={{ p: { xs: "12px 16px", sm: "16px 24px" }, backgroundColor: "#f8fafc", borderTop: "1px solid #e2e8f0", justifyContent: "flex-end" }}>
              <MDButton
                variant="contained"
                size="small"
                onClick={() => setViewUpsellModalOpen(false)}
                sx={{ backgroundColor: "#0f172a !important", color: "#ffffff !important", borderRadius: "8px", textTransform: "none", fontSize: "0.8rem" }}
              >
                Close
              </MDButton>
            </DialogActions>
          </Dialog>

        </Box>
      )}

      {/* PAGE 3: Charges List (Database Records) */}
      {activeTab === 2 && (
        <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
          {/* Top Controls Bar for Admin, View Only, Custom, Full Access */}
          {showDateAtTop && (
            <MDBox display="flex" justifyContent={{ xs: "stretch", sm: "flex-end" }} alignItems="center" mb={3} flexWrap="wrap" gap={1.5} sx={{ width: "100%" }}>
              <MDBox display="flex" alignItems="center" gap={1.25} flexWrap="wrap" sx={{ width: { xs: "100%", sm: "auto" } }}>
                {/* Start Date Input Pill */}
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent={{ xs: "space-between", sm: "flex-start" }}
                  gap={1}
                  px={1.6}
                  py={0.65}
                  sx={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                    width: { xs: "100%", sm: "auto" },
                    flex: { xs: "1 1 100%", sm: "none" },
                    cursor: "default",
                  }}
                >
                  <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                    Start Date:
                  </MDTypography>
                  <input
                    type="date"
                    value={chargeStartDate}
                    onChange={(e) => {
                      setChargeStartDate(e.target.value);
                      if (chargeEndDate && e.target.value > chargeEndDate) {
                        setChargeEndDate(e.target.value);
                      }
                    }}
                    style={{
                      border: "none",
                      outline: "none",
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                      fontSize: "0.85rem",
                      color: "#0f172a",
                      fontWeight: 500,
                      backgroundColor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </MDBox>

                {/* End Date Input Pill */}
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent={{ xs: "space-between", sm: "flex-start" }}
                  gap={1}
                  px={1.6}
                  py={0.65}
                  sx={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                    width: { xs: "100%", sm: "auto" },
                    flex: { xs: "1 1 100%", sm: "none" },
                    cursor: "default",
                  }}
                >
                  <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                    End Date:
                  </MDTypography>
                  <input
                    type="date"
                    value={chargeEndDate}
                    onChange={(e) => {
                      setChargeEndDate(e.target.value);
                      if (chargeStartDate && e.target.value < chargeStartDate) {
                        setChargeStartDate(e.target.value);
                      }
                    }}
                    style={{
                      border: "none",
                      outline: "none",
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                      fontSize: "0.85rem",
                      color: "#0f172a",
                      fontWeight: 500,
                      backgroundColor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </MDBox>

                {/* Download Button (Opens Format Choice Modal) */}
                {canDownload && (
                  <MDButton
                    variant="outlined"
                    disabled={isViewOnly}
                    onClick={isViewOnly ? undefined : () => {
                      setTargetDownloadReservation(null);
                      setTargetDownloadCharge(null);
                      setDownloadModalOpen(true);
                    }}
                    sx={{
                      border: isViewOnly ? "1px solid #e2e8f0 !important" : "1px solid #cbd5e1 !important",
                      color: isViewOnly ? "#94a3b8 !important" : "#334155 !important",
                      borderRadius: "8px",
                      px: 1.8,
                      py: 0.65,
                      fontSize: "0.82rem",
                      fontWeight: 500,
                      textTransform: "none",
                      backgroundColor: isViewOnly ? "#f8fafc" : "#ffffff",
                      cursor: isViewOnly ? "not-allowed" : "pointer",
                      flex: { xs: 1, sm: "none" },
                      "&:hover": {
                        backgroundColor: isViewOnly ? "#f8fafc" : "#f8fafc",
                        borderColor: isViewOnly ? "#e2e8f0 !important" : "#94a3b8 !important",
                      },
                      "&.Mui-disabled": {
                        color: "#94a3b8 !important",
                        borderColor: "#e2e8f0 !important",
                        backgroundColor: "#f8fafc !important",
                      },
                    }}
                  >
                    Download
                  </MDButton>
                )}
              </MDBox>
            </MDBox>
          )}

          {/* 1. Three KPI Summary Cards */}
          {(() => {
            const entries = getSelectedDateChargeEntries();
            const filtered = getFilteredCharges();
            const totalAmountPKR = entries.reduce((sum, r) => {
              const isUsd = (r.currency || "").toUpperCase() === "USD" || (r.currency || "").includes("$");
              const price = Number(r.amount || 0);
              const rate = Number(r.pkrRate || livePkrRate || 0);
              return sum + (isUsd && rate > 0 ? price * rate : price);
            }, 0);
            const uniqueRes = [...new Set(entries.map((r) => String(r.reservationId)))].length;

            return (
              <Grid container spacing={2} mb={3.5}>
                {/* Card 1: Total charge records */}
                <Grid item xs={12} sm={4} md={4}>
                  <Card sx={{ p: 2.5, borderRadius: "14px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem", fontWeight: 500 }}>
                      Charge records
                    </MDTypography>
                    <MDTypography variant="h3" sx={{ color: "#0f172a", fontWeight: 700, fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" }, my: 0.6 }}>
                      {entries.length}
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.78rem" }}>
                      Charge lines for selected date
                    </MDTypography>
                  </Card>
                </Grid>

                {/* Card 2: Unique reservations */}
                <Grid item xs={12} sm={4} md={4}>
                  <Card sx={{ p: 2.5, borderRadius: "14px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem", fontWeight: 500 }}>
                      Unique reservations
                    </MDTypography>
                    <MDTypography variant="h3" sx={{ color: "#0f172a", fontWeight: 700, fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" }, my: 0.6 }}>
                      {uniqueRes}
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.78rem" }}>
                      Reservations charged
                    </MDTypography>
                  </Card>
                </Grid>

                {/* Card 3: Total charge revenue */}
                <Grid item xs={12} sm={4} md={4}>
                  <Card sx={{ p: 2.5, borderRadius: "14px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem", fontWeight: 500 }}>
                      Total charge amount
                    </MDTypography>
                    <MDTypography variant="h3" sx={{ color: "#059669", fontWeight: 500, fontSize: { xs: "1.4rem", sm: "1.65rem", md: "2rem" }, my: 0.6, wordBreak: "break-word" }}>
                      {`Rs. ${Math.round(totalAmountPKR).toLocaleString()}`}
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.78rem" }}>
                      All selected-date charges
                    </MDTypography>
                  </Card>
                </Grid>
              </Grid>
            );
          })()}

          {/* 2. Main Charges Table Card */}
          <Card sx={{ mb: 3.5, overflow: "hidden", borderRadius: "16px", boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", backgroundColor: "#ffffff" }}>
            {/* Card Header with Title, Date Filters, Search Bar and Refresh Button */}
            <MDBox p={2.5} px={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ borderBottom: "1px solid #f1f5f9" }}>
              {/* Left Side: Title */}
              <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#0f172a", fontSize: "1.05rem", letterSpacing: "-0.2px" }}>
                Charges
              </MDTypography>

              {/* Right Side: Start/End Date (Standard & Custom View Users), Search Bar, Refresh Button */}
              <MDBox display="flex" alignItems="center" gap={1.25} flexWrap="wrap" sx={{ width: { xs: "100%", sm: "auto" } }}>
                {/* Start Date & End Date Input Pills (Only for Standard Users & Custom View Users before Search Bar) */}
                {showDateBeforeSearchBar && (
                  <>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1}
                      px={1.4}
                      py={0.55}
                      sx={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #cbd5e1",
                        borderRadius: "8px",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                        cursor: "default",
                      }}
                    >
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                        Start Date:
                      </MDTypography>
                      <input
                        type="date"
                        value={chargeStartDate}
                        onChange={(e) => {
                          setChargeStartDate(e.target.value);
                          if (chargeEndDate && e.target.value > chargeEndDate) {
                            setChargeEndDate(e.target.value);
                          }
                        }}
                        style={{
                          border: "none",
                          outline: "none",
                          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                          fontSize: "0.84rem",
                          color: "#0f172a",
                          fontWeight: 500,
                          backgroundColor: "transparent",
                          cursor: "pointer",
                        }}
                      />
                    </MDBox>

                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1}
                      px={1.4}
                      py={0.55}
                      sx={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #cbd5e1",
                        borderRadius: "8px",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                        cursor: "default",
                      }}
                    >
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                        End Date:
                      </MDTypography>
                      <input
                        type="date"
                        value={chargeEndDate}
                        onChange={(e) => {
                          setChargeEndDate(e.target.value);
                          if (chargeStartDate && e.target.value < chargeStartDate) {
                            setChargeStartDate(e.target.value);
                          }
                        }}
                        style={{
                          border: "none",
                          outline: "none",
                          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                          fontSize: "0.84rem",
                          color: "#0f172a",
                          fontWeight: 500,
                          backgroundColor: "transparent",
                          cursor: "pointer",
                        }}
                      />
                    </MDBox>
                  </>
                )}

                {/* Search Bar */}
                <MDBox width={{ xs: "100%", sm: "240px", md: "260px" }}>
                  <MDInput
                    placeholder="Search Res ID, Charge ID, Title..."
                    size="small"
                    fullWidth
                    value={chargeSearchQuery}
                    onChange={(e) => setChargeSearchQuery(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#ffffff",
                        fontSize: "0.84rem",
                        borderColor: "#cbd5e1",
                      },
                    }}
                  />
                </MDBox>

                {/* Refresh Button */}
                <MDButton
                  variant="contained"
                  onClick={fetchDbChargeRecords}
                  disabled={loadingDbCharges}
                  sx={{
                    backgroundColor: "#0284c7 !important",
                    color: "#ffffff !important",
                    borderRadius: "8px",
                    px: 2.2,
                    py: 0.75,
                    fontSize: "0.84rem",
                    fontWeight: 500,
                    textTransform: "none",
                    boxShadow: "0 2px 6px rgba(2, 132, 199, 0.2)",
                    whiteSpace: "nowrap",
                    "&:hover": { backgroundColor: "#0369a1 !important" },
                  }}
                >
                  {loadingDbCharges ? "Loading..." : "Refresh"}
                </MDButton>
              </MDBox>
            </MDBox>

            {/* Table Content */}
            {(() => {
              const filtered = getFilteredCharges();
              const entries = getSelectedDateChargeEntries();
              const totalAmountPKR = entries.reduce((sum, r) => {
                const isUsd = (r.currency || "").toUpperCase() === "USD" || (r.currency || "").includes("$");
                const price = Number(r.amount || 0);
                const rate = Number(r.pkrRate || livePkrRate || 0);
                return sum + (isUsd && rate > 0 ? price * rate : price);
              }, 0);

              if (loadingDbCharges && dbChargeRecords.length === 0) {
                return (
                  <MDBox py={8} textAlign="center" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <CircularProgress color="info" size={38} sx={{ mb: 2 }} />
                    <MDTypography variant="button" color="text" fontWeight="medium">Fetching charge records...</MDTypography>
                  </MDBox>
                );
              }

              if (filtered.length === 0) {
                return (
                  <MDBox py={7} textAlign="center">
                    <Icon color="disabled" sx={{ fontSize: "3.2rem", mb: 1 }}>inbox</Icon>
                    <MDTypography variant="h6" color="text" fontWeight="medium">No Charge Records Found</MDTypography>
                    <MDTypography variant="caption" color="secondary">Try selecting another date range or clearing your search.</MDTypography>
                  </MDBox>
                );
              }

              return (
                <>
                  {/* ---- MOBILE LIST VIEW (xs to sm) ---- */}
                  <MDBox sx={{ display: { xs: "block", md: "none" } }}>
                    {filtered.map((rec) => {
                      const dateFormatted = formatPakistanDateTime(rec.dateTime);
                      const isRefund =
                        Number(rec.amount) < 0 ||
                        rec.status === "refunded" ||
                        rec.type === "refund" ||
                        rec.isRefund ||
                        (rec.title || "").toLowerCase().includes("refund") ||
                        (rec.description || "").toLowerCase().includes("refund");

                      return (
                        <MDBox
                          key={rec.id || rec.chargeId}
                          sx={{ borderBottom: "1px solid #f1f5f9", p: 2, "&:last-child": { borderBottom: "none" } }}
                        >
                          {/* Top row: Res ID + Date + Amount */}
                          <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <MDTypography
                                component="a"
                                href={`https://dashboard.hostaway.com/reservations/${String(rec.reservationId).replace(/[^0-9]/g, "")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  backgroundColor: "#eff6ff", color: "#0284c7",
                                  px: 1.4, py: 0.35, borderRadius: "6px",
                                  fontSize: "0.84rem", fontWeight: 400,
                                  border: "1px solid #e0f2fe", textDecoration: "none", display: "inline-block",
                                }}
                              >
                                #{rec.reservationId}
                              </MDTypography>
                              <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.76rem" }}>
                                {dateFormatted}
                              </MDTypography>
                            </MDBox>
                            <MDBox
                              px={1.4}
                              py={0.4}
                              borderRadius="6px"
                              display="inline-block"
                              sx={{
                                backgroundColor: isRefund ? "#fef2f2" : "#f8fafc",
                                color: isRefund ? "#b91c1c" : "#0f172a",
                                border: isRefund ? "1px solid #fecaca" : "1px solid #e2e8f0",
                                fontWeight: isRefund ? 600 : 400,
                                fontSize: "0.84rem"
                              }}
                            >
                              {formatChargeAmountWithConversion(rec.amount, rec.currency, rec.pkrRate)}
                            </MDBox>
                          </MDBox>
                          {/* Guest Name & Apartment Name */}
                          <MDTypography variant="body2" sx={{ color: "#0f172a", fontSize: "0.86rem", mb: 0.2 }}>
                            {rec.guestName || "Guest"}
                          </MDTypography>
                          {rec.apartmentName && rec.apartmentName !== "-" ? (
                            <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.78rem", mb: 0.5, display: "block" }}>
                              Apartment: <strong style={{ color: "#334155" }}>{rec.apartmentName}</strong>
                            </MDTypography>
                          ) : null}
                          {/* Title row */}
                          <MDBox display="flex" alignItems="center" gap={1} mb={0.3}>
                            <MDTypography variant="body2" sx={{ color: "#0f172a", fontSize: "0.86rem", fontWeight: 500 }}>
                              {rec.title || "-"}
                            </MDTypography>
                            {isRefund && (
                              <Chip
                                label="Refund"
                                size="small"
                                sx={{
                                  backgroundColor: "#fee2e2",
                                  color: "#b91c1c",
                                  fontWeight: 600,
                                  fontSize: "0.68rem",
                                  height: "20px",
                                  borderRadius: "4px",
                                }}
                              />
                            )}
                          </MDBox>
                          {/* Description */}
                          {rec.description && (
                            <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.78rem", display: "block", mb: 1, whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.45 }}>
                              {rec.description}
                            </MDTypography>
                          )}
                          {/* Action Buttons */}
                          <MDBox display="flex" gap={1} mt={1}>
                            <MDButton
                              variant="outlined" size="small"
                              onClick={() => { setSelectedChargeForView(rec); setViewChargeModalOpen(true); }}
                              sx={{ border: "1px solid #cbd5e1 !important", color: "#334155 !important", borderRadius: "8px", px: 1.8, fontSize: "0.76rem", fontWeight: 500, textTransform: "none", backgroundColor: "#ffffff", flex: 1 }}
                            >View</MDButton>
                            {canDownload && (
                              <MDButton
                                variant="outlined" size="small"
                                disabled={isViewOnly}
                                onClick={isViewOnly ? undefined : () => { setTargetDownloadReservation(null); setTargetDownloadCharge(rec); setDownloadModalOpen(true); }}
                                sx={{
                                  border: isViewOnly ? "1px solid #e2e8f0 !important" : "1px solid #cbd5e1 !important",
                                  color: isViewOnly ? "#94a3b8 !important" : "#334155 !important",
                                  borderRadius: "8px", px: 1.8, fontSize: "0.76rem", fontWeight: 500,
                                  textTransform: "none",
                                  backgroundColor: isViewOnly ? "#f8fafc" : "#ffffff",
                                  cursor: isViewOnly ? "not-allowed" : "pointer",
                                  flex: 1,
                                  "&.Mui-disabled": {
                                    color: "#94a3b8 !important",
                                    borderColor: "#e2e8f0 !important",
                                    backgroundColor: "#f8fafc !important",
                                  },
                                }}
                              >Download</MDButton>
                            )}
                          </MDBox>
                        </MDBox>
                      );
                    })}
                    {/* Mobile Footer */}
                    <MDBox p={2} px={3} display="flex" justifyContent="space-between" alignItems="center" sx={{ borderTop: "1px solid #f1f5f9", backgroundColor: "#f8fafc" }}>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                        Records: <strong style={{ color: "#0f172a" }}>{filtered.length}</strong>
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                        Total: <strong style={{ color: "#0f172a" }}>{`Rs. ${Math.round(totalAmountPKR).toLocaleString()}`}</strong>
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  {/* ---- DESKTOP TABLE VIEW (md and above) ---- */}
                  <MDBox sx={{ display: { xs: "none", md: "block" }, overflowX: "auto" }}>
                    <Table sx={{ width: "100%", minWidth: 950, borderCollapse: "separate", borderSpacing: "0" }}>
                      <TableHead sx={{ display: "table-header-group" }}>
                        <TableRow sx={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                          <TableCell align="center" style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>DATE &amp; TIME</TableCell>
                          <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>RESERVATION ID</TableCell>
                          <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>GUEST NAME</TableCell>
                          <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>APARTMENT NAME</TableCell>
                          <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>TRANSACTION TYPE</TableCell>
                          <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>DESCRIPTION</TableCell>
                          <TableCell align="center" style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em" }}>AMOUNT</TableCell>
                          <TableCell align="center" style={{ fontWeight: 500, color: "#64748b", fontSize: "0.74rem", textTransform: "uppercase", padding: "14px 20px", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.04em" }}>ACTIONS</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody sx={{ backgroundColor: "#ffffff" }}>
                        {filtered.map((rec) => {
                          const dateFormatted = formatPakistanDateTime(rec.dateTime);
                          const isRefund =
                            Number(rec.amount) < 0 ||
                            rec.status === "refunded" ||
                            rec.type === "refund" ||
                            rec.isRefund ||
                            (rec.title || "").toLowerCase().includes("refund") ||
                            (rec.description || "").toLowerCase().includes("refund");

                          return (
                            <TableRow key={rec.id || rec.chargeId} sx={{ borderBottom: "1px solid #f1f5f9", transition: "background-color 0.15s ease", "&:hover": { backgroundColor: "#f8fafc" } }}>
                              <TableCell align="center" style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", color: "#0f172a", fontSize: "0.82rem", whiteSpace: "nowrap" }}>{dateFormatted}</TableCell>
                              <TableCell style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                                <MDTypography component="a" href={`https://dashboard.hostaway.com/reservations/${String(rec.reservationId).replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" variant="button" sx={{ backgroundColor: "#eff6ff", color: "#0284c7", px: 1.4, py: 0.35, borderRadius: "6px", fontSize: "0.84rem", fontWeight: 400, border: "1px solid #e0f2fe", textDecoration: "none", display: "inline-block", "&:hover": { backgroundColor: "#dbeafe", color: "#0369a1" } }}>#{rec.reservationId}</MDTypography>
                              </TableCell>
                              <TableCell style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                                <MDTypography variant="button" sx={{ color: "#0f172a", fontWeight: 400, fontSize: "0.86rem" }}>{rec.guestName || "Guest"}</MDTypography>
                              </TableCell>
                              <TableCell style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                                <MDTypography variant="caption" sx={{ color: "#475569", fontWeight: 500, fontSize: "0.82rem" }}>
                                  {(() => {
                                    if (rec.apartmentName && rec.apartmentName !== "-") return rec.apartmentName;
                                    const cleanId = String(rec.reservationId || "").replace(/[^0-9]/g, "");
                                    const matchedFdo = (fdoRecords || []).find((f) => {
                                      const rId = String(f.fields?.["Reservation ID"] || f.fields?.["Reservation ID "] || f.fields?.["Hostaway Reservation ID"] || f.fields?.ReservationId || f.fields?.reservationId || f.id || "").replace(/[^0-9]/g, "");
                                      return rId === cleanId;
                                    });
                                    return (
                                      matchedFdo?.fields?.["Listing Name"] ||
                                      matchedFdo?.fields?.["Listing Name "] ||
                                      matchedFdo?.fields?.["Apartment Name"] ||
                                      matchedFdo?.fields?.["Apartment Name "] ||
                                      matchedFdo?.fields?.["Apartment"] ||
                                      matchedFdo?.fields?.["Listing"] ||
                                      matchedFdo?.fields?.["Unit"] ||
                                      rec.apartmentName ||
                                      "-"
                                    );
                                  })()}
                                </MDTypography>
                              </TableCell>
                              <TableCell style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                                <MDBox display="flex" alignItems="center" gap={1}>
                                  <MDTypography variant="button" sx={{ color: "#0f172a", fontWeight: 400, fontSize: "0.86rem" }}>{rec.title || "-"}</MDTypography>
                                  {isRefund && (
                                    <Chip
                                      label="Refund"
                                      size="small"
                                      sx={{
                                        backgroundColor: "#fee2e2",
                                        color: "#b91c1c",
                                        fontWeight: 600,
                                        fontSize: "0.68rem",
                                        height: "20px",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  )}
                                </MDBox>
                              </TableCell>
                              <TableCell style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", minWidth: "160px", maxWidth: "320px" }}>
                                <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: 400, fontSize: "0.82rem", whiteSpace: "normal", wordBreak: "break-word", display: "block", lineHeight: 1.45 }}>{rec.description || "-"}</MDTypography>
                              </TableCell>
                              <TableCell align="center" style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                                <MDBox
                                  px={1.4}
                                  py={0.4}
                                  borderRadius="6px"
                                  display="inline-block"
                                  sx={{
                                    backgroundColor: isRefund ? "#fef2f2" : "#f8fafc",
                                    color: isRefund ? "#b91c1c" : "#0f172a",
                                    border: isRefund ? "1px solid #fecaca" : "1px solid #e2e8f0",
                                    fontWeight: isRefund ? 600 : 400,
                                    fontSize: "0.84rem"
                                  }}
                                >
                                  {formatChargeAmountWithConversion(rec.amount, rec.currency, rec.pkrRate)}
                                </MDBox>
                              </TableCell>
                              <TableCell align="center" style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                                <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
                                  <MDButton variant="outlined" size="small" onClick={() => { setSelectedChargeForView(rec); setViewChargeModalOpen(true); }} sx={{ border: "1px solid #cbd5e1 !important", color: "#334155 !important", borderRadius: "8px", px: 1.4, py: 0.35, fontSize: "0.76rem", fontWeight: 500, textTransform: "none", backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#f8fafc", borderColor: "#94a3b8 !important" } }}>View</MDButton>
                                  {canDownload && (
                                    <MDButton
                                      variant="outlined" size="small"
                                      disabled={isViewOnly}
                                      onClick={isViewOnly ? undefined : () => { setTargetDownloadReservation(null); setTargetDownloadCharge(rec); setDownloadModalOpen(true); }}
                                      sx={{
                                        border: isViewOnly ? "1px solid #e2e8f0 !important" : "1px solid #cbd5e1 !important",
                                        color: isViewOnly ? "#94a3b8 !important" : "#334155 !important",
                                        borderRadius: "8px", px: 1.4, py: 0.35, fontSize: "0.76rem", fontWeight: 500,
                                        textTransform: "none",
                                        backgroundColor: isViewOnly ? "#f8fafc" : "#ffffff",
                                        cursor: isViewOnly ? "not-allowed" : "pointer",
                                        "&:hover": {
                                          backgroundColor: isViewOnly ? "#f8fafc" : "#f8fafc",
                                          borderColor: isViewOnly ? "#e2e8f0 !important" : "#94a3b8 !important",
                                        },
                                        "&.Mui-disabled": {
                                          color: "#94a3b8 !important",
                                          borderColor: "#e2e8f0 !important",
                                          backgroundColor: "#f8fafc !important",
                                        },
                                      }}
                                    >Download</MDButton>
                                  )}
                                </MDBox>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </MDBox>

                  {/* Table Footer Summary Bar */}
                  <MDBox p={2} px={3} display="flex" justifyContent="flex-end" alignItems="center" gap={3} sx={{ borderTop: "1px solid #f1f5f9", backgroundColor: "#f8fafc" }}>
                    <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem" }}>
                      Selected date: <strong style={{ color: "#0f172a", fontWeight: 500 }}>{chargeStartDate === chargeEndDate ? formatFooterDate(chargeStartDate) : `${formatFooterDate(chargeStartDate)} - ${formatFooterDate(chargeEndDate)}`}</strong>
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem" }}>
                      Records: <strong style={{ color: "#0f172a", fontWeight: 500 }}>{filtered.length}</strong>
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem" }}>
                      Total: <strong style={{ color: "#0f172a", fontWeight: 500 }}>{`Rs. ${Math.round(totalAmountPKR).toLocaleString()}`}</strong>
                    </MDTypography>
                  </MDBox>
                </>
              );
            })()}
          </Card>

          {/* 4. View Single Charge Modal Dialog */}
          <Dialog
            open={viewChargeModalOpen}
            onClose={() => setViewChargeModalOpen(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "16px",
                m: { xs: 1, sm: 3 },
                boxShadow: "0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.05)",
                border: "1px solid #e2e8f0",
                overflow: "hidden",
                maxWidth: { xs: "95%", sm: "90%", md: "1150px", lg: "1200px" },
              },
            }}
          >
            <DialogTitle component="div" sx={{ p: "18px 24px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDBox display="flex" alignItems="center" gap={1.2}>
                    <MDTypography variant="h6" sx={{ color: "#0f172a", fontWeight: 700, fontSize: "1.1rem" }}>
                      {Number(selectedChargeForView?.amount) < 0 || selectedChargeForView?.status === "refunded" ? "Refund Details" : "Charge Details"}
                    </MDTypography>
                    <Chip
                      label={selectedChargeForView?.currency || "PKR"}
                      size="small"
                      sx={{ backgroundColor: "#e0f2fe", color: "#0284c7", fontWeight: 700, fontSize: "0.75rem", height: "24px" }}
                    />
                    {(Number(selectedChargeForView?.amount) < 0 || selectedChargeForView?.status === "refunded") && (
                      <Chip
                        label="Refund"
                        size="small"
                        sx={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: 700, fontSize: "0.75rem", height: "24px" }}
                      />
                    )}
                  </MDBox>
                  <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.82rem", mt: 0.3, display: "block" }}>
                    Reservation ID: <span style={{ color: "#0f172a", fontWeight: 400 }}>#{selectedChargeForView?.reservationId}</span>
                  </MDTypography>
                </MDBox>
                <IconButton size="small" onClick={() => setViewChargeModalOpen(false)} sx={{ color: "#64748b", "&:hover": { color: "#0f172a" } }}>
                  <Icon>close</Icon>
                </IconButton>
              </MDBox>
            </DialogTitle>

            <DialogContent sx={{ p: 0, overflowX: { xs: "auto", md: "hidden" } }}>
              {/* MOBILE CARD VIEW FOR VIEW CHARGE DIALOG (xs/sm) */}
              <MDBox sx={{ display: { xs: "block", md: "none" }, p: 2 }}>
                {selectedChargeForView && (
                  <MDBox>
                    <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <MDTypography variant="body2" sx={{ color: "#0f172a", fontWeight: 600, fontSize: "0.9rem" }}>
                          {selectedChargeForView.title || "-"}
                        </MDTypography>
                        {(Number(selectedChargeForView?.amount) < 0 || selectedChargeForView?.status === "refunded") && (
                          <Chip
                            label="Refund"
                            size="small"
                            sx={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: 600, fontSize: "0.68rem", height: "20px", borderRadius: "4px" }}
                          />
                        )}
                      </MDBox>
                      <MDBox
                        px={1.2}
                        py={0.3}
                        borderRadius="6px"
                        display="inline-block"
                        sx={{
                          backgroundColor: Number(selectedChargeForView.amount) < 0 ? "#fef2f2" : "#f8fafc",
                          color: Number(selectedChargeForView.amount) < 0 ? "#b91c1c" : "#0f172a",
                          border: Number(selectedChargeForView.amount) < 0 ? "1px solid #fecaca" : "1px solid #e2e8f0",
                          fontWeight: 500,
                          fontSize: "0.82rem"
                        }}
                      >
                        {formatChargeAmountWithConversion(selectedChargeForView.amount, selectedChargeForView.currency, selectedChargeForView.pkrRate)}
                      </MDBox>
                    </MDBox>
                    <MDTypography variant="body2" sx={{ color: "#0f172a", fontSize: "0.86rem", mb: 0.2 }}>
                      {selectedChargeForView.guestName || "Guest"}
                    </MDTypography>
                    {selectedChargeForView.apartmentName && selectedChargeForView.apartmentName !== "-" ? (
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.78rem", mb: 0.8, display: "block" }}>
                        Apartment: <span style={{ color: "#334155", fontWeight: 400 }}>{selectedChargeForView.apartmentName}</span>
                      </MDTypography>
                    ) : null}
                    <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={1}>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.78rem" }}>
                        Reservation ID: <span style={{ color: "#0284c7", fontWeight: 400 }}>#{selectedChargeForView.reservationId}</span>
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.76rem" }}>
                        {selectedChargeForView.dateTime
                          ? new Date(selectedChargeForView.dateTime).toLocaleString("en-US", { month: "short", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })
                          : "-"}
                      </MDTypography>
                    </MDBox>
                    {selectedChargeForView.description && (
                      <MDBox p={1.5} mb={1.5} borderRadius="8px" sx={{ backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                        <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.75rem", fontWeight: 600, display: "block", mb: 0.3, textTransform: "uppercase" }}>
                          Description
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: "#334155", fontSize: "0.82rem", whiteSpace: "normal", wordBreak: "break-word", display: "block", lineHeight: 1.45 }}>
                          {selectedChargeForView.description}
                        </MDTypography>
                      </MDBox>
                    )}
                    <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.74rem" }}>
                        Added By: {selectedChargeForView.addedBy || "-"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                )}
              </MDBox>

              {/* DESKTOP TABLE VIEW FOR VIEW CHARGE DIALOG (md+) */}
              <TableContainer sx={{ boxShadow: "none", overflowX: { xs: "auto", md: "hidden" }, display: { xs: "none", md: "block" } }}>
                <Table sx={{ width: "100%", tableLayout: "auto", borderCollapse: "separate", borderSpacing: "0", "& .MuiTableCell-root": { display: "table-cell !important", verticalAlign: "middle" } }}>
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      <TableCell align="center" style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "12px 10px", borderBottom: "1px solid #e2e8f0", width: "45px", whiteSpace: "nowrap" }}>SR</TableCell>
                      <TableCell align="center" style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "12px 12px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>Date &amp; Time</TableCell>
                      <TableCell style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "12px 12px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>Guest Name</TableCell>
                      <TableCell style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "12px 12px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>Apartment Name</TableCell>
                      <TableCell style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "12px 12px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>Transaction Type</TableCell>
                      <TableCell style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "12px 12px", borderBottom: "1px solid #e2e8f0" }}>Description</TableCell>
                      <TableCell align="center" style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "12px 12px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>Amount</TableCell>
                      <TableCell align="center" style={{ fontWeight: 600, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "12px 12px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>Added By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ backgroundColor: "#ffffff" }}>
                    {selectedChargeForView && (
                      <TableRow sx={{ "&:hover": { backgroundColor: "#f8fafc" }, borderBottom: "1px solid #f1f5f9" }}>
                        <TableCell align="center" style={{ padding: "12px 10px", color: "#64748b", fontSize: "0.82rem", borderBottom: "1px solid #f1f5f9" }}>1</TableCell>
                        <TableCell align="center" style={{ padding: "12px 12px", color: "#64748b", fontSize: "0.8rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>
                          {formatPakistanDateTime(selectedChargeForView.dateTime)}
                        </TableCell>
                        <TableCell style={{ padding: "12px 12px", color: "#0f172a", fontWeight: 400, fontSize: "0.84rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>
                          {selectedChargeForView.guestName || "Guest"}
                        </TableCell>
                        <TableCell style={{ padding: "12px 12px", color: "#475569", fontWeight: 500, fontSize: "0.82rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>
                          {selectedChargeForView.apartmentName || "-"}
                        </TableCell>
                        <TableCell style={{ padding: "12px 12px", color: "#0f172a", fontWeight: 600, fontSize: "0.84rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <span>{selectedChargeForView.title || "-"}</span>
                            {(Number(selectedChargeForView?.amount) < 0 || selectedChargeForView?.status === "refunded") && (
                              <Chip
                                label="Refund"
                                size="small"
                                sx={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: 600, fontSize: "0.68rem", height: "20px", borderRadius: "4px" }}
                              />
                            )}
                          </MDBox>
                        </TableCell>
                        <TableCell style={{ padding: "12px 12px", borderBottom: "1px solid #f1f5f9" }}>
                          <MDTypography
                            variant="caption"
                            sx={{
                              color: "#334155",
                              fontWeight: 400,
                              fontSize: "0.82rem",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              display: "block",
                              lineHeight: 1.45,
                            }}
                          >
                            {selectedChargeForView.description || "-"}
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center" style={{ padding: "12px 12px", color: Number(selectedChargeForView.amount) < 0 ? "#b91c1c" : "#0f172a", fontWeight: 500, fontSize: "0.84rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>
                          {formatChargeAmountWithConversion(selectedChargeForView.amount, selectedChargeForView.currency, selectedChargeForView.pkrRate)}
                        </TableCell>
                        <TableCell align="center" style={{ padding: "12px 12px", color: "#0f172a", fontSize: "0.82rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>
                          {selectedChargeForView.addedBy || "-"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                  <TableFooter sx={{ backgroundColor: "#f8fafc" }}>
                    <TableRow sx={{ borderTop: "2px solid #e2e8f0" }}>
                      <TableCell colSpan={6} style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.82rem", padding: "12px 12px", borderBottom: "none" }}>
                        Total:
                      </TableCell>
                      <TableCell align="center" style={{ fontWeight: 500, color: "#0f172a", fontSize: "0.86rem", padding: "12px 12px", whiteSpace: "nowrap", borderBottom: "none" }}>
                        {formatChargeAmountWithConversion(selectedChargeForView?.amount, selectedChargeForView?.currency, selectedChargeForView?.pkrRate)}
                      </TableCell>
                      <TableCell colSpan={1} style={{ padding: "12px 12px", borderBottom: "none" }} />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </DialogContent>

            <DialogActions sx={{ p: { xs: "12px 16px", sm: "16px 24px" }, backgroundColor: "#f8fafc", borderTop: "1px solid #e2e8f0", justifyContent: "flex-end" }}>
              <MDButton
                variant="contained"
                size="small"
                onClick={() => setViewChargeModalOpen(false)}
                sx={{ backgroundColor: "#0f172a !important", color: "#ffffff !important", borderRadius: "8px", textTransform: "none", fontSize: "0.8rem" }}
              >
                Close
              </MDButton>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* 5. Download Options Modal Dialog (PDF or Excel - Shared globally across all tabs) */}
      <Dialog
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
            boxShadow: "0 20px 25px -5px rgba(15, 23, 42, 0.15)",
            border: "1px solid #e2e8f0",
          },
        }}
      >
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6" sx={{ color: "#0f172a", fontWeight: 700, fontSize: "1.05rem" }}>
              {targetDownloadCharge ? `Export Charge #${targetDownloadCharge.chargeId || targetDownloadCharge.id}` : targetDownloadReservation ? `Export Reservation #${targetDownloadReservation.reservationId}` : activeTab === 2 ? "Export Charges Overview" : "Export Upsell Overview"}
            </MDTypography>
            <IconButton size="small" onClick={() => setDownloadModalOpen(false)} sx={{ color: "#64748b" }}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
          <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.8rem", mt: 0.2, display: "block" }}>
            Choose your preferred export format:
          </MDTypography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, pb: 2 }}>
          <MDBox display="flex" flexDirection="column" gap={1.5}>
            {/* Option 1: PDF */}
            <MDBox
              onClick={() => {
                if (targetDownloadCharge) {
                  handleExportChargePDF(targetDownloadCharge);
                } else if (targetDownloadReservation) {
                  handleExportSinglePDF(targetDownloadReservation);
                } else if (activeTab === 2) {
                  handleExportAllChargesPDF();
                } else {
                  handleExportAllPDF();
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "#ef4444",
                  backgroundColor: "#fef2f2",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(239, 68, 68, 0.12)",
                },
              }}
            >
              <MDBox
                width="42px"
                height="42px"
                borderRadius="10px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{ backgroundColor: "#fee2e2", color: "#dc2626" }}
              >
                <Icon sx={{ fontSize: "1.5rem !important" }}>picture_as_pdf</Icon>
              </MDBox>
              <MDBox display="flex" flexDirection="column">
                <MDTypography variant="button" sx={{ color: "#0f172a", fontWeight: 700, fontSize: "0.9rem" }}>
                  Download PDF
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.76rem" }}>
                  Clean, printable PDF document format
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* Option 2: Excel */}
            <MDBox
              onClick={() => {
                if (targetDownloadCharge) {
                  handleExportChargeExcel(targetDownloadCharge);
                } else if (targetDownloadReservation) {
                  handleExportSingleExcel(targetDownloadReservation);
                } else if (activeTab === 2) {
                  handleExportAllChargesExcel();
                } else {
                  handleExportAllExcel();
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "#10b981",
                  backgroundColor: "#ecfdf5",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(16, 185, 129, 0.12)",
                },
              }}
            >
              <MDBox
                width="42px"
                height="42px"
                borderRadius="10px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{ backgroundColor: "#d1fae5", color: "#059669" }}
              >
                <Icon sx={{ fontSize: "1.5rem !important" }}>table_chart</Icon>
              </MDBox>
              <MDBox display="flex" flexDirection="column">
                <MDTypography variant="button" sx={{ color: "#0f172a", fontWeight: 700, fontSize: "0.9rem" }}>
                  Download Excel (.xlsx)
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.76rem" }}>
                  Full spreadsheet for data analysis
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>
      </Dialog>

      {/* Add Transaction POPUP MODAL DIALOG (Post New Charge) */}
      <Dialog
        open={openChargeModal}
        onClose={() => setOpenChargeModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            m: { xs: 1.5, sm: 3 },
            boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.15)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            maxWidth: { xs: "95%", sm: "800px", md: "860px" },
          },
        }}
      >
        <DialogTitle component="div" sx={{ p: "18px 24px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDBox display="flex" alignItems="center" gap={1.2}>
              <MDTypography
                variant="h6"
                fontWeight="bold"
                sx={{
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  fontSize: "1.05rem",
                  color: "#0f172a",
                }}
              >
                {chargeModalStep === 1
                  ? "Reservation Details"
                  : chargeModalStep === 2
                    ? "Add New Charge"
                    : "Refund Security Deposit"}
              </MDTypography>
              <Chip
                label={
                  chargeModalStep === 1
                    ? "Overview"
                    : chargeModalStep === 2
                      ? "Add Charge"
                      : "Refund Deposit"
                }
                size="small"
                sx={{
                  backgroundColor:
                    chargeModalStep === 1
                      ? "#e0f2fe"
                      : chargeModalStep === 2
                        ? "#dcfce7"
                        : "#ffe4e6",
                  color:
                    chargeModalStep === 1
                      ? "#0369a1"
                      : chargeModalStep === 2
                        ? "#15803d"
                        : "#be123c",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  height: "20px",
                }}
              />
            </MDBox>
            <IconButton
              size="small"
              onClick={() => setOpenChargeModal(false)}
              sx={{ color: "#64748b", p: 0.5, "&:hover": { color: "#0f172a" } }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ p: "24px" }}>
          <MDBox display="flex" flexDirection="column" gap={2} pt={0.5}>
            {/* Hostaway Reservation: Res ID, Guest Name & Apartment Name in Complete Full-Width Line */}
            {reservationDetails?.id || selectedResId ? (
              <MDBox
                p={{ xs: 1.5, sm: 2 }}
                px={{ xs: 1.5, sm: 2.5 }}
                borderRadius="10px"
                sx={{
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  width: "100%",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
                  gap: { xs: 1, sm: 2 },
                  flexWrap: "wrap",
                }}
              >
                {/* Reservation ID */}
                <MDBox display="flex" alignItems="center" gap={0.8} sx={{ whiteSpace: "nowrap" }}>
                  <span style={{ color: "#64748b", fontSize: "0.82rem", fontWeight: 400 }}>Reservation ID:</span>
                  <MDBox
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      backgroundColor: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: "6px",
                      px: 1.1,
                      py: 0.25,
                      fontSize: "0.84rem",
                      fontWeight: 400,
                      color: "#1d4ed8",
                    }}
                  >
                    #{reservationDetails?.id || selectedResId}
                  </MDBox>
                </MDBox>

                <MDBox component="span" sx={{ color: "#cbd5e1", fontSize: "1rem", display: { xs: "none", sm: "inline" } }}>|</MDBox>

                {/* Guest Name */}
                <MDBox display="flex" alignItems="center" gap={0.8} sx={{ whiteSpace: "nowrap" }}>
                  <span style={{ color: "#64748b", fontSize: "0.82rem", fontWeight: 400 }}>Guest Name:</span>
                  <span style={{ color: "#0f172a", fontSize: "0.88rem", fontWeight: 400 }}>
                    {(() => {
                      if (reservationDetails?.guestName && reservationDetails.guestName !== "Guest") {
                        return reservationDetails.guestName;
                      }
                      const cleanId = String(reservationDetails?.id || selectedResId || "").replace(/[^0-9]/g, "");
                      const matched = (fdoRecords || []).find((r) => {
                        const f = r.fields || r;
                        const rId = String(f["Reservation ID"] || f["Reservation ID "] || f["Hostaway Reservation ID"] || f.ReservationId || f.reservationId || f.id || "").replace(/[^0-9]/g, "");
                        return rId === cleanId;
                      });
                      return matched?.fields?.["Guest Name"] || matched?.fields?.["Guest Name "] || matched?.fields?.guestName || reservationDetails?.guestName || "Guest";
                    })()}
                  </span>
                </MDBox>

                <MDBox component="span" sx={{ color: "#cbd5e1", fontSize: "1rem", display: { xs: "none", sm: "inline" } }}>|</MDBox>

                {/* Apartment Name */}
                <MDBox display="flex" alignItems="center" gap={0.8} sx={{ whiteSpace: "nowrap" }}>
                  <span style={{ color: "#64748b", fontSize: "0.82rem", fontWeight: 400 }}>Apartment Name:</span>
                  <MDBox
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      backgroundColor: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      px: 1.1,
                      py: 0.25,
                      fontSize: "0.84rem",
                      fontWeight: 400,
                      color: "#0f172a",
                    }}
                  >
                    {(() => {
                      const direct = reservationDetails?.listingName || reservationDetails?.apartmentName;
                      if (direct && direct !== "-") return direct;
                      const cleanId = String(reservationDetails?.id || selectedResId || "").replace(/[^0-9]/g, "");
                      if (!cleanId) return "-";
                      const matched = (fdoRecords || []).find((r) => {
                        const f = r.fields || r;
                        const rId = String(f["Reservation ID"] || f["Reservation ID "] || f["Hostaway Reservation ID"] || f.ReservationId || f.reservationId || f.id || "").replace(/[^0-9]/g, "");
                        return rId === cleanId;
                      });
                      return (
                        matched?.fields?.["Listing Name"] ||
                        matched?.fields?.["Listing Name "] ||
                        matched?.fields?.["Apartment Name"] ||
                        matched?.fields?.["Apartment Name "] ||
                        matched?.fields?.["Apartment"] ||
                        matched?.fields?.["Listing"] ||
                        matched?.fields?.["Unit"] ||
                        direct ||
                        "-"
                      );
                    })()}
                  </MDBox>
                </MDBox>
              </MDBox>
            ) : (
              <MDBox display="flex" flexDirection="column" gap={0.75}>
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.03em",
                    color: "#334155",
                  }}
                >
                  Reservation ID
                </MDTypography>
                <MDInput
                  placeholder="Enter Hostaway Reservation ID..."
                  fullWidth
                  value={searchInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, "");
                    setSearchInput(val);
                    setSelectedResId(val);
                    if (val.length >= 7) {
                      handleOpenChargeModal(val);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchInput) {
                      handleOpenChargeModal(searchInput);
                    }
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "42px",
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                      border: "1px solid #cbd5e1",
                      fontSize: "0.875rem",
                      fontFamily: "'Inter', sans-serif",
                      color: "#0f172a",
                    },
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "& .MuiInputBase-input": { fontWeight: "600", padding: "10px 12px" },
                  }}
                />
              </MDBox>
            )}

            {/* PAGE 1: Financial & Payment Overview */}
            {chargeModalStep === 1 && (
              <>
                {loadingFinanceFields ? (
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    py={7}
                    sx={{ minHeight: "220px", backgroundColor: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0" }}
                  >
                    <CircularProgress size={38} sx={{ color: "#0284c7", mb: 2 }} />
                    <MDTypography variant="button" sx={{ color: "#64748b", fontSize: "0.85rem", fontWeight: 600 }}>
                      Loading Financial & Payment Details...
                    </MDTypography>
                  </MDBox>
                ) : (
                  <>
                    {/* Total Amount, Paid Amount, Pending Amount cards */}
                    {(() => {
                      const filteredFinanceFields = hostawayFinanceFields.filter((f) => {
                        if (f.isDeleted === 1 || f.isDeleted === "1" || f.isDeleted === true || f.raw?.isDeleted === 1 || f.raw?.isDeleted === "1") {
                          return false;
                        }
                        const c = `${f.concept || ""} ${f.name || ""} ${f.alias || ""} ${f.feeName || ""} ${f.type || ""}`.toLowerCase();
                        return (
                          !c.includes("total price") &&
                          !c.includes("totalprice") &&
                          !c.includes("total_price") &&
                          !c.includes("discount") &&
                          !c.includes("refund sum") &&
                          !c.includes("refundsum") &&
                          !c.includes("refund_sum") &&
                          !c.includes("refund excluded sum") &&
                          !c.includes("refundexcludedsum") &&
                          !c.includes("refund_excluded_sum") &&
                          !c.includes("host channel fee") &&
                          !c.includes("hostchannelfee") &&
                          !c.includes("host_channel_fee") &&
                          f.type !== "totals" &&
                          f.name !== "totalPriceFromChannel"
                        );
                      });

                      const refundTotal = hostawayPayments.reduce((sum, record) => {
                        const f = record.fields || record || {};
                        const statusVal = String(f.Status || f.status || "").toLowerCase().trim();
                        const typeVal = String(f.Type || f.type || "").toLowerCase().trim();
                        const descVal = String(f.Description || f.description || f.Title || f.title || "").toLowerCase();
                        const rawAmt = Number(String(f.Amount || f.amount || "0").replace(/[^0-9.-]/g, ""));
                        const isRefund = statusVal === "refunded" || typeVal === "refund" || descVal.includes("refund") || (!isNaN(rawAmt) && rawAmt < 0);
                        return sum + (isRefund ? Math.abs(isNaN(rawAmt) ? 0 : rawAmt) : 0);
                      }, 0);

                      const financeSum = filteredFinanceFields.reduce((sum, f) => sum + getFieldVal(f), 0);
                      const rawTotal = Number(hostawayTotalPrice || reservationDetails?.totalPrice || 0);
                      const baseTotal = rawTotal > 0 ? rawTotal : financeSum;
                      const totalVal = Math.max(0, baseTotal - refundTotal);

                      const paymentsSum = hostawayPayments.reduce((sum, record) => {
                        const fields = record.fields || record || {};
                        const amtVal = Number(String(fields.Amount || fields.amount || "0").replace(/[^0-9.-]/g, ""));
                        return sum + (isNaN(amtVal) ? 0 : amtVal);
                      }, 0);
                      const rawPaid = Number(hostawayPaidAmount || reservationDetails?.paidAmount || 0);
                      const paidVal = paymentsSum !== 0 ? paymentsSum : Math.max(0, rawPaid - refundTotal);
                      const pendingVal = totalVal > 0 ? Math.max(0, totalVal - paidVal) : 0;

                      return (
                        <MDBox display="flex" gap={1.5} flexWrap="wrap">
                          {/* Total Amount */}
                          <MDBox
                            p={1.5}
                            borderRadius="8px"
                            sx={{
                              flex: { xs: "1 1 100%", sm: "1 1 calc(33.33% - 10px)" },
                              minWidth: "130px",
                              backgroundColor: "#ffffff",
                              border: "1px solid #e2e8f0",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              sx={{
                                color: "#64748b",
                                fontSize: "0.72rem",
                                fontWeight: 400,
                                textTransform: "uppercase",
                                letterSpacing: "0.03em",
                                display: "block",
                                mb: 0.5,
                              }}
                            >
                              Total Amount
                            </MDTypography>
                            <MDTypography
                              variant="body2"
                              sx={{
                                color: "#0f172a",
                                fontSize: "1.05rem",
                                fontWeight: 400,
                                fontFamily: "'Inter', sans-serif",
                              }}
                            >
                              {currency === "PKR" ? `Rs. ${Math.round(totalVal).toLocaleString()}` : `$${totalVal.toFixed(2)}`}
                            </MDTypography>
                          </MDBox>

                          {/* Paid Amount (Only shown if greater than 0) */}
                          {paidVal > 0 && (
                            <MDBox
                              p={1.5}
                              borderRadius="8px"
                              sx={{
                                flex: { xs: "1 1 100%", sm: "1 1 calc(33.33% - 10px)" },
                                minWidth: "130px",
                                backgroundColor: "#ffffff",
                                border: "1px solid #e2e8f0",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                              }}
                            >
                              <MDTypography
                                variant="caption"
                                sx={{
                                  color: "#64748b",
                                  fontSize: "0.72rem",
                                  fontWeight: 400,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.03em",
                                  display: "block",
                                  mb: 0.5,
                                }}
                              >
                                Paid Amount
                              </MDTypography>
                              <MDTypography
                                variant="body2"
                                sx={{
                                  color: "#059669",
                                  fontSize: "1.05rem",
                                  fontWeight: 400,
                                  fontFamily: "'Inter', sans-serif",
                                }}
                              >
                                {currency === "PKR" ? `Rs. ${Math.round(paidVal).toLocaleString()}` : `$${paidVal.toFixed(2)}`}
                              </MDTypography>
                            </MDBox>
                          )}

                          {/* Pending Amount (Only shown if greater than 0) */}
                          {pendingVal > 0 && (
                            <MDBox
                              p={1.5}
                              borderRadius="8px"
                              sx={{
                                flex: { xs: "1 1 100%", sm: "1 1 calc(33.33% - 10px)" },
                                minWidth: "130px",
                                backgroundColor: "#ffffff",
                                border: "1px solid #e2e8f0",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                              }}
                            >
                              <MDTypography
                                variant="caption"
                                sx={{
                                  color: "#64748b",
                                  fontSize: "0.72rem",
                                  fontWeight: 400,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.03em",
                                  display: "block",
                                  mb: 0.5,
                                }}
                              >
                                Pending Amount
                              </MDTypography>
                              <MDTypography
                                variant="body2"
                                sx={{
                                  color: "#b45309",
                                  fontSize: "1.05rem",
                                  fontWeight: 400,
                                  fontFamily: "'Inter', sans-serif",
                                }}
                              >
                                {currency === "PKR" ? `Rs. ${Math.round(pendingVal).toLocaleString()}` : `$${pendingVal.toFixed(2)}`}
                              </MDTypography>
                            </MDBox>
                          )}
                        </MDBox>
                      );
                    })()}

                    {/* Financial Fields Table */}
                    {(() => {
                      const filteredFinanceFields = hostawayFinanceFields.filter((f) => {
                        if (f.isDeleted === 1 || f.isDeleted === "1" || f.isDeleted === true || f.raw?.isDeleted === 1 || f.raw?.isDeleted === "1") {
                          return false;
                        }
                        const c = `${f.concept || ""} ${f.name || ""} ${f.alias || ""} ${f.feeName || ""} ${f.type || ""}`.toLowerCase();
                        return (
                          !c.includes("total price") &&
                          !c.includes("totalprice") &&
                          !c.includes("total_price") &&
                          !c.includes("discount") &&
                          !c.includes("refund sum") &&
                          !c.includes("refundsum") &&
                          !c.includes("refund_sum") &&
                          !c.includes("refund excluded sum") &&
                          !c.includes("refundexcludedsum") &&
                          !c.includes("refund_excluded_sum") &&
                          !c.includes("host channel fee") &&
                          !c.includes("hostchannelfee") &&
                          !c.includes("host_channel_fee") &&
                          f.type !== "totals" &&
                          f.name !== "totalPriceFromChannel"
                        );
                      });

                      if (filteredFinanceFields.length === 0) return null;
                      const totalAmountSum = filteredFinanceFields.reduce((sum, f) => sum + getFieldVal(f), 0);

                      // Extract any refund records from deduplicated payments
                      const uniquePaymentsList = deduplicatePayments(hostawayPayments);
                      const refundPayments = uniquePaymentsList.filter((record) => {
                        const f = record.fields || record || {};
                        const statusVal = String(f.Status || f.status || "").toLowerCase().trim();
                        const typeVal = String(f.Type || f.type || "").toLowerCase().trim();
                        const descVal = String(f.Description || f.description || f.Title || f.title || "").toLowerCase();
                        const amtVal = Number(String(f.Amount || f.amount || "0").replace(/[^0-9.-]/g, ""));
                        return (
                          statusVal === "refunded" ||
                          typeVal === "refund" ||
                          descVal.includes("refund") ||
                          (!isNaN(amtVal) && amtVal < 0)
                        );
                      });

                      const refundTotalSum = refundPayments.reduce((sum, r) => {
                        const rf = r.fields || r || {};
                        const rawAmt = Number(String(rf.Amount || rf.amount || "0").replace(/[^0-9.-]/g, ""));
                        return sum + Math.abs(isNaN(rawAmt) ? 0 : rawAmt);
                      }, 0);

                      return (
                        <MDBox
                          p={2.5}
                          borderRadius="10px"
                          sx={{
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <MDTypography
                            variant="h6"
                            fontWeight="bold"
                            mb={2}
                            sx={{
                              fontFamily: "'Inter', sans-serif",
                              fontSize: "1rem",
                              fontWeight: 700,
                              color: "#0f172a",
                            }}
                          >
                            Financial Fields
                          </MDTypography>

                          <TableContainer sx={{ boxShadow: "none", overflowX: "auto" }}>
                            <Table sx={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, "& .MuiTableCell-root": { display: "table-cell !important", verticalAlign: "middle" } }}>
                              <TableHead sx={{ display: "table-header-group" }}>
                                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                                  <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>TITLE</TableCell>
                                  <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>UPSELL NAME</TableCell>
                                  <TableCell align="right" style={{ fontWeight: 500, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 12px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>AMOUNT</TableCell>
                                  <TableCell align="center" style={{ fontWeight: 500, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 12px", borderBottom: "1px solid #e2e8f0", width: "80px" }}>QUANTITY</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody sx={{ backgroundColor: "#ffffff" }}>
                                {filteredFinanceFields.map((f, idx) => (
                                  <TableRow key={f.id || idx} sx={{ "&:hover": { backgroundColor: "#f8fafc" }, borderBottom: "1px solid #f1f5f9" }}>
                                    <TableCell style={{ padding: "10px 12px", color: "#1e293b", fontWeight: 400, fontSize: "0.85rem", borderBottom: "1px solid #f1f5f9" }}>{f.concept || f.name || "-"}</TableCell>
                                    <TableCell style={{ padding: "10px 12px", color: "#334155", fontWeight: 400, fontSize: "0.84rem", borderBottom: "1px solid #f1f5f9" }}>{f.feeName || ""}</TableCell>
                                    <TableCell align="right" style={{ padding: "10px 12px", color: "#0891b2", fontWeight: 400, fontSize: "0.86rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>{getFieldVal(f).toFixed(2)}</TableCell>
                                    <TableCell align="center" style={{ padding: "10px 12px", color: "#334155", fontWeight: 400, fontSize: "0.85rem", borderBottom: "1px solid #f1f5f9" }}>{f.quantity ?? f.rawQuantity ?? 1}</TableCell>
                                  </TableRow>
                                ))}
                                {refundTotalSum > 0 && (
                                  <TableRow sx={{ "&:hover": { backgroundColor: "#f8fafc" }, borderBottom: "1px solid #f1f5f9" }}>
                                    <TableCell style={{ padding: "10px 12px", color: "#b91c1c", fontWeight: 400, fontSize: "0.85rem", borderBottom: "1px solid #f1f5f9" }}>Refunds</TableCell>
                                    <TableCell style={{ padding: "10px 12px", color: "#64748b", fontWeight: 400, fontSize: "0.84rem", borderBottom: "1px solid #f1f5f9" }} />
                                    <TableCell align="right" style={{ padding: "10px 12px", color: "#b91c1c", fontWeight: 400, fontSize: "0.86rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>-{refundTotalSum.toFixed(2)}</TableCell>
                                    <TableCell align="center" style={{ padding: "10px 12px", borderBottom: "1px solid #f1f5f9" }} />
                                  </TableRow>
                                )}
                              </TableBody>
                              <TableFooter sx={{ backgroundColor: "#ffffff" }}>
                                <TableRow sx={{ borderTop: "2px solid #e2e8f0" }}>
                                  <TableCell colSpan={2} style={{ fontWeight: 400, color: "#0f172a", fontSize: "0.88rem", padding: "12px 12px", borderBottom: "none" }}>
                                    Total price
                                  </TableCell>
                                  <TableCell align="right" style={{ fontWeight: 400, color: "#0891b2", fontSize: "0.92rem", padding: "12px 12px", whiteSpace: "nowrap", borderBottom: "none" }}>
                                    {Math.max(0, totalAmountSum - refundTotalSum).toFixed(2)}
                                  </TableCell>
                                  <TableCell style={{ padding: "12px 12px", borderBottom: "none" }} />
                                </TableRow>
                              </TableFooter>
                            </Table>
                          </TableContainer>
                        </MDBox>
                      );
                    })()}

                    {/* Payment Details Table */}
                    {(() => {
                      const uniquePayments = deduplicatePayments(hostawayPayments);
                      if (uniquePayments.length === 0) return null;
                      return (
                        <MDBox
                          p={2.5}
                          borderRadius="10px"
                          sx={{
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <MDTypography
                              variant="h6"
                              fontWeight="regular"
                              sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: "1rem",
                                fontWeight: 400,
                                color: "#0f172a",
                              }}
                            >
                              Payment Details
                            </MDTypography>
                            <Chip
                              label={`${uniquePayments.length} payment${uniquePayments.length > 1 ? "s" : ""}`}
                              size="small"
                              sx={{
                                backgroundColor: "#ecfdf5",
                                color: "#059669",
                                fontWeight: 400,
                                fontSize: "0.72rem",
                                height: "22px",
                                borderRadius: "6px",
                              }}
                            />
                          </MDBox>

                          <TableContainer sx={{ boxShadow: "none", overflowX: "auto" }}>
                            <Table sx={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, "& .MuiTableCell-root": { display: "table-cell !important", verticalAlign: "middle" } }}>
                              <TableHead sx={{ display: "table-header-group" }}>
                                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                                  <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>CHARGE ID</TableCell>
                                  <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>REFUND ID</TableCell>
                                  <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>TITLE</TableCell>
                                  <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>DESCRIPTION</TableCell>
                                  <TableCell style={{ fontWeight: 500, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 12px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>CHARGE DATE &amp; TIME</TableCell>
                                  <TableCell align="right" style={{ fontWeight: 500, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 12px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>AMOUNT</TableCell>
                                  <TableCell align="center" style={{ fontWeight: 500, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 12px", borderBottom: "1px solid #e2e8f0", width: "100px" }}>STATUS</TableCell>
                                  <TableCell align="center" style={{ fontWeight: 500, color: "#64748b", fontSize: "0.72rem", textTransform: "uppercase", padding: "10px 8px", borderBottom: "1px solid #e2e8f0", width: "42px" }}></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody sx={{ backgroundColor: "#ffffff" }}>
                                {uniquePayments.map((record, index) => {
                                  const fields = record.fields || record || {};
                                  const recCurrency = fields.Currency || fields.currency || currency || "PKR";
                                  const rawAmt = Number(String(fields.Amount || fields.amount || "0").replace(/[^0-9.-]/g, ""));
                                  let statusVal = fields.Status || fields.status || "-";
                                  if (typeof statusVal === "object" && statusVal !== null) {
                                    statusVal = statusVal.name || statusVal.value || String(statusVal);
                                  }
                                  let labelStr = String(statusVal);
                                  const typeStr = String(fields.Type || fields.type || "").toLowerCase().trim();
                                  const descStr = String(fields.Description || fields.description || fields.Title || fields.title || "").toLowerCase();
                                  const isRefund =
                                    typeStr === "refund" ||
                                    labelStr.toLowerCase() === "refunded" ||
                                    descStr.includes("refund") ||
                                    (!isNaN(rawAmt) && rawAmt < 0);

                                  if (isRefund && labelStr !== "Refunded") {
                                    labelStr = "Refunded";
                                  }

                                  const amtVal = isRefund && !isNaN(rawAmt) ? -Math.abs(rawAmt) : rawAmt;
                                  const displayAmt =
                                    isNaN(amtVal) || amtVal === 0
                                      ? "-"
                                      : recCurrency === "PKR"
                                        ? `Rs. ${amtVal.toLocaleString()}`
                                        : `$${amtVal.toFixed(2)}`;

                                  const isSuccess = ["paid", "successded", "succeeded"].includes(labelStr.toLowerCase());
                                  const originalChargeId = getOriginalChargeId(record, uniquePayments, dbChargeRecords);

                                  // Resolve Refund ID cleanly
                                  let refundIdVal = "-";
                                  let isSixDigitRefund = false;
                                  let isMoreThanSixRefund = false;
                                  if (isRefund) {
                                    // 0. Use backend-provided refundIdMap (most reliable)
                                    let pRefId = null;
                                    const recIdNum = Number(fields.id || record.id || fields.ID || record.ID || 0);
                                    const recIdStr = String(fields.id || record.id || fields.ID || record.ID || "").trim();
                                    if (recIdNum && hostawayRefundIdMap[recIdNum]) {
                                      pRefId = String(hostawayRefundIdMap[recIdNum]);
                                    } else if (recIdStr && hostawayRefundIdMap[recIdStr]) {
                                      pRefId = String(hostawayRefundIdMap[recIdStr]);
                                    }

                                    // 1. Direct "Refund Id" (from Teable or Hostaway enriched rows)
                                    if (!pRefId) {
                                      const rawRef =
                                        fields["Refund Id"] ||
                                        fields["Refund ID"] ||
                                        fields["refundId"] ||
                                        fields["refund_id"] ||
                                        fields["Refund id"] ||
                                        fields["RefundId"] ||
                                        record["Refund Id"] ||
                                        record["Refund ID"] ||
                                        record.refundId ||
                                        record.refund_id ||
                                        fields.paymentProviderRefundId ||
                                        record.paymentProviderRefundId ||
                                        fields.paymentProviderChargeId ||
                                        record.paymentProviderChargeId ||
                                        fields.refundInfo?.id ||
                                        record.refundInfo?.id ||
                                        fields.raw?.refundInfo?.id ||
                                        record.raw?.refundInfo?.id ||
                                        fields.raw?.paymentProviderChargeId ||
                                        record.raw?.paymentProviderChargeId ||
                                        fields.raw?.paymentProviderRefundId ||
                                        record.raw?.paymentProviderRefundId ||
                                        fields["Payment Provider Charge ID"] ||
                                        fields["Payment Provider Refund ID"] ||
                                        fields["paymentProviderChargeId"] ||
                                        fields["paymentProviderRefundId"];
                                      if (rawRef && String(rawRef).trim() !== "" && String(rawRef).trim() !== "-" && String(rawRef).trim() !== "null") {
                                        const cleanRef = String(rawRef).trim();
                                        pRefId = hostawayRefundIdMap[cleanRef] || hostawayRefundIdMap[Number(cleanRef)] || cleanRef;
                                      }
                                    }

                                    // 2. Extract from paymentProviderMessage e.g. "Payment Provider charge ID: 539149"
                                    if (!pRefId) {
                                      const msg = fields.paymentProviderMessage || record.paymentProviderMessage || fields.raw?.paymentProviderMessage || "";
                                      const match = String(msg).match(/Payment Provider charge ID:\s*([0-9]+)/i);
                                      if (match && match[1]) {
                                        pRefId = match[1];
                                      }
                                    }

                                    // 3. Look up from parent charge's refundIds -> paymentProviderRefundIds mapping
                                    if (!pRefId) {
                                      for (const p of uniquePayments) {
                                        const pf = p.fields || p.raw || p || {};
                                        const rIds = pf.refundIds || p.refundIds || (p.raw && p.raw.refundIds);
                                        const ppRIds = pf.paymentProviderRefundIds || p.paymentProviderRefundIds || (p.raw && p.raw.paymentProviderRefundIds);
                                        if (Array.isArray(rIds) && Array.isArray(ppRIds)) {
                                          const idx = rIds.indexOf(recIdNum);
                                          if (idx !== -1 && ppRIds[idx]) {
                                            pRefId = ppRIds[idx];
                                            break;
                                          }
                                        }
                                      }
                                    }

                                    // 4. Look up by parent positive charge sequence
                                    if (!pRefId) {
                                      const parentCharge = uniquePayments.find((p) => {
                                        const pf = p.fields || p.raw || p || {};
                                        const pid = String(pf.id || p.id || "").trim();
                                        return pid === String(originalChargeId);
                                      });
                                      if (parentCharge) {
                                        const pf = parentCharge.fields || parentCharge.raw || parentCharge || {};
                                        const ppRIds = pf.paymentProviderRefundIds || parentCharge.paymentProviderRefundIds || (parentCharge.raw && parentCharge.raw.paymentProviderRefundIds);
                                        const rIds = pf.refundIds || parentCharge.refundIds || (parentCharge.raw && parentCharge.raw.refundIds);
                                        if (Array.isArray(ppRIds) && ppRIds.length > 0) {
                                          if (Array.isArray(rIds)) {
                                            const rIndex = rIds.indexOf(recIdNum);
                                            if (rIndex !== -1 && ppRIds[rIndex]) {
                                              pRefId = ppRIds[rIndex];
                                            }
                                          }
                                          if (!pRefId) {
                                            const refundsUnderCharge = uniquePayments.filter((p) => {
                                              const f = p.fields || p.raw || p || {};
                                              const amt = Number(String(f.Amount || f.amount || "0").replace(/[^0-9.-]/g, ""));
                                              const sVal = String(f.Status || f.status || "").toLowerCase().trim();
                                              const tVal = String(f.Type || f.type || "").toLowerCase().trim();
                                              const dVal = String(f.Description || f.description || f.Title || f.title || "").toLowerCase();
                                              const isRef = tVal === "refund" || sVal === "refunded" || dVal.includes("refund") || (!isNaN(amt) && amt < 0);
                                              const origCId = getOriginalChargeId(p, uniquePayments, dbChargeRecords);
                                              return isRef && String(origCId) === String(originalChargeId);
                                            });
                                            const mySeqIndex = refundsUnderCharge.findIndex((p) => (p.id || p.fields?.id) === (record.id || fields.id));
                                            if (mySeqIndex !== -1 && ppRIds[mySeqIndex]) {
                                              pRefId = ppRIds[mySeqIndex];
                                            }
                                          }
                                        }
                                      }
                                    }

                                     if (pRefId && String(pRefId).trim() !== "" && String(pRefId) !== "0" && String(pRefId) !== "-") {
                                       const cleanCandidate = String(pRefId).trim();
                                       if (/^\d{6}$/.test(cleanCandidate)) {
                                         refundIdVal = cleanCandidate;
                                         isSixDigitRefund = true;
                                       } else if (cleanCandidate.length > 6) {
                                         const mapped = hostawayRefundIdMap[cleanCandidate] || hostawayRefundIdMap[Number(cleanCandidate)];
                                         if (mapped && /^\d{6}$/.test(String(mapped).trim())) {
                                           refundIdVal = String(mapped).trim();
                                           isSixDigitRefund = true;
                                         } else {
                                           refundIdVal = cleanCandidate;
                                           isMoreThanSixRefund = true;
                                         }
                                       } else {
                                         refundIdVal = cleanCandidate;
                                       }
                                     }
                                  }

                                  return (
                                    <TableRow key={record.id || fields.id || index} sx={{ "&:hover": { backgroundColor: "#f8fafc" }, borderBottom: "1px solid #f1f5f9" }}>
                                      <TableCell style={{ padding: "10px 12px", color: "#64748b", fontWeight: 400, fontSize: "0.82rem", borderBottom: "1px solid #f1f5f9" }}>{originalChargeId}</TableCell>
                                      <TableCell style={{ padding: "10px 12px", borderBottom: "1px solid #f1f5f9" }}>
                                        {isMoreThanSixRefund ? (
                                          <Tooltip title={`Error: #${refundIdVal} has more than 6 digits! Expected 6-digit provider ID. Click to re-verify from Hostaway.`} arrow>
                                            <Chip
                                              label={`Error: #${refundIdVal} (>6 digits)`}
                                              size="small"
                                              onClick={() => handleReverifyHostawayRefund(selectedResId || reservationDetails?.id)}
                                              sx={{
                                                fontSize: "0.68rem",
                                                height: "20px",
                                                fontWeight: 600,
                                                backgroundColor: "#fee2e2",
                                                color: "#b91c1c",
                                                border: "1px solid #fca5a5",
                                                cursor: "pointer",
                                                "&:hover": { backgroundColor: "#fecaca" }
                                              }}
                                            />
                                          </Tooltip>
                                        ) : (
                                          <span style={{
                                            color: isSixDigitRefund ? "#059669" : refundIdVal !== "-" ? "#b91c1c" : "#64748b",
                                            fontWeight: isSixDigitRefund ? 600 : refundIdVal !== "-" ? 500 : 400,
                                            fontSize: "0.82rem",
                                            fontFamily: isSixDigitRefund ? "'Roboto Mono', monospace" : "inherit"
                                          }}>
                                            {refundIdVal}
                                          </span>
                                        )}
                                      </TableCell>
                                      <TableCell style={{ padding: "10px 12px", color: "#0f172a", fontWeight: 400, fontSize: "0.84rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>{fields.Title || fields.title || "-"}</TableCell>
                                      <TableCell style={{ padding: "10px 12px", color: "#475569", fontWeight: 400, fontSize: "0.82rem", borderBottom: "1px solid #f1f5f9" }}>{fields.Description || fields.description || "-"}</TableCell>
                                      <TableCell style={{ padding: "10px 12px", color: "#64748b", fontWeight: 400, fontSize: "0.8rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>{formatPakistanDateTime(fields.date || fields.chargeDate || fields["Charge Date"] || fields.dateTime || fields.date_time)}</TableCell>
                                      <TableCell align="right" style={{ padding: "10px 12px", color: isRefund ? "#b91c1c" : "#059669", fontWeight: 400, fontSize: "0.86rem", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>{displayAmt}</TableCell>
                                      <TableCell align="center" style={{ padding: "10px 12px", borderBottom: "1px solid #f1f5f9" }}>
                                        <Chip
                                          label={labelStr}
                                          size="small"
                                          sx={{
                                            fontSize: "0.68rem",
                                            height: "20px",
                                            fontWeight: 400,
                                            backgroundColor: isRefund ? "#fee2e2" : isSuccess ? "#dcfce7" : "#fef3c7",
                                            color: isRefund ? "#b91c1c" : isSuccess ? "#15803d" : "#b45309",
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="center" style={{ padding: "6px 8px", borderBottom: "1px solid #f1f5f9", width: "42px" }}>
                                        {!isRefund && amtVal > 0 && (
                                          <Tooltip title="Refund Security Deposit" arrow>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleRowRefundClick(record)}
                                              sx={{
                                                color: "#64748b",
                                                p: 0.5,
                                                "&:hover": {
                                                  backgroundColor: "#fff1f2",
                                                  color: "#e11d48",
                                                },
                                              }}
                                            >
                                              <Icon sx={{ fontSize: "1.2rem !important" }}>more_vert</Icon>
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                              <TableFooter sx={{ backgroundColor: "#ffffff" }}>
                                <TableRow sx={{ borderTop: "2px solid #e2e8f0" }}>
                                  <TableCell colSpan={5} style={{ fontWeight: 400, color: "#0f172a", fontSize: "0.88rem", padding: "12px 12px", borderBottom: "none" }}>
                                    Total Amount
                                  </TableCell>
                                  <TableCell align="right" style={{ fontWeight: 400, color: "#059669", fontSize: "0.92rem", padding: "12px 12px", whiteSpace: "nowrap", borderBottom: "none" }}>
                                    {(() => {
                                      const totalAmount = uniquePayments.reduce((sum, record) => {
                                        const fields = record.fields || record || {};
                                        const amtVal = Number(String(fields.Amount || fields.amount || "0").replace(/[^0-9.-]/g, ""));
                                        return sum + (isNaN(amtVal) ? 0 : amtVal);
                                      }, 0);
                                      return currency === "PKR" ? `Rs. ${totalAmount.toLocaleString()}` : `$${totalAmount.toFixed(2)}`;
                                    })()}
                                  </TableCell>
                                  <TableCell style={{ padding: "12px 12px", borderBottom: "none" }} colSpan={2} />
                                </TableRow>
                              </TableFooter>
                            </Table>
                          </TableContainer>
                        </MDBox>
                      );
                    })()}
                  </>
                )}
              </>
            )}

            {/* PAGE 2: Add Charge Section */}
            {chargeModalStep === 2 && (
              <>
                {/* Transaction Type / Account (Manual Entry + Dropdown) */}
                <MDBox display="flex" flexDirection="column" gap={0.75}>
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      color: "#334155",
                    }}
                  >
                    Transaction Type <span style={{ color: "#e11d48" }}>*</span>
                  </MDTypography>
                  <Autocomplete
                    freeSolo
                    openOnFocus
                    options={["CIH-BNR", "MZN-BNR01", "KEENU-BNR"]}
                    value={selectedChargeName || ""}
                    inputValue={selectedChargeName || ""}
                    onInputChange={(event, newInputValue) => {
                      setSelectedChargeName(newInputValue);
                    }}
                    onChange={(event, newValue) => {
                      const val = typeof newValue === "string" ? newValue : (newValue || "");
                      setSelectedChargeName(val);
                    }}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        placeholder="Select Transaction Type..."
                        fullWidth
                        sx={{
                          "& .MuiInputBase-root": {
                            height: "42px",
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                            border: "1px solid #cbd5e1",
                            fontSize: "0.875rem",
                            fontFamily: "'Inter', sans-serif",
                            color: "#0f172a",
                            padding: "0 8px !important",
                          },
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                          "& .MuiInputBase-input": { fontWeight: "400", padding: "10px 4px !important" },
                        }}
                      />
                    )}
                  />
                </MDBox>

                {/* Amount Input */}
                <MDBox display="flex" flexDirection="column" gap={0.75}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                          color: "#334155",
                        }}
                      >
                        Amount
                      </MDTypography>
                      {(() => {
                        const filteredFinanceFields = hostawayFinanceFields.filter((f) => {
                          if (f.isDeleted === 1 || f.isDeleted === "1" || f.isDeleted === true || f.raw?.isDeleted === 1 || f.raw?.isDeleted === "1") return false;
                          const c = `${f.concept || ""} ${f.name || ""} ${f.alias || ""} ${f.feeName || ""} ${f.type || ""}`.toLowerCase();
                          return (
                            !c.includes("total price") &&
                            !c.includes("totalprice") &&
                            !c.includes("total_price") &&
                            !c.includes("discount") &&
                            !c.includes("refund sum") &&
                            !c.includes("refundsum") &&
                            !c.includes("refund_sum") &&
                            !c.includes("refund excluded sum") &&
                            !c.includes("refundexcludedsum") &&
                            !c.includes("refund_excluded_sum") &&
                            !c.includes("host channel fee") &&
                            !c.includes("hostchannelfee") &&
                            !c.includes("host_channel_fee") &&
                            f.type !== "totals" &&
                            f.name !== "totalPriceFromChannel"
                          );
                        });
                        const refundTotal = hostawayPayments.reduce((sum, record) => {
                          const f = record.fields || record || {};
                          const statusVal = String(f.Status || f.status || "").toLowerCase().trim();
                          const typeVal = String(f.Type || f.type || "").toLowerCase().trim();
                          const descVal = String(f.Description || f.description || f.Title || f.title || "").toLowerCase();
                          const rawAmt = Number(String(f.Amount || f.amount || "0").replace(/[^0-9.-]/g, ""));
                          const isRefund = statusVal === "refunded" || typeVal === "refund" || descVal.includes("refund") || (!isNaN(rawAmt) && rawAmt < 0);
                          return sum + (isRefund ? Math.abs(isNaN(rawAmt) ? 0 : rawAmt) : 0);
                        }, 0);

                        const financeSum = filteredFinanceFields.reduce((sum, f) => sum + getFieldVal(f), 0);
                        const rawTotal = Number(hostawayTotalPrice || reservationDetails?.totalPrice || 0);
                        const baseTotal = rawTotal > 0 ? rawTotal : financeSum;
                        const totalVal = Math.max(0, baseTotal - refundTotal);

                        const paymentsSum = hostawayPayments.reduce((sum, record) => {
                          const fields = record.fields || record || {};
                          const amtVal = Number(String(fields.Amount || fields.amount || "0").replace(/[^0-9.-]/g, ""));
                          return sum + (isNaN(amtVal) ? 0 : amtVal);
                        }, 0);
                        const rawPaid = Number(hostawayPaidAmount || reservationDetails?.paidAmount || 0);
                        const paidVal = paymentsSum !== 0 ? paymentsSum : Math.max(0, rawPaid - refundTotal);
                        const pendingVal = totalVal > 0 ? Math.max(0, totalVal - paidVal) : 0;

                        const isDollar = (currency || "").toUpperCase() === "USD";
                        if (pendingVal > 0) {
                          const formattedPending = isDollar ? `$${Number(pendingVal).toFixed(2)}` : `Rs. ${Math.round(pendingVal).toLocaleString()}`;
                          return (
                            <Chip
                              label={`Pending: ${formattedPending}`}
                              size="small"
                              onClick={() => setChargeAmountInput(isDollar ? Number(pendingVal).toFixed(2) : String(Math.round(pendingVal)))}
                              sx={{
                                fontWeight: "600",
                                fontSize: "0.72rem",
                                height: "20px",
                                backgroundColor: "#fef3c7",
                                color: "#b45309",
                                borderRadius: "6px",
                                cursor: "pointer",
                                "&:hover": { backgroundColor: "#fde68a" },
                              }}
                            />
                          );
                        }
                        return null;
                      })()}
                    </MDBox>
                    <Chip
                      label={currency || "PKR"}
                      size="small"
                      sx={{
                        fontWeight: "700",
                        fontSize: "0.72rem",
                        height: "20px",
                        backgroundColor: "#e0f2fe",
                        color: "#0369a1",
                        borderRadius: "6px",
                      }}
                    />
                  </MDBox>
                  <MDInput
                    type="number"
                    placeholder="0.00"
                    fullWidth
                    inputProps={{ min: "0", step: "any" }}
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "e") e.preventDefault();
                    }}
                    onFocus={(e) => e.target.select()}
                    value={chargeAmountInput}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (val.length > 1 && val.startsWith("0") && val[1] !== ".") {
                        val = val.replace(/^0+/, "");
                      }
                      if (!val.includes("-")) setChargeAmountInput(val);
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "42px",
                        borderRadius: "8px",
                        backgroundColor: "#ffffff",
                        border: "1px solid #cbd5e1",
                        fontSize: "0.875rem",
                        fontFamily: "'Inter', sans-serif",
                        color: "#0f172a",
                      },
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "& .MuiInputBase-input": { fontWeight: "600", padding: "10px 12px" },
                    }}
                  />
                </MDBox>

                {/* Description */}
                <MDBox display="flex" flexDirection="column" gap={0.75}>
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      color: "#334155",
                    }}
                  >
                    Description
                  </MDTypography>
                  <MDInput
                    multiline
                    rows={3}
                    placeholder="Enter transaction details or notes..."
                    fullWidth
                    value={chargeDescription}
                    onChange={(e) => {
                      setChargeDescription(e.target.value);
                      if (e.target.value.trim()) setChargeErrorToast("");
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: "8px",
                        backgroundColor: "#ffffff",
                        border: "1px solid #cbd5e1",
                        fontSize: "0.875rem",
                        fontFamily: "'Inter', sans-serif",
                        color: "#0f172a",
                      },
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "& .MuiInputBase-input": { padding: "10px 12px" },
                    }}
                  />
                </MDBox>

                {/* Toast Error Alert Banner */}
                {chargeErrorToast && (
                  <MDBox
                    p={1.5}
                    borderRadius="8px"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      backgroundColor: "#fde8e8",
                      border: "1px solid #f8b4b4",
                      color: "#9b1c1c",
                      fontSize: "0.825rem",
                      fontWeight: "600",
                    }}
                  >
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Icon color="error" sx={{ fontSize: "1.1rem !important" }}>error</Icon>
                      <span>{chargeErrorToast}</span>
                    </MDBox>
                    <Icon
                      sx={{ cursor: "pointer", fontSize: "1rem !important", color: "#9b1c1c" }}
                      onClick={() => setChargeErrorToast("")}
                    >
                      close
                    </Icon>
                  </MDBox>
                )}
              </>
            )}

            {/* PAGE 3: Security Deposit Refund Form (Zero Blink Seamless Transition) */}
            {chargeModalStep === 3 && (
              <>
                {/* Field 1: Charge ID (Constant / Read-Only) */}
                <MDBox display="flex" flexDirection="column" gap={0.75}>
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      color: "#334155",
                    }}
                  >
                    Charge ID <span style={{ color: "#e11d48" }}>*</span>
                  </MDTypography>
                  <MDInput
                    fullWidth
                    value={refundDetectedChargeId || ""}
                    disabled
                    inputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "42px",
                        borderRadius: "8px",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #cbd5e1",
                        fontSize: "0.875rem",
                        fontFamily: "'Inter', sans-serif",
                        color: "#334155 !important",
                        padding: "0 12px !important",
                        cursor: "not-allowed",
                      },
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "& .MuiInputBase-input": {
                        fontWeight: "500",
                        color: "#334155 !important",
                        WebkitTextFillColor: "#334155 !important",
                        padding: "10px 0 !important",
                        cursor: "not-allowed",
                      },
                    }}
                  />
                </MDBox>

                {/* Field 2: Transaction Account (Manual Entry + Dropdown) */}
                <MDBox display="flex" flexDirection="column" gap={0.75}>
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      color: "#334155",
                    }}
                  >
                    Transaction Type <span style={{ color: "#e11d48" }}>*</span>
                  </MDTypography>
                  <Autocomplete
                    freeSolo
                    openOnFocus
                    options={["CIH-BNR", "MZN-BNR01"]}
                    value={refundChargeId || ""}
                    inputValue={refundChargeId || ""}
                    onInputChange={(event, newInputValue) => {
                      setRefundChargeId(newInputValue);
                      setRefundTitle(newInputValue);
                    }}
                    onChange={(event, newValue) => {
                      const val = typeof newValue === "string" ? newValue : (newValue || "");
                      setRefundChargeId(val);
                      setRefundTitle(val);
                    }}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        placeholder="Select Transaction Type..."
                        fullWidth
                        sx={{
                          "& .MuiInputBase-root": {
                            height: "42px",
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                            border: "1px solid #cbd5e1",
                            fontSize: "0.875rem",
                            fontFamily: "'Inter', sans-serif",
                            color: "#0f172a",
                            padding: "0 8px !important",
                          },
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                          "& .MuiInputBase-input": { fontWeight: "400", padding: "10px 4px !important" },
                        }}
                      />
                    )}
                  />
                </MDBox>

                {/* Field 3: Refund Amount */}
                <MDBox display="flex" flexDirection="column" gap={0.75}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                          color: "#334155",
                        }}
                      >
                        Refund Amount <span style={{ color: "#e11d48" }}>*</span>
                      </MDTypography>
                    </MDBox>
                    <Chip
                      label={currency || "PKR"}
                      size="small"
                      sx={{
                        fontWeight: "700",
                        fontSize: "0.72rem",
                        height: "20px",
                        backgroundColor: "#e0f2fe",
                        color: "#0369a1",
                        borderRadius: "6px",
                      }}
                    />
                  </MDBox>
                  <MDInput
                    type="number"
                    placeholder="0.00"
                    fullWidth
                    inputProps={{ min: "0", step: "any" }}
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "e") e.preventDefault();
                    }}
                    onFocus={(e) => e.target.select()}
                    value={refundAmountInput}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (val.length > 1 && val.startsWith("0") && val[1] !== ".") {
                        val = val.replace(/^0+/, "");
                      }
                      if (!val.includes("-")) {
                        setRefundAmountInput(val);
                        const num = parseFloat(val);
                        if (num === 0 || !val.trim()) {
                          setRefundErrorToast("Please enter amount");
                        } else if (num > 0) {
                          if (refundErrorToast === "Please enter amount" || refundErrorToast.includes("Refund cannot be processed")) {
                            setRefundErrorToast("");
                          }
                        }
                      }
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "42px",
                        borderRadius: "8px",
                        backgroundColor: "#ffffff",
                        border: "1px solid #cbd5e1",
                        fontSize: "0.875rem",
                        fontFamily: "'Inter', sans-serif",
                        color: "#0f172a",
                      },
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "& .MuiInputBase-input": { fontWeight: "400", padding: "10px 12px" },
                    }}
                  />
                </MDBox>

                {/* Field 4: Reason */}
                <MDBox display="flex" flexDirection="column" gap={0.75}>
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      color: "#334155",
                    }}
                  >
                    Reason <span style={{ color: "#e11d48" }}>*</span>
                  </MDTypography>
                  <FormControl fullWidth size="small">
                    <Select
                      displayEmpty
                      value={refundReason}
                      onChange={(e) => {
                        setRefundReason(e.target.value);
                        if (e.target.value) setRefundErrorToast("");
                      }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: "#94a3b8", fontWeight: 400 }}>Select a Reason...</span>;
                        }
                        return selected;
                      }}
                      sx={{
                        height: "42px",
                        borderRadius: "8px",
                        fontWeight: "400",
                        fontSize: "0.875rem",
                        fontFamily: "'Inter', sans-serif",
                        backgroundColor: "#ffffff",
                        border: "1px solid #cbd5e1",
                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em style={{ color: "#94a3b8", fontStyle: "normal" }}>Select a Reason...</em>
                      </MenuItem>
                      <MenuItem value="Duplicate">Duplicate</MenuItem>
                      <MenuItem value="Fraudulent">Fraudulent</MenuItem>
                      <MenuItem value="Requested by customer">Requested by customer</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Field 5: Description Notes */}
                <MDBox display="flex" flexDirection="column" gap={0.75}>
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      color: "#334155",
                    }}
                  >
                    Description Notes <span style={{ color: "#e11d48" }}>*</span>
                  </MDTypography>
                  <MDInput
                    multiline
                    rows={3}
                    placeholder="Enter refund notes, bank transfer details, or deductions..."
                    fullWidth
                    value={refundDescription}
                    onChange={(e) => {
                      setRefundDescription(e.target.value);
                      if (e.target.value.trim()) setRefundErrorToast("");
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: "8px",
                        backgroundColor: "#ffffff",
                        border: "1px solid #cbd5e1",
                        fontSize: "0.875rem",
                        fontFamily: "'Inter', sans-serif",
                        color: "#0f172a",
                        p: 1.2,
                      },
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    }}
                  />
                </MDBox>

                {/* Include in price breakdown Checkbox */}
                <MDBox display="flex" alignItems="center" mt={0.25}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeInPriceBreakdown}
                        onChange={(e) => setIncludeInPriceBreakdown(e.target.checked)}
                        sx={{
                          color: "#94a3b8",
                          "&.Mui-checked": { color: "#0284c7" },
                          padding: "4px 8px 4px 0",
                        }}
                      />
                    }
                    label={
                      <MDBox display="flex" alignItems="center" gap={0.5}>
                        <MDTypography sx={{ fontSize: "0.85rem", color: "#334155", fontWeight: 400, fontFamily: "'Inter', sans-serif" }}>
                          Include in price breakdown
                        </MDTypography>
                        <Tooltip title="Include this transaction in the guest's financial price breakdown in Hostaway">
                          <Icon sx={{ fontSize: "1.05rem !important", color: "#64748b", cursor: "pointer", ml: 0.25 }}>info_outline</Icon>
                        </Tooltip>
                      </MDBox>
                    }
                  />
                </MDBox>

                {/* Error Toast */}
                {refundErrorToast && (
                  <MDBox
                    p={1.25}
                    borderRadius="8px"
                    sx={{
                      backgroundColor: "#fee2e2",
                      border: "1px solid #fca5a5",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Icon sx={{ color: "#dc2626", fontSize: "1rem !important" }}>error</Icon>
                    <MDTypography variant="caption" sx={{ color: "#b91c1c", fontWeight: 600 }}>
                      {refundErrorToast}
                    </MDTypography>
                  </MDBox>
                )}
              </>
            )}
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ p: "16px 24px", backgroundColor: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.25 }}>
          {chargeModalStep === 1 ? (
            <>
              {/* 1. Back */}
              <MDButton
                variant="outlined"
                onClick={() => setOpenChargeModal(false)}
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  backgroundColor: "#ffffff",
                  color: "#334155 !important",
                  borderColor: "#e2e8f0 !important",
                  padding: "9px 16px",
                  fontSize: "0.825rem",
                  fontWeight: "600",
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                    color: "#0f172a !important",
                  },
                }}
              >
                Back
              </MDButton>

              {/* 2. Add Charge */}
              <MDButton
                variant="contained"
                onClick={() => {
                  // If chargeAmountInput is empty or 0, auto-fill with pending amount
                  const filteredFinanceFields = hostawayFinanceFields.filter((f) => {
                    if (f.isDeleted === 1 || f.isDeleted === "1" || f.isDeleted === true || f.raw?.isDeleted === 1 || f.raw?.isDeleted === "1") return false;
                    const c = `${f.concept || ""} ${f.name || ""} ${f.alias || ""} ${f.feeName || ""} ${f.type || ""}`.toLowerCase();
                    return (
                      !c.includes("total price") &&
                      !c.includes("totalprice") &&
                      !c.includes("total_price") &&
                      !c.includes("discount") &&
                      !c.includes("refund sum") &&
                      !c.includes("refundsum") &&
                      !c.includes("refund_sum") &&
                      !c.includes("refund excluded sum") &&
                      !c.includes("refundexcludedsum") &&
                      !c.includes("refund_excluded_sum") &&
                      !c.includes("host channel fee") &&
                      !c.includes("hostchannelfee") &&
                      !c.includes("host_channel_fee") &&
                      f.type !== "totals" &&
                      f.name !== "totalPriceFromChannel"
                    );
                  });
                  const refundTotal = hostawayPayments.reduce((sum, record) => {
                    const f = record.fields || record || {};
                    const statusVal = String(f.Status || f.status || "").toLowerCase().trim();
                    const typeVal = String(f.Type || f.type || "").toLowerCase().trim();
                    const descVal = String(f.Description || f.description || f.Title || f.title || "").toLowerCase();
                    const rawAmt = Number(String(f.Amount || f.amount || "0").replace(/[^0-9.-]/g, ""));
                    const isRefund = statusVal === "refunded" || typeVal === "refund" || descVal.includes("refund") || (!isNaN(rawAmt) && rawAmt < 0);
                    return sum + (isRefund ? Math.abs(isNaN(rawAmt) ? 0 : rawAmt) : 0);
                  }, 0);

                  const financeSum = filteredFinanceFields.reduce((sum, f) => sum + getFieldVal(f), 0);
                  const rawTotal = Number(hostawayTotalPrice || reservationDetails?.totalPrice || 0);
                  const baseTotal = rawTotal > 0 ? rawTotal : financeSum;
                  const totalVal = Math.max(0, baseTotal - refundTotal);

                  const paymentsSum = hostawayPayments.reduce((sum, record) => {
                    const fields = record.fields || record || {};
                    const amtVal = Number(String(fields.Amount || fields.amount || "0").replace(/[^0-9.-]/g, ""));
                    return sum + (isNaN(amtVal) ? 0 : amtVal);
                  }, 0);
                  const rawPaid = Number(hostawayPaidAmount || reservationDetails?.paidAmount || 0);
                  const paidVal = paymentsSum !== 0 ? paymentsSum : Math.max(0, rawPaid - refundTotal);
                  const pendingVal = totalVal > 0 ? Math.max(0, totalVal - paidVal) : 0;

                  const isDollar = (currency || "").toUpperCase() === "USD";
                  if ((!chargeAmountInput || chargeAmountInput === "0" || chargeAmountInput === "") && pendingVal > 0) {
                    setChargeAmountInput(isDollar ? Number(pendingVal).toFixed(2) : String(Math.round(pendingVal)));
                  }
                  setChargeModalStep(2);
                }}
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  backgroundColor: "#000000 !important",
                  color: "#ffffff !important",
                  borderColor: "#000000 !important",
                  padding: "9px 18px",
                  fontSize: "0.825rem",
                  fontWeight: "600",
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  "&:hover": {
                    backgroundColor: "#1e293b !important",
                  },
                }}
              >
                <Icon sx={{ fontSize: "1.05rem !important" }}>add</Icon>
                Add Charge
              </MDButton>
            </>
          ) : chargeModalStep === 2 ? (
            <>
              {/* Step 2 Buttons: Back, Refunded Security Deposit, Add Charge */}
              <MDButton
                variant="outlined"
                onClick={() => setChargeModalStep(1)}
                disabled={isSavingCharge}
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  backgroundColor: "#ffffff",
                  color: "#334155 !important",
                  borderColor: "#e2e8f0 !important",
                  padding: "9px 16px",
                  fontSize: "0.825rem",
                  fontWeight: "600",
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                    color: "#0f172a !important",
                  },
                }}
              >
                Back
              </MDButton>

              <MDButton
                variant="contained"
                onClick={handleSaveChargeModal}
                disabled={isSavingCharge}
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  backgroundColor: "#000000 !important",
                  color: "#ffffff !important",
                  borderColor: "#000000 !important",
                  padding: "9px 18px",
                  fontSize: "0.825rem",
                  fontWeight: "600",
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  "&:hover": {
                    backgroundColor: "#1e293b !important",
                  },
                }}
              >
                {isSavingCharge && <CircularProgress size={16} color="inherit" />}
                {isSavingCharge ? "Adding Charge..." : "Add Charge"}
              </MDButton>
            </>
          ) : (
            <>
              {/* Step 3 Buttons: Back, Process Refund */}
              <MDButton
                variant="outlined"
                onClick={() => setChargeModalStep(1)}
                disabled={isSavingRefund}
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  backgroundColor: "#ffffff",
                  color: "#334155 !important",
                  borderColor: "#e2e8f0 !important",
                  padding: "9px 16px",
                  fontSize: "0.825rem",
                  fontWeight: "600",
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                    color: "#0f172a !important",
                  },
                }}
              >
                Back
              </MDButton>

              <MDButton
                variant="contained"
                onClick={handleSaveRefundModal}
                disabled={isSavingRefund}
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  backgroundColor: "#000000 !important",
                  color: "#ffffff !important",
                  borderColor: "#000000 !important",
                  padding: "9px 18px",
                  fontSize: "0.825rem",
                  fontWeight: "600",
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  "&:hover": {
                    backgroundColor: "#1e293b !important",
                  },
                }}
              >
                {isSavingRefund && <CircularProgress size={16} color="inherit" />}
                {isSavingRefund ? `Processing (${refundElapsedSeconds}s)...` : "Process Refund"}
              </MDButton>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ARE YOU SURE YOU WANT TO PROCEED WITH REFUND? CONFIRMATION POPUP MODAL */}
      <Dialog
        open={openRefundConfirmModal}
        onClose={isSavingRefund ? undefined : () => setOpenRefundConfirmModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            m: { xs: 2, sm: 3 },
            boxShadow: "0 20px 25px -5px rgba(15, 23, 42, 0.08), 0 8px 10px -6px rgba(15, 23, 42, 0.04)",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
          },
        }}
      >
        <DialogTitle component="div" sx={{ p: "24px 28px 16px" }}>
          <MDTypography
            variant="h5"
            fontWeight="bold"
            sx={{
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              fontSize: "1.25rem",
              color: "#1e3a5f",
              fontWeight: 700,
            }}
          >
            Refund Security Deposit
          </MDTypography>
        </DialogTitle>

        <DialogContent sx={{ p: "0 28px 16px" }}>
          {isSavingRefund ? (
            <MDBox
              p={2.5}
              borderRadius="12px"
              sx={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 1.5,
              }}
            >
              <CircularProgress size={36} sx={{ color: "#556b46" }} />
              <MDBox>
                <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1e293b", fontSize: "1rem", mb: 0.5 }}>
                  Processing Refund in Hostaway...
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                  {refundElapsedSeconds < 5
                    ? "Connecting to Hostaway automation service..."
                    : refundElapsedSeconds < 15
                      ? "Navigating reservation and locating security deposit..."
                      : refundElapsedSeconds < 30
                        ? "Executing refund transaction in Hostaway..."
                        : "Finalizing verification with Hostaway..."}
                </MDTypography>
              </MDBox>
              <MDBox
                px={2}
                py={0.75}
                borderRadius="20px"
                sx={{
                  backgroundColor: "#e2e8f0",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <Icon sx={{ fontSize: "14px !important", color: "#334155" }}>timer</Icon>
                <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#0f172a", fontFamily: "monospace", fontSize: "0.825rem" }}>
                  Time elapsed: {Math.floor(refundElapsedSeconds / 60) > 0 ? `${Math.floor(refundElapsedSeconds / 60)}m ` : ""}{refundElapsedSeconds % 60}s
                </MDTypography>
              </MDBox>
              <MDTypography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.75rem" }}>
                ⚠️ Please do not close or refresh this page.
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox
              p={2}
              borderRadius="10px"
              sx={{
                backgroundColor: "#fff7ed",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Icon sx={{ color: "#f97316", fontSize: "1.4rem !important", flexShrink: 0 }}>
                warning_amber
              </Icon>
              <MDTypography
                sx={{
                  color: "#64748b",
                  fontSize: "0.95rem",
                  fontFamily: "'Inter', sans-serif",
                  lineHeight: 1.4,
                }}
              >
                Are you sure you want to proceed with Refund?
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>

        <DialogActions sx={{ p: "16px 28px 24px", justifyContent: "flex-end", gap: 2 }}>
          <MDButton
            variant="text"
            onClick={() => setOpenRefundConfirmModal(false)}
            disabled={isSavingRefund}
            sx={{
              fontFamily: "'Inter', sans-serif",
              color: "#64748b !important",
              fontSize: "0.875rem",
              fontWeight: 600,
              textTransform: "none",
              padding: "8px 16px",
              "&:hover": {
                color: "#1e3a5f !important",
                backgroundColor: "transparent",
              },
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            onClick={handleExecuteRefund}
            disabled={isSavingRefund}
            sx={{
              fontFamily: "'Inter', sans-serif",
              backgroundColor: "#556b46 !important",
              color: "#ffffff !important",
              borderColor: "#556b46 !important",
              padding: "9px 24px",
              fontSize: "0.875rem",
              fontWeight: 600,
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "none",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              "&:hover": {
                backgroundColor: "#445638 !important",
              },
            }}
          >
            {isSavingRefund && <CircularProgress size={16} color="inherit" />}
            {isSavingRefund ? `Processing (${refundElapsedSeconds}s)...` : "Confirm Refund"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ARE YOU SURE YOU WANT TO PROCEED WITH ADD UPSELL? CONFIRMATION POPUP MODAL */}
      <Dialog
        open={openUpsellConfirmModal}
        onClose={isSavingUpsell ? undefined : () => setOpenUpsellConfirmModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            m: { xs: 2, sm: 3 },
            boxShadow: "0 20px 25px -5px rgba(15, 23, 42, 0.08), 0 8px 10px -6px rgba(15, 23, 42, 0.04)",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
          },
        }}
      >
        <DialogTitle component="div" sx={{ p: "24px 28px 16px" }}>
          <MDTypography
            variant="h5"
            fontWeight="bold"
            sx={{
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              fontSize: "1.25rem",
              color: "#1e3a5f",
              fontWeight: 700,
            }}
          >
            Add Upsell Item
          </MDTypography>
        </DialogTitle>

        <DialogContent sx={{ p: "0 28px 16px" }}>
          <MDBox
            p={2}
            borderRadius="10px"
            sx={{
              backgroundColor: "#f0fdf4",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              border: "1px solid #bbf7d0",
            }}
          >
            <Icon sx={{ color: "#16a34a", fontSize: "1.4rem !important", flexShrink: 0 }}>
              help_outline
            </Icon>
            <MDTypography
              sx={{
                color: "#166534",
                fontSize: "0.95rem",
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1.4,
                fontWeight: 500,
              }}
            >
              Are you sure you want to proceed with Add Upsell?
            </MDTypography>
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ p: "16px 28px 24px", justifyContent: "flex-end", gap: 2 }}>
          <MDButton
            variant="text"
            onClick={() => setOpenUpsellConfirmModal(false)}
            disabled={isSavingUpsell}
            sx={{
              fontFamily: "'Inter', sans-serif",
              color: "#64748b !important",
              fontSize: "0.875rem",
              fontWeight: 600,
              textTransform: "none",
              padding: "8px 16px",
              "&:hover": {
                color: "#1e3a5f !important",
                backgroundColor: "transparent",
              },
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            onClick={handleExecuteUpsell}
            disabled={isSavingUpsell}
            sx={{
              fontFamily: "'Inter', sans-serif",
              backgroundColor: "#000000 !important",
              color: "#ffffff !important",
              borderColor: "#000000 !important",
              padding: "9px 24px",
              fontSize: "0.875rem",
              fontWeight: 600,
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "none",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              "&:hover": {
                backgroundColor: "#1e293b !important",
              },
            }}
          >
            {isSavingUpsell && <CircularProgress size={16} color="inherit" />}
            {isSavingUpsell ? "Adding Upsell..." : "Confirm Add Upsell"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ARE YOU SURE YOU WANT TO PROCEED WITH ADD CHARGE? CONFIRMATION POPUP MODAL */}
      <Dialog
        open={openChargeConfirmModal}
        onClose={isSavingCharge ? undefined : () => setOpenChargeConfirmModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            m: { xs: 2, sm: 3 },
            boxShadow: "0 20px 25px -5px rgba(15, 23, 42, 0.08), 0 8px 10px -6px rgba(15, 23, 42, 0.04)",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
          },
        }}
      >
        <DialogTitle component="div" sx={{ p: "24px 28px 16px" }}>
          <MDTypography
            variant="h5"
            fontWeight="bold"
            sx={{
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              fontSize: "1.25rem",
              color: "#1e3a5f",
              fontWeight: 700,
            }}
          >
            Add Charge
          </MDTypography>
        </DialogTitle>

        <DialogContent sx={{ p: "0 28px 16px" }}>
          <MDBox
            p={2}
            borderRadius="10px"
            sx={{
              backgroundColor: "#f0fdf4",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              border: "1px solid #bbf7d0",
            }}
          >
            <Icon sx={{ color: "#16a34a", fontSize: "1.4rem !important", flexShrink: 0 }}>
              help_outline
            </Icon>
            <MDTypography
              sx={{
                color: "#166534",
                fontSize: "0.95rem",
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1.4,
                fontWeight: 500,
              }}
            >
              Are you sure you want to proceed with Add Charge?
            </MDTypography>
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ p: "16px 28px 24px", justifyContent: "flex-end", gap: 2 }}>
          <MDButton
            variant="text"
            onClick={() => setOpenChargeConfirmModal(false)}
            disabled={isSavingCharge}
            sx={{
              fontFamily: "'Inter', sans-serif",
              color: "#64748b !important",
              fontSize: "0.875rem",
              fontWeight: 600,
              textTransform: "none",
              padding: "8px 16px",
              "&:hover": {
                color: "#1e3a5f !important",
                backgroundColor: "transparent",
              },
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            onClick={handleExecuteCharge}
            disabled={isSavingCharge}
            sx={{
              fontFamily: "'Inter', sans-serif",
              backgroundColor: "#000000 !important",
              color: "#ffffff !important",
              borderColor: "#000000 !important",
              padding: "9px 24px",
              fontSize: "0.875rem",
              fontWeight: 600,
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "none",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              "&:hover": {
                backgroundColor: "#1e293b !important",
              },
            }}
          >
            {isSavingCharge && <CircularProgress size={16} color="inherit" />}
            {isSavingCharge ? "Adding Charge..." : "Confirm Add Charge"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Add Upsell POPUP MODAL DIALOG */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            m: { xs: 1.5, sm: 3 },
            boxShadow: "0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.05)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle component="div" sx={{ p: "18px 24px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography
              variant="h6"
              fontWeight="bold"
              sx={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                fontSize: "1rem",
                color: "#0f172a",
              }}
            >
              Add Upsell Item
            </MDTypography>
            <IconButton
              size="small"
              onClick={() => setOpenModal(false)}
              sx={{ color: "#64748b", p: 0.5, "&:hover": { color: "#0f172a" } }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ p: "24px" }}>
          <MDBox display="flex" flexDirection="column" gap={2} pt={0.5}>
            {/* Guest Summary Badge */}
            <MDBox
              px={1.75}
              py={1.25}
              borderRadius="8px"
              sx={{
                backgroundColor: "#f1f5f9",
                border: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "0.825rem",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              <span>
                <strong style={{ color: "#0f172a", fontWeight: 700 }}>
                  {reservationDetails?.id ? `Res ID-${reservationDetails.id}` : "Res ID-0000"}
                </strong>
              </span>
              <span style={{ color: "#334155", fontWeight: 600 }}>
                {reservationDetails?.guestName || "Guest Name"}
              </span>
            </MDBox>

            {/* Select Upsell Item */}
            <MDBox display="flex" flexDirection="column" gap={0.75}>
              <MDTypography
                variant="caption"
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  color: "#334155",
                }}
              >
                Select Upsell Item
              </MDTypography>
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={selectedFieldId}
                  onChange={handleDropdownSelect}
                  sx={{
                    height: "42px",
                    borderRadius: "8px",
                    fontWeight: "400",
                    fontSize: "0.875rem",
                    fontFamily: "'Inter', sans-serif",
                    backgroundColor: "#ffffff",
                    border: "1px solid #cbd5e1",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  }}
                >
                  <MenuItem value="" disabled>
                    <em style={{ color: "#94a3b8", fontStyle: "normal" }}>Select an option...</em>
                  </MenuItem>
                  {OFFICIAL_UPSELL_FIELDS.map((f) => (
                    <MenuItem key={f.id} value={f.id} sx={{ py: 1, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <span style={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.2 }}>{f.category}</span>
                      <span style={{ fontSize: "0.875rem", color: "#0f172a", fontWeight: 400, lineHeight: 1.5 }}>{f.name}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>

            {/* Price Per Unit & Quantity Side-by-Side Row */}
            {(() => {
              const selectedUpsellField = OFFICIAL_UPSELL_FIELDS.find((f) => f.id === selectedFieldId);
              const hideQuantityForUpsell = Boolean(selectedUpsellField && isNoQtyItem(selectedUpsellField));

              return (
                <MDBox display="flex" flexDirection="column" gap={1.5}>
                  <MDBox display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1.5} alignItems="stretch">
                    {/* Price Input */}
                    <MDBox display="flex" flexDirection="column" gap={0.75} flex={1}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="center" height="24px">
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          sx={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                            color: "#334155",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {hideQuantityForUpsell ? "Price / Amount" : "Price Per Unit"}
                        </MDTypography>
                        <Chip
                          label={upsellCurrency || "PKR"}
                          size="small"
                          sx={{
                            fontWeight: "700",
                            fontSize: "0.72rem",
                            height: "20px",
                            backgroundColor: "#e0f2fe",
                            color: "#0369a1",
                            borderRadius: "6px",
                          }}
                        />
                      </MDBox>
                      <MDInput
                        type="number"
                        placeholder="0.00"
                        fullWidth
                        inputProps={{ min: "0", step: "any" }}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e") e.preventDefault();
                        }}
                        onFocus={(e) => e.target.select()}
                        value={priceInput}
                        onChange={(e) => {
                          let val = e.target.value;
                          if (val.length > 1 && val.startsWith("0") && val[1] !== ".") {
                            val = val.replace(/^0+/, "");
                          }
                          if (!val.includes("-")) setPriceInput(val);
                        }}
                        sx={{
                          "& .MuiInputBase-root": {
                            height: "42px",
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                            border: "1px solid #cbd5e1",
                            fontSize: "0.875rem",
                            fontFamily: "'Inter', sans-serif",
                            color: "#0f172a",
                          },
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                          "& .MuiInputBase-input": { fontWeight: "600", padding: "10px 12px" },
                        }}
                      />
                    </MDBox>

                    {/* Quantity Input (Hidden for Security Deposit, Early Checkin, Late Checkout) */}
                    {!hideQuantityForUpsell && (
                      <MDBox display="flex" flexDirection="column" gap={0.75} flex={1}>
                        <MDBox display="flex" alignItems="center" height="24px">
                          <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{
                              fontFamily: "'Inter', sans-serif",
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.03em",
                              color: "#334155",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Quantity
                          </MDTypography>
                        </MDBox>
                        <MDInput
                          type="number"
                          placeholder="1"
                          fullWidth
                          inputProps={{ min: "1", step: "1" }}
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "e" || e.key === ".") e.preventDefault();
                          }}
                          value={qtyInput}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!val.includes("-")) setQtyInput(val);
                          }}
                          sx={{
                            "& .MuiInputBase-root": {
                              height: "42px",
                              borderRadius: "8px",
                              backgroundColor: "#ffffff",
                              border: "1px solid #cbd5e1",
                              fontSize: "0.875rem",
                              fontFamily: "'Inter', sans-serif",
                              color: "#0f172a",
                            },
                            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                            "& .MuiInputBase-input": { fontWeight: "600", padding: "10px 12px" },
                          }}
                        />
                      </MDBox>
                    )}
                  </MDBox>

                  {/* Auto-Calculated Total Amount Field (Hidden when Quantity is hidden) */}
                  {!hideQuantityForUpsell && (
                    <MDBox display="flex" flexDirection="column" gap={0.75}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="center" height="24px">
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          sx={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                            color: "#334155",
                          }}
                        >
                          Total Amount
                        </MDTypography>
                      </MDBox>
                      <MDInput
                        type="text"
                        fullWidth
                        readOnly
                        value={
                          parseFloat(priceInput) > 0
                            ? `${(parseFloat(priceInput) * (parseInt(qtyInput) || 1)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${upsellCurrency || "PKR"}`
                            : `0.00 ${upsellCurrency || "PKR"}`
                        }
                        sx={{
                          "& .MuiInputBase-root": {
                            height: "42px",
                            borderRadius: "8px",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #cbd5e1",
                            fontSize: "0.875rem",
                            fontFamily: "'Inter', sans-serif",
                            color: "#0f172a",
                          },
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                          "& .MuiInputBase-input": { fontWeight: "700", padding: "10px 12px", color: "#0f172a" },
                        }}
                      />
                    </MDBox>
                  )}
                </MDBox>
              );
            })()}
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ p: { xs: "14px 16px", sm: "16px 24px" }, backgroundColor: "#f8fafc", borderTop: "1px solid #e2e8f0", justifyContent: "flex-end", gap: 1.25, flexWrap: "wrap" }}>
          <MDButton
            variant="outlined"
            onClick={() => setOpenModal(false)}
            disabled={isSavingUpsell}
            sx={{
              flex: { xs: 1, sm: "none" },
              fontFamily: "'Inter', sans-serif",
              backgroundColor: "#ffffff",
              color: "#334155 !important",
              borderColor: "#e2e8f0 !important",
              padding: "9px 16px",
              fontSize: "0.825rem",
              fontWeight: "600",
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#f1f5f9",
                color: "#0f172a !important",
              },
            }}
          >
            Close
          </MDButton>
          <MDButton
            variant="contained"
            onClick={handleSaveUpsellModal}
            disabled={isSavingUpsell}
            sx={{
              flex: { xs: 1, sm: "none" },
              fontFamily: "'Inter', sans-serif",
              backgroundColor: "#000000 !important",
              color: "#ffffff !important",
              borderColor: "#000000 !important",
              padding: "9px 18px",
              fontSize: "0.825rem",
              fontWeight: "600",
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "none",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": {
                backgroundColor: "#1e293b !important",
              },
            }}
          >
            {isSavingUpsell && <CircularProgress size={16} color="inherit" />}
            {isSavingUpsell ? "Adding Upsell..." : "Add Upsell"}
          </MDButton>
        </DialogActions>
      </Dialog>



      <MDSnackbar
        color={snackbar.color}
        icon={snackbar.icon || (snackbar.color === "success" ? "check" : snackbar.color === "error" ? "warning" : "notifications")}
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.open}
        autoHideDuration={8000}
        dateTime={new Date().toLocaleTimeString()}
        close={() => setSnackbar({ ...snackbar, open: false })}
      />
    </MDBox>
  );

  if (user?.role === "user" || isEmbedded) {
    return mainContent;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {mainContent}
      <Footer />
    </DashboardLayout>
  );
}

export default Upsells;

